import { jsx, jsxs } from 'react/jsx-runtime';
import { safeRest } from '@wds/utils';
import { createMemoStyles, getColor } from '@wds/styles';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var useStyles = createMemoStyles(function (_a) {
  var palette = _a.palette,
    type = _a.type,
    iconSize = _a.iconSize;
  return {
    wrenchIcons: {
      boxSizing: 'border-box',
      display: 'inline-block',
      fill: function (_a) {
        var color = _a.color,
          onDark = _a.onDark;
        return getColor(palette, color, undefined, onDark);
      },
      fontFamily: type.fontFamily.base,
      fontSize: function (_a) {
        var size = _a.size;
        return size ? iconSize[size] : 'inherit';
      },
      '-webkit-font-smoothing': 'antialiased',
      margin: '0 auto',
      height: '1em',
      textAlign: 'center',
      verticalAlign: 'middle',
      width: '1em'
    }
  };
});

function withIcon(Component) {
  var WithIcon = function WithIcon(_a) {
    var color = _a.color,
      _b = _a.onDark,
      onDark = _b === void 0 ? false : _b,
      title = _a.title,
      size = _a.size,
      rest = __rest(_a, ["color", "onDark", "title", "size"]);
    var wrenchIcons = useStyles({
      color: color,
      onDark: onDark,
      size: size
    }).wrenchIcons;
    return jsx("svg", __assign({
      className: wrenchIcons,
      viewBox: "0 0 16 16",
      preserveAspectRatio: "xMidYMid",
      "aria-label": title,
      role: "img"
    }, safeRest(rest), {
      children: jsx(Component, {})
    }));
  };
  WithIcon.displayName = "WithIcon(".concat(Component.name, ")");
  WithIcon.addOnType = "icon";
  WithIcon.getOriginalComponent = function () {
    return Component;
  };
  return WithIcon;
}

var IconAirplane = function IconAirplane() {
  return jsx("g", {
    children: jsx("path", {
      d: "m15.996.82-.016.102-2.926 12.534a1.25 1.25 0 0 1-1.765.84l-.12-.066-2.031-1.275-2.214 2.603a1.25 1.25 0 0 1-2.196-.682l-.006-.128-.001-4.567L.586 7.584a1.25 1.25 0 0 1 .109-2.178l.117-.05L14.985.048a.75.75 0 0 1 1.011.77zM6.221 11.122v2.946l1.633-1.92-1.633-1.026zm7.72-8.075L6.897 9.775l4.774 2.998 2.27-9.726zm-1.839-.317L1.812 6.582l3.775 2.37 6.515-6.222z"
    })
  });
};
// pure annotation needed for tree-shaking
var Airplane = /* #__PURE__ */withIcon(IconAirplane);

var IconA11yScreenReader = function IconA11yScreenReader() {
  return jsx("g", {
    children: jsx("path", {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M16 7.999A9.031 9.031 0 0 0 11.349.095a.752.752 0 1 0-.73 1.318 7.524 7.524 0 0 1 3.876 6.586 7.524 7.524 0 0 1-3.88 6.588.754.754 0 0 0 .73 1.319A9.031 9.031 0 0 0 16 7.998Zm-3.137 0a5.893 5.893 0 0 0-3.262-5.276.752.752 0 1 0-.672 1.349 4.385 4.385 0 0 1 2.429 3.927 4.385 4.385 0 0 1-2.44 3.932.754.754 0 0 0 .669 1.351A5.893 5.893 0 0 0 12.863 8Zm-9.74 4.596 1.079-3.032h-.006l.12-.219.12.225 1.084 3.026a.608.608 0 0 0 .31.347.594.594 0 0 0 .664-.103.615.615 0 0 0 .159-.662l-.737-2.072c-.063-.179-.107-.332-.149-.478l-.018-.064a5.101 5.101 0 0 1-.12-.558c-.059-.33-.114-.69-.156-1.03-.037-.29-.064-.567-.074-.799-.013-.317.095-.337.315-.375.055-.008.118-.02.188-.035l2.103-.403a.597.597 0 0 0 .387-.256.618.618 0 0 0-.157-.858.595.595 0 0 0-.452-.087l-2.2.42a8.72 8.72 0 0 0-.204.022l-.11.013a9.622 9.622 0 0 1-1.078.082 6.835 6.835 0 0 1-.866-.065l-.048-.006c-.078-.009-.154-.018-.23-.025L.713 5.158a.594.594 0 0 0-.616.268.62.62 0 0 0 .008.681.607.607 0 0 0 .389.253l2.17.415.054.013c.254.061.34.082.329.404-.009.26-.02.572-.035.894a22.36 22.36 0 0 1-.065.969 5.062 5.062 0 0 1-.066.513 3.266 3.266 0 0 1-.14.49l-.753 2.119a.623.623 0 0 0 .16.662.598.598 0 0 0 .973-.244Zm1.801-9.35a1.054 1.054 0 0 0-1.428.073 1.097 1.097 0 0 0-.132 1.367c.138.208.341.362.577.434.236.073.49.06.717-.036.227-.096.415-.27.53-.49a1.099 1.099 0 0 0-.264-1.348Z"
    })
  });
};
// pure annotation needed for tree-shaking
var A11yScreenReader = /* #__PURE__ */withIcon(IconA11yScreenReader);

var IconArrow180 = function IconArrow180() {
  return jsx("g", {
    children: jsx("path", {
      d: "M14.75 1a.75.75 0 0 1 .102 1.493l-.102.007h-9a4.25 4.25 0 0 0-.206 8.495L5.75 11h7.748l-1.286-1.277-.07-.08a.712.712 0 0 1 .07-.933.727.727 0 0 1 .932-.076l.09.076 2.555 2.533a.72.72 0 0 1 .102.127l.032.057.038.09.03.12.009.091-.005.112-.009.052a.71.71 0 0 1-.063.18l-.032.058-.032.046-.07.08-2.555 2.534a.727.727 0 0 1-1.022 0 .712.712 0 0 1-.07-.933l.07-.08 1.285-1.277H5.75a5.75 5.75 0 0 1-.22-11.496L5.75 1h9z"
    })
  });
};
// pure annotation needed for tree-shaking
var Arrow180 = /* #__PURE__ */withIcon(IconArrow180);

var IconArrow360 = function IconArrow360() {
  return jsx("g", {
    children: jsx("path", {
      d: "m6.217 1.147.084.073 2.23 2.23a.75.75 0 0 1 .072.977l-.073.084-2.229 2.23a.75.75 0 0 1-1.133-.977l.073-.085.948-.949h-.306A4.383 4.383 0 0 0 1.5 9.115a4.384 4.384 0 0 0 4.17 4.38l.213.004h4.243A4.375 4.375 0 0 0 14.5 9.124a4.387 4.387 0 0 0-4.004-4.371.75.75 0 0 1 .128-1.495A5.887 5.887 0 0 1 16 9.124a5.875 5.875 0 0 1-5.654 5.872l-.22.004H5.883A5.884 5.884 0 0 1 0 9.115a5.884 5.884 0 0 1 5.662-5.881l.22-.004.306-.001-.947-.948a.75.75 0 0 1-.073-.977l.073-.084a.75.75 0 0 1 .976-.073z"
    })
  });
};
// pure annotation needed for tree-shaking
var Arrow360 = /* #__PURE__ */withIcon(IconArrow360);

var IconArrowBox = function IconArrowBox() {
  return jsx("g", {
    children: jsx("path", {
      d: "m1.05 2 4.2.006A.75.75 0 0 1 5.354 3.5l-.102.007L1.5 3.5V14.5h11v-3.75a.75.75 0 0 1 1.5 0v4.2c0 .541-.41.987-.936 1.044L12.95 16H1.05a1.05 1.05 0 0 1-1.044-.936L0 14.95V3.05c0-.54.41-.986.936-1.043L1.05 2zM15.3 0a.7.7 0 0 1 .7.7v6.55a.75.75 0 1 1-1.5 0V2.561L9.27 7.789a.75.75 0 0 1-1.133-.976l.073-.084L13.438 1.5H8.75a.75.75 0 0 1 0-1.5h6.55z"
    })
  });
};
// pure annotation needed for tree-shaking
var ArrowBox = /* #__PURE__ */withIcon(IconArrowBox);

var IconArrowCombine = function IconArrowCombine() {
  return jsx("g", {
    children: jsx("path", {
      d: "M2.5 1a2.5 2.5 0 0 1 1.916 4.106L4.336 5c1.16 1.572 2.242 2.195 3.93 2.247l.202.002H10V5.5a.75.75 0 0 1 1.02-.7l.094.044 4.5 2.5a.75.75 0 0 1 0 1.312l-4.5 2.5A.75.75 0 0 1 10 10.5V8.75H8.468l-.202.003c-1.65.05-2.72.646-3.85 2.14a2.5 2.5 0 1 1-1.266-.808C3.85 9.143 4.56 8.46 5.361 8 4.56 7.54 3.85 6.857 3.15 5.916A2.5 2.5 0 1 1 2.5 1zm0 10.5a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm9-4.726v2.451L13.705 8 11.5 6.774zM2.5 2.5a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"
    })
  });
};
// pure annotation needed for tree-shaking
var ArrowCombine = /* #__PURE__ */withIcon(IconArrowCombine);

var IconArrowDashed = function IconArrowDashed() {
  return jsx("g", {
    children: jsx("path", {
      d: "m10.452 2.147.084.073 5.245 5.248a.75.75 0 0 1 .072.977l-.072.084-5.249 5.251a.75.75 0 0 1-1.133-.977l.073-.084 4.717-4.721L9.475 3.28a.75.75 0 0 1-.072-.977l.072-.084a.75.75 0 0 1 .977-.073zM5 7.997a.75.75 0 0 1-.75.751H.74a.745.745 0 0 1-.74-.75c0-.38.278-.694.64-.743l.1-.007h3.51a.75.75 0 0 1 .75.75zm5.76-.75c.409 0 .74.337.74.751 0 .38-.278.694-.64.744l-.1.007H7.25a.75.75 0 0 1 0-1.502h3.51z"
    })
  });
};
// pure annotation needed for tree-shaking
var ArrowDashed = /* #__PURE__ */withIcon(IconArrowDashed);

var IconArrowDown = function IconArrowDown() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8.001 1a.75.75 0 0 1 .744.648l.005 10.791 3.964-3.968a.749.749 0 0 1 .976-.073l.084.073c.266.266.29.683.073.977l-.073.084-5.243 5.251a.749.749 0 0 1-.976.073l-.084-.073L2.225 9.53A.752.752 0 0 1 3.2 8.394l.084.073 3.964 3.968L7.25 1.75A.75.75 0 0 1 8.001 1z"
    })
  });
};
// pure annotation needed for tree-shaking
var ArrowDown = /* #__PURE__ */withIcon(IconArrowDown);

var IconArrowExit = function IconArrowExit() {
  return jsx("g", {
    children: jsx("path", {
      d: "M5.999 2a.75.75 0 0 1 .102 1.493l-.102.007H2.501v8.998H6a.75.75 0 0 1 .744.649l.007.101a.75.75 0 0 1-.65.744L6 13.998H2.051c-.542 0-.988-.41-1.045-.935L1 12.948V3.05c0-.541.41-.987.937-1.044L2.05 2H6zm4.45 1.15.084.073 4.248 4.247a.75.75 0 0 1 .072.976l-.072.084-4.25 4.25a.75.75 0 0 1-1.134-.976l.072-.084 2.969-2.97H4.749a.75.75 0 0 1-.101-1.493l.101-.007h7.691L9.473 4.283a.75.75 0 0 1-.073-.976l.073-.084a.75.75 0 0 1 .976-.073z"
    })
  });
};
// pure annotation needed for tree-shaking
var ArrowExit = /* #__PURE__ */withIcon(IconArrowExit);

var IconArrowForward = function IconArrowForward() {
  return jsx("g", {
    children: jsx("path", {
      d: "m10.442 2.147.085.073 5.253 5.248a.75.75 0 0 1 .073.977l-.073.084-5.257 5.251a.752.752 0 0 1-1.135-.977l.073-.084 3.969-3.97-7.415.001C2.693 8.75 0 6.063 0 2.75a.75.75 0 0 1 1.503 0 4.505 4.505 0 0 0 4.3 4.495l.212.005h7.419v-.003l-3.97-3.966a.75.75 0 0 1-.072-.977l.072-.084a.752.752 0 0 1 .978-.073z"
    })
  });
};
// pure annotation needed for tree-shaking
var ArrowForward = /* #__PURE__ */withIcon(IconArrowForward);

var IconArrowIntegrate = function IconArrowIntegrate() {
  return jsx("g", {
    children: jsx("path", {
      d: "m9.486 5.04 1.763 1.697-.005-1.156.007-.108a.732.732 0 0 1 .733-.624c.377.002.69.281.742.64l.007.1.017 2.983-.004.08-.021.114-.037.104-.036.07-.042.064-.043.054-.093.087-.092.062-.07.036-.07.026-.075.02-.078.011-.08.004-3.018-.016a.75.75 0 0 1-.749-.74.729.729 0 0 1 .63-.725l.11-.007 1.239.006-.188-.184L8.59 6.25c-1.666-1.647-4.315-1.687-5.915-.105-1.552 1.533-1.56 4.04-.04 5.692l.146.153 1.764 1.743c.294.29.294.76 0 1.05a.758.758 0 0 1-.985.066l-.077-.066-1.764-1.743c-2.246-2.22-2.3-5.777-.106-7.945C3.756 2.976 7.24 2.979 9.486 5.04zm2.954-3.889.077.066 1.764 1.743c2.246 2.22 2.3 5.777.106 7.945-2.143 2.119-5.627 2.116-7.873.055L4.751 9.263l.005 1.156-.007.108a.732.732 0 0 1-.733.624.752.752 0 0 1-.742-.64l-.007-.1-.017-2.983.004-.08.021-.114.037-.104.036-.07.042-.064.043-.054.093-.087.092-.062.07-.036.07-.026.075-.02.078-.011.08-.004 3.018.016a.75.75 0 0 1 .749.74.729.729 0 0 1-.63.725l-.11.007-1.239-.006.188.184L7.41 9.75c1.666 1.647 4.315 1.687 5.915.105 1.552-1.533 1.56-4.04.04-5.692l-.146-.153-1.764-1.743a.736.736 0 0 1 0-1.05.758.758 0 0 1 .985-.066z"
    })
  });
};
// pure annotation needed for tree-shaking
var ArrowIntegrate = /* #__PURE__ */withIcon(IconArrowIntegrate);

var IconArrowLeft = function IconArrowLeft() {
  return jsx("g", {
    children: jsx("path", {
      d: "m5.545 2.147-.084.073L.22 7.468a.751.751 0 0 0-.073.977l.073.084 5.244 5.251a.749.749 0 0 0 1.133-.977l-.073-.084-3.962-3.971H15.25a.75.75 0 0 0 .102-1.493l-.102-.007H2.56L6.52 3.28a.751.751 0 0 0 .073-.977l-.072-.084a.749.749 0 0 0-.976-.073z"
    })
  });
};
// pure annotation needed for tree-shaking
var ArrowLeft = /* #__PURE__ */withIcon(IconArrowLeft);

var IconArrowOrder = function IconArrowOrder() {
  return jsx("g", {
    children: jsx("path", {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M4.362 1.009 4.272 1l-.112.006-.052.008-.092.025-.089.038-.057.032-.046.033-.08.07L1.21 3.766l-.076.09a.727.727 0 0 0 .076.932c.254.257.652.28.933.07l.08-.07L3.5 3.5V13l.007.102A.75.75 0 0 0 5 13V3.501l1.277 1.287.08.07.098.062c.27.14.609.096.835-.132a.727.727 0 0 0 .076-.932l-.076-.09-2.533-2.554-.062-.056-.065-.046-.057-.033-.09-.038-.12-.03Zm8.131.89A.75.75 0 0 0 11 2v10.498l-1.277-1.286-.08-.07a.712.712 0 0 0-.933.07.727.727 0 0 0 0 1.022l2.533 2.555.08.07.047.032.057.032c.058.03.12.05.181.063l.051.009.113.005.09-.008.122-.031.089-.038.057-.032a.713.713 0 0 0 .127-.102l2.533-2.555.076-.09a.727.727 0 0 0-.076-.932.712.712 0 0 0-.933-.07l-.08.07-1.277 1.286V2l-.007-.102Z",
      fill: "#333E48"
    })
  });
};
// pure annotation needed for tree-shaking
var ArrowOrder = /* #__PURE__ */withIcon(IconArrowOrder);

var IconArrowRandom = function IconArrowRandom() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 3a.75.75 0 0 1 .102 1.494L8 4.501H4.25a.25.25 0 0 0-.243.193L4 4.75v7.746l1.277-1.285.08-.07a.712.712 0 0 1 .933.07c.252.254.277.65.076.932l-.076.09-2.533 2.554a.72.72 0 0 1-.127.102l-.057.033-.09.038-.12.03-.107.009-.087-.005-.06-.01a.71.71 0 0 1-.182-.062l-.057-.032-.046-.033-.08-.07L.21 12.234a.727.727 0 0 1 0-1.022.712.712 0 0 1 .933-.07l.08.07L2.5 12.499V4.751a1.75 1.75 0 0 1 1.606-1.744L4.25 3H8zm4.772-2 .09.009.122.03.089.038.057.033.065.046.062.056 2.533 2.554.076.09a.727.727 0 0 1-.076.932.712.712 0 0 1-.835.132l-.098-.062-.08-.07L13.5 3.501v7.744a1.75 1.75 0 0 1-1.606 1.744l-.144.006H8a.75.75 0 0 1-.102-1.493L8 11.495h3.75a.25.25 0 0 0 .243-.193l.007-.057V3.5l-1.277 1.288-.08.07a.712.712 0 0 1-.933-.07.727.727 0 0 1-.076-.932l.076-.09 2.533-2.554.08-.07.047-.033.057-.032.09-.038.091-.025.052-.008.112-.006zM10 7.25a.75.75 0 0 1 .102 1.493L10 8.75H6a.75.75 0 0 1-.102-1.493L6 7.25h4z"
    })
  });
};
// pure annotation needed for tree-shaking
var ArrowRandom = /* #__PURE__ */withIcon(IconArrowRandom);

var IconArrowRefresh = function IconArrowRefresh() {
  return jsx("g", {
    children: jsx("path", {
      d: "M1.75 7.25A.75.75 0 0 1 2.5 8a5.5 5.5 0 0 0 9.937 3.251l-1.187-.001a.75.75 0 1 1 0-1.5h2.95a.8.8 0 0 1 .8.8v2.95a.75.75 0 1 1-1.5 0l.001-1.17A7 7 0 0 1 1 8a.75.75 0 0 1 .75-.75zM8 1a7 7 0 0 1 7 7 .75.75 0 1 1-1.5 0 5.5 5.5 0 0 0-9.938-3.25H4.75a.75.75 0 0 1 0 1.5H1.8a.8.8 0 0 1-.8-.8V2.5a.75.75 0 0 1 1.5 0v1.169A6.996 6.996 0 0 1 8 1z"
    })
  });
};
// pure annotation needed for tree-shaking
var ArrowRefresh = /* #__PURE__ */withIcon(IconArrowRefresh);

var IconArrowRight = function IconArrowRight() {
  return jsx("g", {
    children: jsx("path", {
      d: "m10.455 2.147.084.073 5.241 5.248c.267.266.29.683.073.977l-.073.084-5.244 5.251a.749.749 0 0 1-1.133-.977l.073-.084 3.962-3.971H.75a.75.75 0 0 1-.102-1.493l.102-.007h12.69L9.48 3.28a.751.751 0 0 1-.073-.977l.072-.084a.749.749 0 0 1 .976-.073z"
    })
  });
};
// pure annotation needed for tree-shaking
var ArrowRight = /* #__PURE__ */withIcon(IconArrowRight);

var IconArrowSkip = function IconArrowSkip() {
  return jsx("g", {
    children: jsx("path", {
      d: "M7.75 2c3.368 0 6.125 2.56 6.246 5.78L14 8v3.504l.785-.793.08-.07a.706.706 0 0 1 .927.07c.25.253.275.647.075.928l-.075.09-2.039 2.06-.079.067.007-.005-.008.008a.712.712 0 0 1-.006.005l.001-.003-.032.024a.706.706 0 0 1-.341.114h-.02l-.03.001-.04-.001a.707.707 0 0 1-.34-.113l-.034-.025.002.003a.712.712 0 0 1-.006-.005l-.007-.006-.016-.012a.721.721 0 0 1-.057-.052l-2.039-2.06a.725.725 0 0 1 0-1.018.706.706 0 0 1 .927-.07l.08.07.785.793V8c0-2.477-2.119-4.5-4.75-4.5-2.558 0-4.632 1.912-4.745 4.295L3 8v1.628a2.25 2.25 0 1 1-1.5 0V8c0-3.322 2.806-6 6.25-6zm0 7.5a2.25 2.25 0 1 1 0 4.5 2.25 2.25 0 0 1 0-4.5zm0 1.5a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var ArrowSkip = /* #__PURE__ */withIcon(IconArrowSkip);

var IconArrowSpread = function IconArrowSpread() {
  return jsx("g", {
    children: jsx("path", {
      d: "m8.586 1 .098.009.094.021.05.017.104.05.077.05.094.086.062.077.035.057.037.074.028.079 1.054 3.524.025.117a.744.744 0 0 1-.512.806.72.72 0 0 1-.819-.293l-.051-.092-.039-.102-.52-1.74C7.52 5.46 6.558 6.734 5.5 7.56c-.206.162-.428.307-.664.436.236.13.458.274.664.435 1.06.828 2.022 2.105 2.902 3.828v.002l.521-1.741.039-.102a.721.721 0 0 1 .87-.385.744.744 0 0 1 .512.806l-.025.117-1.054 3.524a.74.74 0 0 1-.065.153l-.035.057-.062.077-.094.086-.077.05-.104.05-.05.017a.715.715 0 0 1-.192.03l-.066-.002-.058-.006-.105-.025-3.489-1.09a.745.745 0 0 1-.487-.924.723.723 0 0 1 .803-.511l.106.024 1.823.568c-.8-1.589-1.65-2.728-2.537-3.42-.59-.461-1.406-.756-2.464-.873l.076.009H.75a.75.75 0 0 1-.102-1.493L.75 7.25h1.37l.275-.035c.922-.136 1.645-.417 2.181-.836.885-.691 1.733-1.827 2.532-3.411l-1.818.566-.106.024a.723.723 0 0 1-.803-.511.746.746 0 0 1 .395-.887l.092-.036 3.489-1.09.105-.026.058-.006L8.586 1zm4.558 3.885.09.075 2.554 2.533.07.08.033.048.032.056.038.09.025.091.008.052.006.112-.009.09-.03.122-.038.09-.033.056-.046.066-.056.06-2.554 2.534-.09.076a.727.727 0 0 1-.932-.076.712.712 0 0 1-.132-.835l.062-.098.07-.08 1.283-1.277H8.751a.75.75 0 0 1-.102-1.493l.102-.007h4.747l-1.286-1.276-.07-.08a.712.712 0 0 1 .07-.934.727.727 0 0 1 .932-.075z"
    })
  });
};
// pure annotation needed for tree-shaking
var ArrowSpread = /* #__PURE__ */withIcon(IconArrowSpread);

var IconArrowUp = function IconArrowUp() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8.005 14.998a.75.75 0 0 0 .744-.648l.006-10.79 3.967 3.965a.75.75 0 0 0 .978.072l.084-.072a.75.75 0 0 0 .072-.977l-.072-.084-5.248-5.246a.75.75 0 0 0-.977-.073l-.084.073-5.252 5.25A.75.75 0 0 0 3.2 7.6l.085-.073 3.97-3.966v10.686c0 .414.336.75.75.75z"
    })
  });
};
// pure annotation needed for tree-shaking
var ArrowUp = /* #__PURE__ */withIcon(IconArrowUp);

var IconArtificialIntelligence = function IconArtificialIntelligence() {
  return jsxs("g", {
    children: [jsx("path", {
      d: "M4.08083 8.29666C4.6875 9.98999 6.02083 11.3133 7.70417 11.92C7.86083 11.98 7.86083 12.2 7.70417 12.26C6.02417 12.8667 4.6875 14.19 4.08083 15.8833C4.02083 16.04 3.80083 16.04 3.74083 15.8833C3.13417 14.19 1.80083 12.8667 0.1175 12.26C-0.0391667 12.2 -0.0391667 11.98 0.1175 11.92C1.7975 11.3133 3.13417 9.98999 3.74083 8.29666C3.80083 8.13999 4.02083 8.13999 4.08083 8.29666Z"
    }), jsx("path", {
      d: "M2.79083 0.08C3.20417 1.24 4.1175 2.14333 5.2675 2.55667C5.37417 2.59667 5.37417 2.74667 5.2675 2.79C4.1175 3.20333 3.20417 4.11 2.79083 5.26667C2.75083 5.37333 2.60083 5.37333 2.5575 5.26667C2.14417 4.10667 1.23083 3.20333 0.0808342 2.79C-0.0258325 2.75 -0.0258325 2.6 0.0808342 2.55667C1.23083 2.14333 2.14417 1.24 2.5575 0.08C2.5975 -0.0266667 2.7475 -0.0266667 2.79083 0.08Z"
    }), jsx("path", {
      d: "M15.8175 5.87C13.1808 4.92 11.0842 2.84334 10.1342 0.18667C10.0875 0.0633366 9.9775 0.00333659 9.8675 0.00333659C9.7575 0.00333659 9.6475 0.0633366 9.60084 0.18667C8.65084 2.84334 6.55417 4.92 3.9175 5.87C3.67084 5.96334 3.67084 6.31 3.9175 6.4C6.55417 7.35 8.65084 9.42667 9.60084 12.0833C9.6475 12.2067 9.7575 12.2667 9.8675 12.2667C9.9775 12.2667 10.0875 12.2067 10.1342 12.0833C11.0842 9.42667 13.1808 7.35 15.8175 6.4C16.0642 6.30667 16.0642 5.96 15.8175 5.87ZM9.8675 9.27334C9.0475 8.02334 7.98084 6.95334 6.72417 6.13334C7.9775 5.31334 9.0475 4.24667 9.8675 2.99334C10.6875 4.24334 11.7542 5.31334 13.0108 6.13334C11.7575 6.95334 10.6875 8.02 9.8675 9.27334Z"
    })]
  });
};
// pure annotation needed for tree-shaking
var ArtificialIntelligence = /* #__PURE__ */withIcon(IconArtificialIntelligence);

var IconBadge = function IconBadge() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8.5 1A1.5 1.5 0 0 1 10 2.5V3h4a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4v-.5A1.5 1.5 0 0 1 7.5 1h1zM6 4.5H2a.5.5 0 0 0-.492.41L1.5 5v8a.5.5 0 0 0 .41.492L2 13.5h12a.5.5 0 0 0 .492-.41L14.5 13V5a.5.5 0 0 0-.41-.492L14 4.5h-4A1.5 1.5 0 0 1 8.5 6h-1A1.5 1.5 0 0 1 6 4.5zm0 2A1.5 1.5 0 0 1 7.5 8v3A1.5 1.5 0 0 1 6 12.5H4A1.5 1.5 0 0 1 2.5 11V8A1.5 1.5 0 0 1 4 6.5h2zM6 8H4v3h2V8zm6.25 1a.75.75 0 1 1 0 1.5h-2.5a.75.75 0 1 1 0-1.5h2.5zm0-2.5a.75.75 0 1 1 0 1.5h-2.5a.75.75 0 0 1 0-1.5h2.5zm-3.75-4h-1v2h1v-2z"
    })
  });
};
// pure annotation needed for tree-shaking
var Badge = /* #__PURE__ */withIcon(IconBadge);

var IconBalloon = function IconBalloon() {
  return jsx("g", {
    children: jsx("path", {
      d: "M5.502 0c3.047 0 5.502 2.583 5.502 5.75 0 2.416-1.428 4.492-3.458 5.34l.653 1.631a.75.75 0 0 1-.593 1.022l-.103.007h-.846c.466.74 1.07.908 1.921.609l.147-.055c3.077-1.23 5.078-1.238 7.006.372a.75.75 0 1 1-.962 1.151c-1.417-1.183-2.868-1.178-5.487-.13-1.94.775-3.453.052-4.299-1.946l-1.482-.001a.75.75 0 0 1-.728-.93l.032-.099.653-1.63C1.428 10.24 0 8.166 0 5.75 0 2.583 2.454 0 5.502 0zm.58 11.469a5.337 5.337 0 0 1-1.16 0l-.313.781h1.785l-.312-.781zM5.502 1.5C3.3 1.5 1.5 3.394 1.5 5.75S3.3 10 5.5 10c2.202 0 4.002-1.894 4.002-4.25S7.703 1.5 5.502 1.5zm0 .75a.75.75 0 1 1 0 1.5c-.95 0-1.75.88-1.75 2a.75.75 0 0 1-1.501 0c0-1.918 1.439-3.5 3.25-3.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var Balloon = /* #__PURE__ */withIcon(IconBalloon);

var IconBell = function IconBell() {
  return jsx("g", {
    children: jsx("path", {
      d: "M7.995 0a.75.75 0 0 1 .743.649l.007.102v.785a5.754 5.754 0 0 1 5 5.47l.005.237v2.353l1.188 2.897a.751.751 0 0 1-.506 1.012l-.094.018-.098.006-3.74-.023-.005.156a2.501 2.501 0 0 1-4.99 0l-.005-.184-3.758-.02a.75.75 0 0 1-.72-.951l.033-.093L2.25 9.59V7.243a5.754 5.754 0 0 1 4.994-5.706l.001-.786A.75.75 0 0 1 7.995 0zM7 13.487v.01a1 1 0 0 0 2 .002l-2-.012zM8 2.989l-.206.005a4.252 4.252 0 0 0-4.039 4.043l-.005.206v2.502a.751.751 0 0 1-.027.198l-.032.095-.816 1.923 10.248.06-.817-1.991-.03-.093-.02-.095-.006-.097V7.243a4.252 4.252 0 0 0-4.044-4.249L8 2.99z"
    })
  });
};
// pure annotation needed for tree-shaking
var Bell = /* #__PURE__ */withIcon(IconBell);

var IconBestWorst = function IconBestWorst() {
  return jsx("g", {
    children: jsx("path", {
      d: "M.75 11c-.414 0-.75-.352-.75-.786V.786C0 .352.336 0 .75 0s.75.352.75.786v9.428c0 .434-.336.786-.75.786ZM14.75 16c-.414 0-.75-.352-.75-.786V5.786c0-.434.336-.786.75-.786s.75.352.75.786v9.428c0 .434-.336.786-.75.786ZM10.5 13.25a.75.75 0 0 0 1.5 0v-7.5a.75.75 0 0 0-1.5 0v7.5ZM7.75 11a.75.75 0 0 1-.75-.75v-4.5a.75.75 0 0 1 1.5 0v4.5a.75.75 0 0 1-.75.75ZM3.5 10.25a.75.75 0 0 0 1.5 0v-7.5a.75.75 0 0 0-1.5 0v7.5Z"
    })
  });
};
// pure annotation needed for tree-shaking
var BestWorst = /* #__PURE__ */withIcon(IconBestWorst);

var IconBlocks = function IconBlocks() {
  return jsx("g", {
    children: jsx("path", {
      d: "M16 15.253a.747.747 0 0 1-.747.747H.747A.747.747 0 0 1 0 15.253v-4.906C0 9.934.334 9.6.747 9.6H4.8V5.547c0-.413.334-.747.747-.747H9.6V.747c0-.413.334-.747.747-.747h4.906c.413 0 .747.334.747.747v14.506zM4.8 11.2H1.6v3.2h3.2v-3.2zm4.8 0H6.4v3.2h3.2v-3.2zm4.8 0h-3.2v3.2h3.2v-3.2zM9.6 6.4H6.4v3.2h3.2V6.4zm4.8 0h-3.2v3.2h3.2V6.4zm0-4.8h-3.2v3.2h3.2V1.6z"
    })
  });
};
// pure annotation needed for tree-shaking
var Blocks = /* #__PURE__ */withIcon(IconBlocks);

var IconBooks = function IconBooks() {
  return jsx("g", {
    children: jsx("path", {
      d: "M15.25 14.5a.75.75 0 1 1 0 1.5H.75a.75.75 0 1 1 0-1.5h14.5zM2.25 0A.75.75 0 0 1 3 .75v11.5a.75.75 0 1 1-1.5 0V.75A.75.75 0 0 1 2.25 0zm4.5 0a.75.75 0 0 1 .75.75v11.5a.75.75 0 1 1-1.5 0V.75A.75.75 0 0 1 6.75 0zm4.87.476.03.098 2.81 11.493a.75.75 0 0 1-1.426.457l-.03-.098L10.194.933A.75.75 0 0 1 11.62.476z"
    })
  });
};
// pure annotation needed for tree-shaking
var Books = /* #__PURE__ */withIcon(IconBooks);

var IconBrush = function IconBrush() {
  return jsx("g", {
    children: jsx("path", {
      d: "M.136 12.02c-.352-.517.024-1.209.606-1.2l.24.025c.604.04 1.372-.254 2.297-.922l.376-.278a3.924 3.924 0 0 1 1.525-.657l7.061-7.32a2.148 2.148 0 0 1 3.114 0c.824.854.858 2.217.103 3.113l-.103.114-6.823 7.074c-.175 1.725-1.873 3.094-3.985 3.029-1.64-.05-3.113-1.071-4.41-2.978zm2.033.176.052.055c.773.803 1.562 1.201 2.37 1.226 1.545.048 2.618-.971 2.475-1.998-.138-.99-1.333-1.438-2.563-.592l-.136.099c-.639.488-1.248.851-1.83 1.083l-.247.09-.12.037zm4.875-2.99c.458.218.843.558 1.11 1.001l6.163-6.387a.78.78 0 0 0 0-1.076.715.715 0 0 0-.963-.068l-.075.068-6.235 6.462z"
    })
  });
};
// pure annotation needed for tree-shaking
var Brush = /* #__PURE__ */withIcon(IconBrush);

var IconCalendar = function IconCalendar() {
  return jsx("g", {
    children: jsx("path", {
      d: "M11.75 1a.75.75 0 0 1 .75.75V3H14a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h1.5V1.75a.75.75 0 0 1 1.5 0V3h6V1.75a.75.75 0 0 1 .75-.75zm1.75 6.5h-11v6h11v-6zm0-3h-11V6h11V4.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var Calendar = /* #__PURE__ */withIcon(IconCalendar);

var IconCalendarEdit = function IconCalendarEdit() {
  return jsxs("g", {
    children: [jsx("path", {
      d: "M5 3V1.75a.75.75 0 0 0-1.5 0V3H2a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5a.75.75 0 0 0-1.5 0v4h-11v-6H6A.75.75 0 0 0 6 6H2.5V4.5h6a.75.75 0 0 0 0-1.5H5Z"
    }), jsx("path", {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M13.864 2.192a.751.751 0 0 0-.977.068L7.665 7.444l-.073.084a.748.748 0 0 0-.133.301l-.45 2.278-.013.097a.756.756 0 0 0 .9.79l2.276-.45.106-.03a.746.746 0 0 0 .276-.174l5.222-5.183.074-.084a.753.753 0 0 0-.074-.981l-1.827-1.827-.085-.073ZM8.89 8.352l2.713-2.693.759.763-2.71 2.69-.95.188.188-.948Zm4.53-4.496-.75.743.76.763.75-.745-.76-.76Z"
    })]
  });
};
// pure annotation needed for tree-shaking
var CalendarEdit = /* #__PURE__ */withIcon(IconCalendarEdit);

var IconCamera = function IconCamera() {
  return jsx("g", {
    children: jsx("path", {
      d: "M10.246 1c.474 0 .883.316 1.01.763l.026.114.354 2.122L15 4a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1l3.364-.001.354-2.122a1.05 1.05 0 0 1 .92-.87L5.753 1h4.492zM14.5 5.5h-13v8h13v-8zM8 6.5a3 3 0 1 1 0 6 3 3 0 0 1 0-6zM8 8a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm1.865-5.5H6.134L5.885 4h4.229l-.249-1.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var Camera = /* #__PURE__ */withIcon(IconCamera);

var IconCaretDown = function IconCaretDown() {
  return jsx("g", {
    children: jsx("path", {
      d: "M13.254 4c.19.001.38.088.526.26.267.316.29.81.073 1.158l-.073.1L8.53 11.74l-.084.086a.66.66 0 0 1-.901-.007l-.076-.08L2.22 5.523l-.073-.1a1.024 1.024 0 0 1 0-1.058l.073-.1.084-.085a.674.674 0 0 1 .446-.175V4h10.504z"
    })
  });
};
// pure annotation needed for tree-shaking
var CaretDown = /* #__PURE__ */withIcon(IconCaretDown);

var IconCaretLeft = function IconCaretLeft() {
  return jsx("g", {
    children: jsx("path", {
      d: "M12 13.254a.695.695 0 0 1-.26.526c-.316.267-.81.29-1.158.073l-.1-.073L4.26 8.53l-.086-.084a.66.66 0 0 1 .007-.901l.08-.076 6.217-5.248.1-.073a1.024 1.024 0 0 1 1.058 0l.1.073.085.084c.117.132.175.29.175.446L12 13.254z"
    })
  });
};
// pure annotation needed for tree-shaking
var CaretLeft = /* #__PURE__ */withIcon(IconCaretLeft);

var IconCaretRight = function IconCaretRight() {
  return jsx("g", {
    children: jsx("path", {
      d: "M4 13.254c.001.19.088.38.26.526.316.267.81.29 1.158.073l.1-.073L11.74 8.53l.086-.084a.66.66 0 0 0-.007-.901l-.08-.076L5.523 2.22l-.1-.073a1.024 1.024 0 0 0-1.058 0l-.1.073-.085.084a.674.674 0 0 0-.175.446H4v10.504z"
    })
  });
};
// pure annotation needed for tree-shaking
var CaretRight = /* #__PURE__ */withIcon(IconCaretRight);

var IconCaretUp = function IconCaretUp() {
  return jsx("g", {
    children: jsx("path", {
      d: "M13.254 12a.695.695 0 0 0 .526-.26c.267-.316.29-.81.073-1.158l-.073-.1L8.53 4.26l-.084-.086a.66.66 0 0 0-.901.007l-.076.08-5.248 6.217-.073.1a1.024 1.024 0 0 0 0 1.058l.073.1.084.085c.132.117.29.175.446.175V12h10.504z"
    })
  });
};
// pure annotation needed for tree-shaking
var CaretUp = /* #__PURE__ */withIcon(IconCaretUp);

var IconCaretUpDown = function IconCaretUpDown() {
  return jsx("g", {
    children: jsx("path", {
      d: "M13.254 9c.19.001.38.077.526.228a.8.8 0 0 1 .073 1.013l-.073.087-5.251 5.444-.084.075a.73.73 0 0 1-.901-.006l-.076-.069-5.248-5.44-.073-.087a.802.802 0 0 1 0-.926l.073-.087.084-.076a.73.73 0 0 1 .446-.152V9h10.504zM7.544.159a.73.73 0 0 1 .9-.006l.085.075 5.251 5.444.073.087a.8.8 0 0 1-.073 1.013.735.735 0 0 1-.526.228H2.75a.76.76 0 0 1-.446-.156l-.084-.076-.073-.087a.802.802 0 0 1 0-.926l.073-.087L7.468.228z"
    })
  });
};
// pure annotation needed for tree-shaking
var CaretUpDown = /* #__PURE__ */withIcon(IconCaretUpDown);

var IconCart = function IconCart() {
  return jsx("g", {
    children: jsx("path", {
      d: "M6.75 11.5a2.25 2.25 0 1 1 0 4.5 2.25 2.25 0 0 1 0-4.5zm6 0a2.25 2.25 0 1 1 0 4.5 2.25 2.25 0 0 1 0-4.5zm-6 1.5a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5zm6 0a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5zM2.471 0c.782 0 1.465.506 1.69 1.234l.04.148.354 1.644H14.99a1.004 1.004 0 0 1 1 1.14l-.722 5.477a1.006 1.006 0 0 1-1 .857H5.35c-.476 0-.888-.329-.988-.789L3.194 3.9l-.476-2.206a.25.25 0 0 0-.178-.188l-.069-.01-1.713.025A.753.753 0 0 1 0 .773C0 .393.285.08.655.03L.758.024 2.47 0zm11.594 4.523H5.257a.3.3 0 0 0-.295.357l.75 3.88a.3.3 0 0 0 .295.243h7.558a.3.3 0 0 0 .297-.261l.5-3.88a.3.3 0 0 0-.258-.336l-.039-.003z"
    })
  });
};
// pure annotation needed for tree-shaking
var Cart = /* #__PURE__ */withIcon(IconCart);

var IconChartBarHorz = function IconChartBarHorz() {
  return jsx("g", {
    children: jsx("path", {
      d: "M.75 0a.75.75 0 0 1 .75.75V2h5.546a.95.95 0 0 1 .944.84l.006.11V5.5h3.052a.95.95 0 0 1 .944.84l.006.11V9h3.052a.95.95 0 0 1 .944.84l.006.11v3.1a.95.95 0 0 1-.84.944l-.11.006H1.5v1.25a.75.75 0 0 1-.648.743L.75 16a.75.75 0 0 1-.75-.75V.75A.75.75 0 0 1 .648.007L.751 0zM14.5 10.5H1.75v2h12.748v-2zM10.497 7H1.751v2h8.746V7zM6.495 3.5H1.751v2h4.744v-2z"
    })
  });
};
// pure annotation needed for tree-shaking
var ChartBarHorz = /* #__PURE__ */withIcon(IconChartBarHorz);

var IconChartBarVert = function IconChartBarVert() {
  return jsx("g", {
    children: jsx("path", {
      d: "M0 15.246a.75.75 0 0 1 .75-.75l1.252.001L2 8.954a.95.95 0 0 1 .84-.944l.11-.006H5.5v-3.05a.95.95 0 0 1 .84-.944l.11-.006H9V.954A.95.95 0 0 1 9.84.01l.11-.006h3.1a.95.95 0 0 1 .944.839l.006.11.003 13.544h1.247a.75.75 0 1 1 0 1.5h-1.247V16h-12v-.003H.75a.75.75 0 0 1-.75-.75zM10.5 1.504l.003 12.993h2L12.5 1.504h-2zm-3.5 4 .003 8.993h2L9 5.504H7zm-3.5 4 .003 4.993h2L5.5 9.504h-2z"
    })
  });
};
// pure annotation needed for tree-shaking
var ChartBarVert = /* #__PURE__ */withIcon(IconChartBarVert);

var IconChartConfidenceLevel = function IconChartConfidenceLevel() {
  return jsx("g", {
    children: jsx("path", {
      d: "M.75.015a.75.75 0 0 1 .743.648L1.5.765V14.5h13.752a.75.75 0 0 1 .743.648l.007.102a.75.75 0 0 1-.649.743l-.101.007H1.05a1.05 1.05 0 0 1-1.044-.936L0 14.95V.765a.75.75 0 0 1 .75-.75zM9.307 2.75l.563.004c1.382.058 2.07.823 2.798 2.661l.091.236.185.505.331.948.123.335.112.287.111.262c.468 1.058 1.002 1.609 1.593 1.745l.229.043c.36.097.6.445.55.822a.756.756 0 0 1-.848.646c-1.251-.163-2.217-1.086-2.91-2.654l-.064-.146-.062-.15-.124-.315-.132-.362-.33-.945-.16-.437-.187-.474-.18-.4c-.372-.766-.713-1.067-1.19-1.107l-.5-.004c-.536 0-.902.286-1.302 1.111l-.143.314-.074.175-.15.385-.16.437-.33.945-.132.362-.124.316c-.041.1-.083.198-.126.295-.693 1.568-1.659 2.49-2.91 2.654a.756.756 0 0 1-.848-.646.75.75 0 0 1 .55-.822l.101-.02c.643-.083 1.22-.635 1.72-1.768l.112-.262.112-.287.123-.335.33-.948.186-.505c.762-2.009 1.45-2.837 2.89-2.897l.176-.004z"
    })
  });
};
// pure annotation needed for tree-shaking
var ChartConfidenceLevel = /* #__PURE__ */withIcon(IconChartConfidenceLevel);

var IconChartDonut = function IconChartDonut() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zM1.5 8a6.5 6.5 0 0 0 11.302 4.38.2.2 0 0 0-.038-.302l-1.778-1.184a.2.2 0 0 0-.248.021A3.985 3.985 0 0 1 8 12a4 4 0 0 1-.907-7.896.2.2 0 0 0 .155-.195l.001-2.136a.2.2 0 0 0-.229-.198A6.502 6.502 0 0 0 1.5 8zm12.583-1.536-1.995.767a.2.2 0 0 0-.127.216c.007.043.011.08.015.108a4.044 4.044 0 0 1-.23 1.85.2.2 0 0 0 .075.238L13.6 10.83a.2.2 0 0 0 .291-.08l.02-.044a6.528 6.528 0 0 0 .439-4.099.2.2 0 0 0-.267-.143zM9.833 6.3l-.128-.128-.122-.106-.067-.053-.137-.098-.132-.082-.117-.062-.136-.065-.145-.057-.127-.042-.181-.047-.116-.023-.168-.023-.128-.01-.135-.003-.249.013-.1.012-.191.035-.047.01A2.501 2.501 0 1 0 9.735 9.8l.1-.102.11-.128.11-.147c.075-.107.142-.221.2-.34l.097-.232c.096-.266.148-.552.148-.851l-.006-.157a2.485 2.485 0 0 0-.322-1.081l-.085-.138-.043-.064a2.512 2.512 0 0 0-.212-.26zm2.246-3.064-1.185 1.778a.2.2 0 0 0 .022.249l.03.032c.133.145.256.3.366.463.052.076.15.107.235.073l1.997-.767a.2.2 0 0 0 .1-.289 6.527 6.527 0 0 0-1.265-1.577.2.2 0 0 0-.3.038zm-3.33-1.464V3.91a.2.2 0 0 0 .156.195c.1.023.179.043.237.06.067.02.155.05.265.092a.2.2 0 0 0 .236-.076l1.188-1.78a.2.2 0 0 0-.096-.3 6.453 6.453 0 0 0-1.757-.528.2.2 0 0 0-.229.198z"
    })
  });
};
// pure annotation needed for tree-shaking
var ChartDonut = /* #__PURE__ */withIcon(IconChartDonut);

var IconChartDonutFilled = function IconChartDonutFilled() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm0 1.5A.75.75 0 0 0 8 3a5 5 0 1 1-5 5 .75.75 0 0 0-1.5 0A6.5 6.5 0 1 0 8 1.5zm0 3a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7z"
    })
  });
};
// pure annotation needed for tree-shaking
var ChartDonutFilled = /* #__PURE__ */withIcon(IconChartDonutFilled);

var IconChartHorzStacked = function IconChartHorzStacked() {
  return jsx("g", {
    children: jsx("path", {
      d: "m.75 0 .102.007a.75.75 0 0 1 .642.641L1.5.75 1.5 2h13.55l.11.006a.95.95 0 0 1 .834.833L16 2.95v3.1l-.006.11a.95.95 0 0 1-.834.834L15.05 7h-3.052v2.55l-.006.11a.95.95 0 0 1-.834.834l-.11.006H8.996v2.55l-.006.11a.95.95 0 0 1-.833.834L8.046 14H1.5v1.25a.75.75 0 0 1-.648.743L.751 16l-.103-.007a.75.75 0 0 1-.641-.641L0 15.25V.75A.75.75 0 0 1 .648.007L.75 0zm3.249 10.5H1.751v2h2.248v-2zm3.496 0H5.496v2h2v-2zM6 7H1.751v2h4.248V7zm4.498 0H7.496v2h3.001V7zM7.999 3.5H1.751v2h6.248v-2zm6.5 0H9.496v2h5.003v-2z"
    })
  });
};
// pure annotation needed for tree-shaking
var ChartHorzStacked = /* #__PURE__ */withIcon(IconChartHorzStacked);

var IconChartLine = function IconChartLine() {
  return jsx("g", {
    children: jsx("path", {
      d: "M13.75 1a2.25 2.25 0 1 1-.375 4.469l-2.72 4.895a.75.75 0 0 1-1.106.235l-.08-.069-3.297-3.297-2.236 4.027a2.25 2.25 0 1 1-1.312-.729l2.72-4.895A.75.75 0 0 1 6.451 5.4l.08.069 3.296 3.296 2.237-4.026A2.25 2.25 0 0 1 13.75 1zM2.25 12a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5zm11.5-9.5a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var ChartLine = /* #__PURE__ */withIcon(IconChartLine);

var IconChartPie = function IconChartPie() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 0 1 6.833 12.162.77.77 0 0 1-.062.202l-.054.097a.747.747 0 0 1-.234.226A8 8 0 1 1 8 0zm-.75 1.543A6.501 6.501 0 1 0 12.962 12.2L7.586 8.647l-.027-.019a.75.75 0 0 1-.044-.034l-.013-.012-.004-.003-.02-.018a.746.746 0 0 1-.017-.017l-.017-.02a.75.75 0 0 1-.024-.027l-.007-.008a.744.744 0 0 1-.1-.168l-.005-.01a.764.764 0 0 1-.007-.018l-.011-.03a.75.75 0 0 1-.019-.063l.03.093A.755.755 0 0 1 7.25 8zm7.047 4.84L9.624 8.195l4.168 2.757c.42-.82.67-1.742.704-2.719L14.5 8c0-.558-.07-1.1-.203-1.617zm-2.102-3.348L9.924 6.47l3.834-1.488a6.527 6.527 0 0 0-1.563-1.947zM8.75 1.543v3.983l2.197-3.321a6.456 6.456 0 0 0-2.197-.662z"
    })
  });
};
// pure annotation needed for tree-shaking
var ChartPie = /* #__PURE__ */withIcon(IconChartPie);

var IconChartSegment = function IconChartSegment() {
  return jsx("g", {
    children: jsx("path", {
      d: "M5.693 1.084a.75.75 0 0 1-.373.992 6.5 6.5 0 1 0 8.6 8.614.75.75 0 0 1 1.364.621A8 8 0 1 1 4.7.71a.75.75 0 0 1 .993.374zm1.626.324A1.636 1.636 0 0 1 9.174.016l.273.043a8.012 8.012 0 0 1 6.543 6.867l.008.101.002.078c0 .874-.709 1.583-1.583 1.583H9.053a1.75 1.75 0 0 1-1.75-1.75V1.632c0-.075.006-.15.016-.224zm1.636.092a.133.133 0 0 0-.15.113l-.002.019v5.306c0 .138.112.25.25.25h5.364c.046 0 .083-.037.085-.064l-.04-.283A6.502 6.502 0 0 0 9.2 1.539z"
    })
  });
};
// pure annotation needed for tree-shaking
var ChartSegment = /* #__PURE__ */withIcon(IconChartSegment);

var IconChartTracker = function IconChartTracker() {
  return jsx("g", {
    children: jsx("path", {
      d: "M.75 0a.75.75 0 0 1 .743.649L1.5.75v13.748h13.752a.75.75 0 0 1 .743.648l.007.102a.75.75 0 0 1-.649.744l-.101.007H1.05a1.05 1.05 0 0 1-1.044-.936L0 14.949V.751A.75.75 0 0 1 .75 0zm15.032 3.64c.266.266.29.683.073.977l-.073.084-5.31 5.316a.75.75 0 0 1-.977.073l-.084-.073-2.963-2.965-2.718 2.72a.75.75 0 0 1-.976.074l-.084-.073a.751.751 0 0 1-.073-.978l.073-.084L5.918 5.46a.75.75 0 0 1 .977-.073l.084.073L9.94 8.425l4.78-4.785a.75.75 0 0 1 1.061 0z"
    })
  });
};
// pure annotation needed for tree-shaking
var ChartTracker = /* #__PURE__ */withIcon(IconChartTracker);

var IconChartVertStacked = function IconChartVertStacked() {
  return jsx("g", {
    children: jsx("path", {
      d: "m6.05.004.11.006a.95.95 0 0 1 .834.833L7 .953v3.051h2.55l.11.006a.95.95 0 0 1 .834.833l.006.11v2.051h2.55l.11.006a.95.95 0 0 1 .834.833l.006.11-.001 2.547.005 1.5-.006 2.497h1.252a.75.75 0 0 1 .102 1.492l-.102.007-1.253.001V16h-12v-.003H.75a.75.75 0 0 1-.102-1.494l.102-.007 1.247.001L2 .954l.006-.111A.95.95 0 0 1 2.84.01L2.95.004h3.1zM5.499 8h-2l-.002 6.497h2L5.499 8zm3.499 2h-2l-.001 4.497h2L8.998 10zm3.5 2h-2l-.001 2.497h2L12.498 12zm.002-3.496h-2l-.001 1.996h2l.001-1.996zm-3.5-3H7V6.5L7.004 8l-.005.5h2L9 5.504zm-3.5-4h-2L3.499 6.5h2L5.5 1.504z"
    })
  });
};
// pure annotation needed for tree-shaking
var ChartVertStacked = /* #__PURE__ */withIcon(IconChartVertStacked);

var IconChat = function IconChat() {
  return jsx("g", {
    children: jsx("path", {
      d: "M9.75 0a1.75 1.75 0 0 1 1.744 1.606l.006.144v2.249L14.25 4c.966 0 1.75.784 1.75 1.75v6l-.006.144A1.75 1.75 0 0 1 14.25 13.5H14v1.75l-.007.103a.75.75 0 0 1-1.223.473L9.979 13.5H7.25a1.75 1.75 0 0 1-1.75-1.75V9.934l-2.27 1.892a.751.751 0 0 1-1.204-.375l-.02-.098L2 11.25V9.5h-.25A1.75 1.75 0 0 1 .024 8.042l-.018-.148L0 7.75v-6A1.75 1.75 0 0 1 1.606.006L1.75 0h8zM7 11.75c0 .138.112.25.25.25h3l.104.007a.75.75 0 0 1 .376.167l1.77 1.473v-.897a.75.75 0 0 1 .75-.75h1a.25.25 0 0 0 .25-.25v-6a.25.25 0 0 0-.25-.25h-7a.25.25 0 0 0-.25.25v6zM9.75 1.5h-8a.25.25 0 0 0-.243.193L1.5 1.75v6a.25.25 0 0 0 .193.243L1.75 8h1a.75.75 0 0 1 .743.648l.007.102v.897l1.77-1.473a.75.75 0 0 1 .23-.131V5.75C5.5 4.784 6.284 4 7.25 4L10 3.999V1.75a.25.25 0 0 0-.193-.243L9.75 1.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var Chat = /* #__PURE__ */withIcon(IconChat);

var IconCheck = function IconCheck() {
  return jsx("g", {
    children: jsx("path", {
      d: "M14.716 2.226a.747.747 0 0 1 1.06-.01.757.757 0 0 1 .082.98l-.072.086-10.25 10.492a.747.747 0 0 1-.98.079l-.085-.074L.224 9.51a.757.757 0 0 1 0-1.066.747.747 0 0 1 .976-.073l.084.073 3.712 3.732 9.72-9.95z"
    })
  });
};
// pure annotation needed for tree-shaking
var Check = /* #__PURE__ */withIcon(IconCheck);

var IconCheckBox = function IconCheckBox() {
  return jsx("g", {
    children: jsx("path", {
      d: "m1.05 1 10.201.01a.75.75 0 0 1-.001 1.5L1.5 2.5v10.999h13V7.752a.75.75 0 0 1 .648-.743l.102-.007a.75.75 0 0 1 .743.648l.007.102v6.198c0 .541-.41.987-.936 1.044L14.95 15H1.05a1.05 1.05 0 0 1-1.044-.936L0 13.95V2.05c0-.54.41-.986.936-1.043L1.051 1zm14.733 1.718a.72.72 0 0 1 .069.955l-.073.082-7.263 7.033a.766.766 0 0 1-.98.067l-.083-.071-3.234-3.18a.72.72 0 0 1 .004-1.037.766.766 0 0 1 .979-.068l.084.072L7.99 9.23l6.73-6.516a.765.765 0 0 1 1.063.004z"
    })
  });
};
// pure annotation needed for tree-shaking
var CheckBox = /* #__PURE__ */withIcon(IconCheckBox);

var IconCheckCircle = function IconCheckCircle() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8.976.06a7.97 7.97 0 0 1 3.447 1.272.75.75 0 0 1-.83 1.25 6.5 6.5 0 1 0 2.877 4.775.75.75 0 1 1 1.493-.147A8 8 0 1 1 8.976.06zm6.807 2.658a.72.72 0 0 1 .069.955l-.073.082-7.263 7.033a.766.766 0 0 1-.98.067l-.083-.071-3.234-3.18a.72.72 0 0 1 .004-1.037.766.766 0 0 1 .979-.068l.084.072L7.99 9.23l6.73-6.516a.765.765 0 0 1 1.063.004z"
    })
  });
};
// pure annotation needed for tree-shaking
var CheckCircle = /* #__PURE__ */withIcon(IconCheckCircle);

var IconCheckEnclosed = function IconCheckEnclosed() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0Zm4.197 4.391-6.076 6.22L3.8 8.278l-.053-.046a.467.467 0 0 0-.61.046.473.473 0 0 0 0 .666l2.654 2.67.053.045a.467.467 0 0 0 .613-.049l6.408-6.559.045-.053a.473.473 0 0 0-.051-.613.467.467 0 0 0-.663.006Z"
    })
  });
};
// pure annotation needed for tree-shaking
var CheckEnclosed = /* #__PURE__ */withIcon(IconCheckEnclosed);

var IconChevronDown = function IconChevronDown() {
  return jsx("g", {
    children: jsx("path", {
      d: "M13.709 4.23a.75.75 0 0 1 1.151.956l-.07.085-6.25 6.499a.75.75 0 0 1-.995.076l-.085-.076L1.212 5.27a.751.751 0 0 1 .999-1.115l.082.074L8 10.166l5.709-5.936z"
    })
  });
};
// pure annotation needed for tree-shaking
var ChevronDown = /* #__PURE__ */withIcon(IconChevronDown);

var IconChevronLeft = function IconChevronLeft() {
  return jsx("g", {
    children: jsx("path", {
      d: "M11.77 13.708a.75.75 0 0 1-.955 1.151l-.085-.07-6.499-6.25a.75.75 0 0 1-.076-.995l.076-.085L10.73 1.21a.751.751 0 0 1 1.115.999l-.074.082L5.835 8l5.936 5.709z"
    })
  });
};
// pure annotation needed for tree-shaking
var ChevronLeft = /* #__PURE__ */withIcon(IconChevronLeft);

var IconChevronRight = function IconChevronRight() {
  return jsx("g", {
    children: jsx("path", {
      d: "M4.231 13.708a.75.75 0 0 0 .956 1.151l.085-.07 6.499-6.25a.75.75 0 0 0 .076-.995l-.076-.085L5.272 1.21a.751.751 0 0 0-1.115.999l.074.082L10.167 8 4.23 13.708z"
    })
  });
};
// pure annotation needed for tree-shaking
var ChevronRight = /* #__PURE__ */withIcon(IconChevronRight);

var IconChevronUp = function IconChevronUp() {
  return jsx("g", {
    children: jsx("path", {
      d: "M13.709 11.77a.75.75 0 0 0 1.151-.956l-.07-.085L8.54 4.23a.75.75 0 0 0-.995-.076l-.085.076-6.248 6.499a.751.751 0 0 0 .999 1.115l.082-.074L8 5.834l5.709 5.936z"
    })
  });
};
// pure annotation needed for tree-shaking
var ChevronUp = /* #__PURE__ */withIcon(IconChevronUp);

var IconCircle = function IconCircle() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm0 1.5a6.5 6.5 0 1 1 0 13 6.5 6.5 0 0 1 0-13z"
    })
  });
};
// pure annotation needed for tree-shaking
var Circle = /* #__PURE__ */withIcon(IconCircle);

var IconClock = function IconClock() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm0 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zm0 1.505a.75.75 0 0 1 .743.648l.007.102v3.791l2.6 1.359a.75.75 0 0 1 .36.918l-.042.094a.75.75 0 0 1-.919.358l-.093-.041-3.003-1.57a.75.75 0 0 1-.396-.56L7.25 8V3.755a.75.75 0 0 1 .75-.75z"
    })
  });
};
// pure annotation needed for tree-shaking
var Clock = /* #__PURE__ */withIcon(IconClock);

var IconClone = function IconClone() {
  return jsx("g", {
    children: jsx("path", {
      d: "M12 1a1 1 0 0 1 1 1v2h2a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-2H1a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h11zm2.5 4.5h-10v8h10v-8zm-3-1.5V2.5h-10v8H3V5a1 1 0 0 1 1-1h7.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var Clone = /* #__PURE__ */withIcon(IconClone);

var IconCloud = function IconCloud() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 1c3.176 0 5.75 2.569 5.75 5.737 0 .357-.033.709-.097 1.052A3.742 3.742 0 0 1 16 11.259c0 2-1.573 3.633-3.553 3.736l-.2.005h-7.25C2.243 15 0 12.89 0 10.26c0-1.665.904-3.123 2.267-3.968A5.745 5.745 0 0 1 8 1zm0 1.497a4.25 4.25 0 0 0-4.106 3.142 5.05 5.05 0 0 1 .593-.093l.1-.005.49-.011.926-.009a.75.75 0 1 1 0 1.497l-.974.016-.45.016-.171.014c-1.657.263-2.907 1.602-2.907 3.197 0 1.722 1.451 3.145 3.302 3.238l.193.004h7.252a2.248 2.248 0 0 0 2.251-2.245c0-.89-.525-1.681-1.313-2.041a5.738 5.738 0 0 1-1.298 1.747.751.751 0 0 1-1.06-.045.747.747 0 0 1 .045-1.057 4.222 4.222 0 0 0 1.377-3.125A4.245 4.245 0 0 0 8 2.497z"
    })
  });
};
// pure annotation needed for tree-shaking
var Cloud = /* #__PURE__ */withIcon(IconCloud);

var IconCloudArrow = function IconCloudArrow() {
  return jsx("g", {
    children: jsx("path", {
      d: "m8.446 6.397.084.073 2.5 2.5a.75.75 0 0 1-.976 1.133l-.084-.073-1.22-1.219v5.439a.75.75 0 0 1-1.493.102l-.007-.102V8.811l-1.22 1.22a.75.75 0 0 1-.976.072l-.084-.073a.75.75 0 0 1-.073-.976l.073-.084 2.5-2.5a.75.75 0 0 1 .976-.073zM9.5 1a4.75 4.75 0 0 1 4.677 5.586l-.057.271-.013.046.113.06a3.499 3.499 0 0 1 1.757 2.64l.018.213.005.193a3.491 3.491 0 0 1-3.3 3.486l-.191.005H10.75a.75.75 0 1 1 0-1.5h1.759c1.05 0 1.91-.812 1.986-1.843l.005-.148c0-.922-.63-1.717-1.511-1.936a.75.75 0 0 1-.507-1.027 3.25 3.25 0 1 0-6.1-2.213.75.75 0 0 1-1.07.453l-.217-.136a1.25 1.25 0 0 0-1.716 1.654l.12.212a.75.75 0 0 1-.494 1.048 1.998 1.998 0 0 0 .346 3.93L3.5 12h1.75a.75.75 0 1 1 0 1.5H3.5a3.5 3.5 0 0 1-3.495-3.308L0 10c0-1.217.626-2.308 1.597-2.936l.175-.107.063-.034-.02-.08-.036-.195-.022-.198-.007-.2a2.75 2.75 0 0 1 3.18-2.717l.209.042.12.033.094-.177a4.753 4.753 0 0 1 3.692-2.41l.236-.016L9.5 1z"
    })
  });
};
// pure annotation needed for tree-shaking
var CloudArrow = /* #__PURE__ */withIcon(IconCloudArrow);

var IconComment = function IconComment() {
  return jsx("g", {
    children: jsx("path", {
      d: "M9.759.004 6.244 0A6.247 6.247 0 0 0 0 6.25l.004.229a6.253 6.253 0 0 0 6.247 6.027h.999v2.742a.75.75 0 0 0 1.075.677l2.302-1.11A9.498 9.498 0 0 0 16 6.259 6.251 6.251 0 0 0 9.759.004zm-3.51 1.497 3.508.005A4.75 4.75 0 0 1 14.5 6.258l-.005.288a7.996 7.996 0 0 1-4.519 6.917l-1.226.59v-2.298a.75.75 0 0 0-.75-.75H6.25a4.753 4.753 0 0 1 0-9.504z"
    })
  });
};
// pure annotation needed for tree-shaking
var Comment = /* #__PURE__ */withIcon(IconComment);

var IconCommentCheck = function IconCommentCheck() {
  return jsx("g", {
    children: jsx("path", {
      d: "m6.252 0 3.507.004a.75.75 0 0 1 .1 1.495l-.102.007L6.265 1.5l-.212.004A4.753 4.753 0 0 0 6.033 11l.218.005H8a.75.75 0 0 1 .743.648l.007.102v2.298l1.226-.59a7.996 7.996 0 0 0 4.519-6.917l.005-.288a.75.75 0 1 1 1.5 0 9.498 9.498 0 0 1-5.102 8.422l-.271.136-2.302 1.109a.75.75 0 0 1-1.068-.575l-.007-.102v-2.742h-1A6.253 6.253 0 0 1 .005 6.479L0 6.249A6.25 6.25 0 0 1 5.78.019L6.01.004 6.252 0zm9.53.718a.72.72 0 0 1 .07.955l-.073.082-7.263 7.033a.766.766 0 0 1-.98.067l-.083-.071-3.234-3.18a.72.72 0 0 1 .004-1.037.766.766 0 0 1 .979-.068l.084.072L7.99 7.23 14.72.714a.765.765 0 0 1 1.063.004z"
    })
  });
};
// pure annotation needed for tree-shaking
var CommentCheck = /* #__PURE__ */withIcon(IconCommentCheck);

var IconCommentFilled = function IconCommentFilled() {
  return jsx("g", {
    children: jsx("path", {
      d: "M9.759.004 6.244 0A6.247 6.247 0 0 0 0 6.25l.004.229a6.253 6.253 0 0 0 6.247 6.027h.999v2.742a.75.75 0 0 0 1.075.677l2.302-1.11A9.498 9.498 0 0 0 16 6.259 6.251 6.251 0 0 0 9.759.004z"
    })
  });
};
// pure annotation needed for tree-shaking
var CommentFilled = /* #__PURE__ */withIcon(IconCommentFilled);

var IconCommentPlus = function IconCommentPlus() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8.25023 0.0024323C8.66447 0.00293463 9 0.338876 9 0.753107C9 1.16805 8.66336 1.50429 8.24841 1.50378L6.24979 1.50136C3.62398 1.50135 1.5 3.62725 1.5 6.24967C1.5 8.87578 3.62696 11.0047 6.2507 11.0047H8C8.41421 11.0047 8.75 11.3407 8.75 11.7553V14.053L9.97626 13.4631C12.307 12.3401 13.9224 10.1745 14.3726 7.68225C14.442 7.29824 14.7641 7 15.1544 7C15.5948 7 15.9445 7.3762 15.8727 7.81073C15.3736 10.8292 13.4385 13.4613 10.6269 14.8159L8.3253 15.9248C7.82736 16.1647 7.25 15.8015 7.25 15.2484V12.5056L6.2507 12.506C2.87525 12.506 0.124702 9.82806 0.00412244 6.47903L0 6.24967C0 2.79807 2.79555 0 6.24449 0L8.25023 0.0024323ZM14.25 0.75C14.25 0.335786 13.9142 0 13.5 0C13.0858 0 12.75 0.335786 12.75 0.75L12.7491 1.75H11.75C11.3358 1.75 11 2.08579 11 2.5C11 2.91421 11.3358 3.25 11.75 3.25H12.7491L12.75 4.25C12.75 4.66421 13.0858 5 13.5 5C13.9142 5 14.25 4.66421 14.25 4.25L14.2491 3.25H15.25C15.6642 3.25 16 2.91421 16 2.5C16 2.08579 15.6642 1.75 15.25 1.75H14.2491L14.25 0.75Z"
    })
  });
};
// pure annotation needed for tree-shaking
var CommentPlus = /* #__PURE__ */withIcon(IconCommentPlus);

var IconCopyright = function IconCopyright() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm0 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zm0 2.25c.868 0 1.698.261 2.4.742a4.27 4.27 0 0 1 1.082 1.07.75.75 0 0 1-1.228.862 2.77 2.77 0 0 0-.702-.695 2.75 2.75 0 1 0 .818 3.667.75.75 0 1 1 1.292.763A4.254 4.254 0 0 1 8 12.25a4.25 4.25 0 1 1 0-8.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var Copyright = /* #__PURE__ */withIcon(IconCopyright);

var IconCreditCard = function IconCreditCard() {
  return jsx("g", {
    children: jsx("path", {
      d: "M15 2a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1h14Zm-.5 6h-13v4.5h13V8Zm0-4.5h-13v2h13v-2Z"
    })
  });
};
// pure annotation needed for tree-shaking
var CreditCard = /* #__PURE__ */withIcon(IconCreditCard);

var IconDanger = function IconDanger() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zM2.904 3.965a6.5 6.5 0 0 0 9.131 9.131L2.904 3.965zM8 1.5a6.472 6.472 0 0 0-4.035 1.404l9.131 9.131A6.5 6.5 0 0 0 8 1.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var Danger = /* #__PURE__ */withIcon(IconDanger);

var IconDashboard = function IconDashboard() {
  return jsx("g", {
    children: jsx("path", {
      d: "M0 9a8 8 0 0 1 15.996-.25L16 14a1 1 0 0 1-.998 1H1a1 1 0 0 1-1-1V9zm8.75-6.457v.906a.742.742 0 0 1-.75.734.745.745 0 0 1-.743-.634l-.007-.1v-.906c-.951.11-1.84.424-2.62.898l.5.562a.724.724 0 0 1-.071 1.037.762.762 0 0 1-.98.007L4 4.971l-.548-.614A6.508 6.508 0 0 0 1.9 6.75l.86.121c.41.058.695.43.636.831a.745.745 0 0 1-.747.631l-.101-.007-.998-.14c-.024.192-.04.387-.047.585l-.002.134.002.095-.004 4.499h12.999l-.003-4.732a6.545 6.545 0 0 0-.046-.58l-.994.139-.102.007a.745.745 0 0 1-.746-.63.735.735 0 0 1 .536-.811l.1-.02.856-.122a6.505 6.505 0 0 0-1.543-2.385l-.543.606-.079.076a.762.762 0 0 1-.979-.007.724.724 0 0 1-.137-.95l.067-.087.496-.555a6.46 6.46 0 0 0-2.632-.905zm.799 2.75a.75.75 0 0 1 .49.855l-.028.099-.606 1.746A2.25 2.25 0 1 1 7.988 7.5l.606-1.746a.75.75 0 0 1 .955-.462zM8 9a.75.75 0 1 0 0 1.5A.75.75 0 0 0 8 9z"
    })
  });
};
// pure annotation needed for tree-shaking
var Dashboard = /* #__PURE__ */withIcon(IconDashboard);

var IconDashboardAdd = function IconDashboardAdd() {
  return jsxs("g", {
    children: [jsx("path", {
      d: "M5.953 9.016c.54.001.984.41 1.041.935l.006.114v4.884c0 .541-.41.987-.936 1.044L5.95 16h-4.9a1.05 1.05 0 0 1-1.044-.936L0 14.95v-4.9c0-.54.41-.986.937-1.043L1.05 9l4.902.016ZM1.5 14.499h4v-3.985l-4-.014v3.999ZM5.953.016c.54.001.984.41 1.041.935L7 1.065V5.95c0 .541-.41.987-.936 1.044L5.95 7h-4.9a1.05 1.05 0 0 1-1.044-.936L0 5.95v-4.9C0 .51.41.064.936.007L1.05 0l4.903.016Zm9 0c.54.001.984.41 1.041.935l.006.114V5.95c0 .541-.41.987-.935 1.044L14.95 7h-4.9a1.05 1.05 0 0 1-1.044-.936L9 5.95v-4.9c0-.54.41-.986.936-1.043L10.05 0l4.903.016ZM1.5 5.499h4V1.514l-4-.014v3.999Zm9 0h4V1.514l-4-.014v3.999Z"
    }), jsx("path", {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M12.5 9a.75.75 0 0 1 .75.75v2h2a.75.75 0 1 1 0 1.5h-2v2a.75.75 0 0 1-1.5 0v-2h-2a.75.75 0 0 1 0-1.5h2v-2A.75.75 0 0 1 12.5 9Z"
    })]
  });
};
// pure annotation needed for tree-shaking
var DashboardAdd = /* #__PURE__ */withIcon(IconDashboardAdd);

var IconDesktop = function IconDesktop() {
  return jsx("g", {
    children: jsx("path", {
      d: "M15 1a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-4.663l.781 1.561h1.163a.72.72 0 1 1 0 1.439H3.719a.72.72 0 1 1 0-1.439h1.162L5.661 12H1a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h14zM8.66 12H7.337l-.781 1.561h2.885L8.66 12zm5.84-9.5h-13v8h13v-8z"
    })
  });
};
// pure annotation needed for tree-shaking
var Desktop = /* #__PURE__ */withIcon(IconDesktop);

var IconDistribute = function IconDistribute() {
  return jsxs("g", {
    children: [jsx("path", {
      d: "M15.9748 4.39855L15.9848 4.28264L16 0.711365L15.9925 0.605835L15.9821 0.55038L15.9647 0.487962L15.9282 0.398944L15.8805 0.316522L15.85 0.274639L15.7736 0.191884L15.7031 0.134502L15.5947 0.0705967L15.5041 0.0346882L15.4406 0.0175047L15.3604 0.00428639L15.2773 0L11.6494 0.0149626L11.5316 0.0247978C11.1866 0.081583 10.9221 0.377237 10.9206 0.732313C10.9193 1.05112 11.1303 1.32028 11.4228 1.41098L11.5361 1.43637L11.6433 1.44368L13.4619 1.43771L8.06535 6.74999H0.761903C0.341116 6.74999 0 7.08579 0 7.5C0 7.91421 0.341116 8.25 0.761903 8.25H8.33886C8.56787 8.25 8.7875 8.16043 8.94944 8.00103L14.5404 2.49741L14.5334 4.28863L14.5408 4.39416C14.5912 4.73842 14.8917 5.0015 15.256 4.99999C15.6167 4.99851 15.9171 4.73821 15.9748 4.39855Z"
    }), jsx("path", {
      d: "M15.9748 11.6015L15.9848 11.7174L16 15.2886L15.9926 15.3942L15.9822 15.4496L15.9647 15.512L15.9283 15.6011L15.8805 15.6835L15.85 15.7254L15.7736 15.8081L15.7032 15.8655L15.5947 15.9294L15.5041 15.9653L15.4407 15.9825L15.3604 15.9957L15.2773 16L11.6494 15.985L11.5316 15.9752C11.1866 15.9184 10.9222 15.6228 10.9207 15.2677C10.9193 14.9489 11.1303 14.6797 11.4228 14.589L11.5361 14.5636L11.6433 14.5563L13.4623 14.5623L10.0799 11.2327C9.78237 10.9398 9.78237 10.465 10.0799 10.1721C10.3527 9.90358 10.7807 9.8812 11.0794 10.1049L11.1574 10.1721L14.5404 13.5022L14.5334 11.7114L14.5408 11.6058C14.5912 11.2616 14.8917 10.9985 15.2561 11C15.6168 11.0015 15.9171 11.2618 15.9748 11.6015Z"
    })]
  });
};
// pure annotation needed for tree-shaking
var Distribute = /* #__PURE__ */withIcon(IconDistribute);

var IconDivisions = function IconDivisions() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 1a2 2 0 0 1 .751 3.854L8.75 7.25H14a.75.75 0 0 1 .743.648L14.75 8l.001 3.146a2 2 0 1 1-1.501 0V8.75h-4.5l.001 2.396a2 2 0 1 1-1.501 0V4.854A2.007 2.007 0 0 1 6.146 3.75L2.75 3.75l.001 7.396a2 2 0 1 1-1.501 0V3a.75.75 0 0 1 .648-.743L2 2.25h4.145A2 2 0 0 1 8 1z"
    })
  });
};
// pure annotation needed for tree-shaking
var Divisions = /* #__PURE__ */withIcon(IconDivisions);

var IconDocument = function IconDocument() {
  return jsx("g", {
    children: jsx("path", {
      d: "m5.94 0 8.012.012C14.53.013 15 .483 15 1.062v13.883c0 .58-.47 1.05-1.05 1.05H2.05c-.58 0-1.05-.47-1.05-1.05V4.935L5.94 0zM7 1.5v3.445c0 .541-.41.987-.936 1.044l-.114.006H2.5v8.5h11V1.51L7 1.5zM5.5 2.558 3.561 4.495H5.5V2.557z"
    })
  });
};
// pure annotation needed for tree-shaking
var Document = /* #__PURE__ */withIcon(IconDocument);

var IconDocumentArrow = function IconDocumentArrow() {
  return jsx("g", {
    children: jsx("path", {
      d: "m5.94.005 8.012.012C14.53.018 15 .487 15 1.067V14.95c0 .58-.47 1.05-1.05 1.05H2.05C1.47 16 1 15.53 1 14.95V4.94L5.94.004zM7 1.505V4.95c0 .541-.41.987-.936 1.044L5.95 6H2.5v8.5h11V1.514L7 1.505zM9.25 4.5a.75.75 0 0 1 .75.75v5.439l1.22-1.22.084-.072a.75.75 0 0 1 .976 1.133l-2.5 2.5-.084.073a.75.75 0 0 1-.976-.073l-2.5-2.5-.073-.084a.75.75 0 0 1 .073-.976l.084-.073a.75.75 0 0 1 .976.073l1.22 1.219V5.25l.007-.102A.75.75 0 0 1 9.25 4.5zM5.5 2.563 3.561 4.5H5.5V2.562z"
    })
  });
};
// pure annotation needed for tree-shaking
var DocumentArrow = /* #__PURE__ */withIcon(IconDocumentArrow);

var IconDocumentClone = function IconDocumentClone() {
  return jsx("g", {
    children: jsx("path", {
      d: "m4.94.005 5.013.012c.54.001.984.41 1.04.936l.007.114v1.94l3.953.01c.54.001.984.41 1.04.936l.007.114V14.95c0 .541-.41.987-.936 1.044L14.95 16h-8.9a1.05 1.05 0 0 1-1.044-.936L5 14.95V13H1.05a1.05 1.05 0 0 1-1.044-.936L0 11.95V4.94L4.94.004zm6.06 4.5v7.445c0 .541-.41.987-.936 1.044L9.95 13H6.5v1.5h8V4.514l-3.5-.01zm-5-3V4.95c0 .541-.41.987-.936 1.044L4.95 6H1.5v5.5h8V1.514L6 1.505zM4.5 2.565 2.562 4.5H4.5V2.563z"
    })
  });
};
// pure annotation needed for tree-shaking
var DocumentClone = /* #__PURE__ */withIcon(IconDocumentClone);

var IconDocumentDocx = function IconDocumentDocx() {
  return jsx("g", {
    children: jsx("path", {
      d: "M5.953 11.962c1.16 0 1.99.777 1.99 2.02 0 1.242-.83 2.018-1.99 2.018-1.16 0-1.99-.776-1.99-2.019 0-1.242.83-2.019 1.99-2.019zm4.144 0c.83 0 1.327.31 1.658 1.087l-.829.31c0-.31-.331-.62-.663-.62-.497 0-.829.466-.829 1.242 0 .777.332 1.243.83 1.243.33 0 .662-.311.662-.622l.995.31c-.166.622-.829 1.088-1.658 1.088-1.16 0-1.99-.776-1.99-2.019 0-1.242.664-2.019 1.824-2.019zm-8.455.156c1.16 0 1.99.62 1.99 1.863 0 1.243-.83 1.864-1.99 1.864H.15v-3.727h1.492zm11.44-.156.829 1.087.828-1.087H15.9l-1.326 1.864 1.326 2.019h-1.16l-.83-1.243-.828 1.243h-1.16l1.325-2.02-1.326-1.863h1.16zm-6.964.777c-.663 0-.994.466-.994 1.242 0 .777.331 1.243.829 1.243.497 0 .829-.466.829-1.243 0-.776-.166-1.242-.664-1.242zm-4.476.155h-.497v2.174h.497c.663 0 .995-.31.995-1.087 0-.776-.498-1.087-.995-1.087zM4.94 0l10.011.012c.541.001.986.414 1.043.945l.006.115v8.967a.754.754 0 0 1-.75.757.753.753 0 0 1-.743-.654l-.007-.103V1.525l-8.25-.011v3.73c0 .546-.41.996-.936 1.054l-.114.006H1.5v3.735c0 .383-.282.7-.648.75l-.102.007a.753.753 0 0 1-.743-.654L0 10.039V4.98L4.94 0zm-.19 2.33L2.311 4.789H4.75V2.33z"
    })
  });
};
// pure annotation needed for tree-shaking
var DocumentDocx = /* #__PURE__ */withIcon(IconDocumentDocx);

var IconDocumentList = function IconDocumentList() {
  return jsx("g", {
    children: jsx("path", {
      d: "m2.05 0 11.901.017c.58.001 1.049.47 1.049 1.05V14.95c0 .58-.47 1.05-1.05 1.05H2.05C1.47 16 1 15.53 1 14.95V1.05C1 .47 1.47 0 2.05 0zm.45 1.501V14.5h11V1.516l-11-.015zM5.25 10.5a.75.75 0 1 1 0 1.5h-.5a.75.75 0 1 1 0-1.5h.5zm6 0a.75.75 0 1 1 0 1.5h-3.5a.75.75 0 1 1 0-1.5h3.5zm-6-3.25a.75.75 0 0 1 0 1.5h-.5a.75.75 0 0 1 0-1.5h.5zm6 0a.75.75 0 1 1 0 1.5h-3.5a.75.75 0 0 1 0-1.5h3.5zM5.25 4a.75.75 0 0 1 0 1.5h-.5a.75.75 0 0 1 0-1.5h.5zm6 0a.75.75 0 1 1 0 1.5h-3.5a.75.75 0 0 1 0-1.5h3.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var DocumentList = /* #__PURE__ */withIcon(IconDocumentList);

var IconDocumentPdf = function IconDocumentPdf() {
  return jsx("g", {
    children: jsx("path", {
      d: "M13 12v.8h-1.719v.96h1.406v.8h-1.406V16h-.937v-4H13zM4.94 0l10.011.012c.541 0 .986.41 1.043.936l.006.114V15.25a.75.75 0 0 1-1.493.102l-.007-.102V1.51L6.25 1.5v3.695c0 .541-.41.987-.936 1.044l-.114.006H1.5v9.004a.75.75 0 0 1-.648.743L.75 16a.75.75 0 0 1-.743-.648L0 15.249V4.934L4.94 0zm-.378 12c.938 0 1.407.48 1.407 1.28s-.625 1.28-1.407 1.28h-.625v1.28H3V12h1.563zm3.282 0c1.093 0 1.875.64 1.875 1.92 0 1.28-.781 1.92-1.875 1.92H6.437V12h1.407zm0 .8h-.469v2.24h.469c.625 0 .937-.32.937-1.12s-.469-1.12-.937-1.12zm-3.438 0h-.468v1.12h.468c.469 0 .625-.16.625-.48 0-.48-.156-.64-.625-.64zM4.75 2.308 2.311 4.745H4.75V2.307z"
    })
  });
};
// pure annotation needed for tree-shaking
var DocumentPdf = /* #__PURE__ */withIcon(IconDocumentPdf);

var IconDocumentPlus = function IconDocumentPlus() {
  return jsx("g", {
    children: jsx("path", {
      d: "m5.94.005 8.012.012C14.53.018 15 .487 15 1.067V14.95c0 .58-.47 1.05-1.05 1.05H2.05C1.47 16 1 15.53 1 14.95V4.94L5.94.004zM7 1.505V4.95c0 .541-.41.987-.936 1.044L5.95 6H2.5v8.5h11V1.514L7 1.505zM8 6.5a.75.75 0 0 1 .75.75v2h2a.75.75 0 1 1 0 1.5h-2v2a.75.75 0 1 1-1.5 0v-2h-2a.75.75 0 1 1 0-1.5h2v-2A.75.75 0 0 1 8 6.5zM5.5 2.563 3.561 4.5H5.5V2.562z"
    })
  });
};
// pure annotation needed for tree-shaking
var DocumentPlus = /* #__PURE__ */withIcon(IconDocumentPlus);

var IconDocumentPPTX = function IconDocumentPPTX() {
  return jsx("g", {
    children: jsx("path", {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "m4.94 0 10.011.012c.541.001.986.414 1.043.945l.006.115v8.967a.754.754 0 0 1-.75.757.753.753 0 0 1-.743-.654l-.007-.103V1.525l-8.25-.011v3.73c0 .546-.41.996-.936 1.054l-.114.006H1.5v3.735c0 .383-.282.7-.648.75l-.102.007a.753.753 0 0 1-.743-.654L0 10.039V4.98L4.94 0Zm-.19 2.33L2.311 4.789H4.75V2.33ZM0 16h.967v-1.242h.656c.916 0 1.488-.543 1.488-1.373 0-.822-.56-1.385-1.459-1.385H0v4Zm.967-2.004V12.78h.476c.434 0 .666.235.666.606 0 .369-.232.611-.666.611H.967ZM4.086 16h.967v-1.242h.656c.916 0 1.488-.543 1.488-1.373 0-.822-.56-1.385-1.459-1.385H4.086v4Zm.967-2.004V12.78h.476c.434 0 .666.235.666.606 0 .369-.232.611-.666.611h-.476Zm2.988-1.21h1.215V16h.953v-3.215h1.213V12H8.04v.785ZM13.375 12h-1.09l1.21 2-1.241 2h1.101l.75-1.277h.032l.75 1.277h1.11l-1.247-2 1.207-2h-1.082l-.738 1.264h-.032L13.375 12Z"
    })
  });
};
// pure annotation needed for tree-shaking
var DocumentPPTX = /* #__PURE__ */withIcon(IconDocumentPPTX);

var IconDocumentSearch = function IconDocumentSearch() {
  return jsx("g", {
    children: jsx("path", {
      d: "m4.942.005 7.014.012c.54.001.985.41 1.042.936l.006.114.002 3.997A4.244 4.244 0 0 1 14.755 8.5a4.23 4.23 0 0 1-.761 2.427l1.786 1.788a.75.75 0 0 1-.977 1.133l-.084-.073-1.715-1.714v2.889c0 .541-.41.987-.936 1.044l-.114.006H1.05a1.05 1.05 0 0 1-1.044-.936L0 14.95V4.94L4.942.004zM6 1.505V4.95c0 .541-.409.987-.935 1.044L4.952 6H1.5v8.5h10.004v-1.868a4.25 4.25 0 1 1 0-8.263V1.514L6 1.505zm4.502 4.245a2.75 2.75 0 1 0 0 5.5 2.75 2.75 0 0 0 0-5.5zM4.501 2.563 2.562 4.5h1.94L4.5 2.562z"
    })
  });
};
// pure annotation needed for tree-shaking
var DocumentSearch = /* #__PURE__ */withIcon(IconDocumentSearch);

var IconDocumentUser = function IconDocumentUser() {
  return jsx("g", {
    children: jsx("path", {
      d: "m5.94 0 8.012.012c.54.001.985.41 1.042.936l.006.114v13.883c0 .541-.41.987-.936 1.044l-.114.006h-1.334l-.06.005H5.444l-.06-.004H2.05a1.05 1.05 0 0 1-1.044-.936L1 14.945V4.935L5.94 0zM7 1.5v3.445c0 .541-.41.987-.936 1.044l-.114.006H2.5v8.5l2.591-.001.14-1.614.095-1.192.029-.246.01-.058a.05.05 0 0 1 .006-.012c.209-.32.443-.483.843-.63l.14-.048.317-.101.164-.056.131-.05.11-.049.099-.054.1-.063c.087-.057.169-.122.248-.198a.434.434 0 0 1 .617.01l.054.069.805.818.823-.842c.036-.051.081-.09.131-.117a.434.434 0 0 1 .524.062c.08.076.161.141.247.198l.101.063.1.054.109.05.13.05.165.055.209.066c.558.174.842.332 1.091.713l.012.038.021.138.044.447.081 1.08.138 1.419h.575V1.51L7 1.5zm3.464 10.373-.913.869a.8.8 0 0 1-1.103 0l-.913-.869-.036.017-.183.068-.524.171-.102 1.414-.125.951h4.87l-.138-1.227-.085-1.139-.363-.114-.217-.075-.168-.066zM9.089 5.001c.797-.02 1.53.42 1.863 1.118.667 1.411-.204 3.925-1.863 3.925-1.66 0-2.53-2.514-1.864-3.925a2.005 2.005 0 0 1 1.864-1.118zm.036 1.5h-.073c-.212-.006-.397.105-.47.259-.129.27-.112.806.083 1.265.16.373.332.52.424.52.092 0 .264-.147.423-.52.195-.46.212-.994.087-1.259a.493.493 0 0 0-.385-.261L9.125 6.5zM5.5 2.557 3.561 4.495H5.5V2.557z"
    })
  });
};
// pure annotation needed for tree-shaking
var DocumentUser = /* #__PURE__ */withIcon(IconDocumentUser);

var IconDocumentUserFilled = function IconDocumentUserFilled() {
  return jsx("g", {
    children: jsx("path", {
      d: "m4.94.005 8.012.012c.54.001.985.41 1.042.936l.006.114V14.95c0 .541-.41.987-.936 1.044L12.95 16H1.05a1.05 1.05 0 0 1-1.044-.936L0 14.95V4.94L4.94.004zM6 1.505V4.95c0 .541-.41.987-.936 1.044L4.95 6H1.5v8.5l1.898-.001.037-.128C3.744 13.32 4.48 12 6.078 12h2.844c1.665 0 2.394 1.433 2.679 2.5h.899V1.514L6 1.505zM7.5 6.5c1.105 0 2 .824 2 1.84 0 1.018-.895 2.66-2 2.66s-2-1.642-2-2.66c0-1.016.895-1.84 2-1.84zm-3-3.937L2.561 4.5H4.5V2.562z"
    })
  });
};
// pure annotation needed for tree-shaking
var DocumentUserFilled = /* #__PURE__ */withIcon(IconDocumentUserFilled);

var IconDocumentX = function IconDocumentX() {
  return jsx("g", {
    children: jsx("path", {
      d: "m5.94.005 8.012.012C14.53.018 15 .487 15 1.067V14.95c0 .58-.47 1.05-1.05 1.05H2.05C1.47 16 1 15.53 1 14.95V4.94L5.94.004zM7 1.505V4.95c0 .541-.41.987-.936 1.044L5.95 6H2.5v8.5h11V1.514L7 1.505zM6.557 7.53 8 8.973 9.443 7.53a.765.765 0 0 1 1.083 1.082l-1.444 1.443 1.444 1.444a.765.765 0 0 1-1.083 1.082L8 11.138 6.557 12.58A.765.765 0 1 1 5.474 11.5l1.444-1.444-1.444-1.443A.765.765 0 1 1 6.557 7.53zM5.5 2.563 3.561 4.5H5.5V2.562z"
    })
  });
};
// pure annotation needed for tree-shaking
var DocumentX = /* #__PURE__ */withIcon(IconDocumentX);

var IconEmail = function IconEmail() {
  return jsx("g", {
    children: jsx("path", {
      d: "m1.05 2 13.901.017c.541 0 .986.41 1.043.936l.006.114v9.883c0 .541-.41.987-.936 1.044L14.95 14H1.05a1.05 1.05 0 0 1-1.044-.936L0 12.95v-9.9c0-.54.41-.986.936-1.043L1.05 2zM14.5 4.346 8.463 9.09a.75.75 0 0 1-.832.063l-.094-.063L1.5 4.346V12.5h13V4.346zM2.854 3.502 8 7.546l5.131-4.033-10.277-.01z"
    })
  });
};
// pure annotation needed for tree-shaking
var Email = /* #__PURE__ */withIcon(IconEmail);

var IconError = function IconError() {
  return jsx("g", {
    children: jsx("path", {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8ZM8 10.7546C7.6203 10.7546 7.30651 11.034 7.25685 11.3965L7.25 11.4973V11.7573L7.25685 11.8581C7.30651 12.2206 7.6203 12.5 8 12.5C8.3797 12.5 8.69349 12.2206 8.74315 11.8581L8.75 11.7573V11.4973L8.74315 11.3965C8.69349 11.034 8.3797 10.7546 8 10.7546ZM7.25685 4.1419C7.30651 3.7794 7.6203 3.5 8 3.5C8.3797 3.5 8.69349 3.7794 8.74315 4.1419L8.75 4.24268V8.78661L8.74315 8.88739C8.69349 9.24989 8.3797 9.52929 8 9.52929C7.6203 9.52929 7.30651 9.24989 7.25685 8.88739L7.25 8.78661V4.24268L7.25685 4.1419Z"
    })
  });
};
// pure annotation needed for tree-shaking
var Error$1 = /* #__PURE__ */withIcon(IconError);

var IconEye = function IconEye() {
  return jsx("g", {
    children: jsx("path", {
      d: "M7.999 2c2.65 0 5.258 1.889 7.86 5.567a.75.75 0 0 1-.002.871C13.212 12.114 10.607 14 7.999 14 5.39 14 2.786 12.114.14 8.438a.75.75 0 0 1-.003-.871C2.74 3.889 5.348 2 7.998 2zm0 1.5c-1.862 0-3.857 1.315-5.958 4.017l-.225.295-.138.184.142.188c2.064 2.703 4.003 4.114 5.785 4.296l.198.015.196.005c1.824 0 3.819-1.317 5.95-4.021l.23-.295.14-.188-.137-.184C12.15 5.11 10.213 3.702 8.4 3.52l-.201-.015-.2-.005zM8 5a3 3 0 1 1 0 6 3 3 0 0 1 0-6zm0 1.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z"
    })
  });
};
// pure annotation needed for tree-shaking
var Eye = /* #__PURE__ */withIcon(IconEye);

var IconFaceHappy = function IconFaceHappy() {
  return jsx("g", {
    children: jsx("path", {
      d: "M14.5 8.00001C14.5 11.5899 11.5898 14.5 8 14.5C4.41015 14.5 1.5 11.5899 1.5 8.00001C1.5 4.41015 4.41015 1.5 8 1.5C11.5898 1.5 14.5 4.41015 14.5 8.00001ZM8 16C12.4183 16 16 12.4183 16 8.00001C16 3.58173 12.4183 0 8 0C3.58172 0 0 3.58173 0 8.00001C0 12.4183 3.58172 16 8 16ZM6.12454 10.0848C6.5289 10.6929 7.21811 11.0911 7.99999 11.0911C8.78188 11.0911 9.47109 10.6929 9.87545 10.0848C10.1048 9.73983 10.5703 9.64613 10.9153 9.87546C11.2602 10.1048 11.3539 10.5703 11.1246 10.9153C10.454 11.9238 9.30508 12.5911 7.99999 12.5911C6.69491 12.5911 5.546 11.9238 4.87544 10.9153C4.6461 10.5703 4.73981 10.1048 5.08474 9.87546C5.42967 9.64613 5.8952 9.73983 6.12454 10.0848ZM5 7.87399C5.55228 7.87399 6 7.42628 6 6.87399C6 6.32171 5.55228 5.87399 5 5.87399C4.44772 5.87399 4 6.32171 4 6.87399C4 7.42628 4.44772 7.87399 5 7.87399ZM12 6.87399C12 7.42628 11.5523 7.87399 11 7.87399C10.4477 7.87399 10 7.42628 10 6.87399C10 6.32171 10.4477 5.87399 11 5.87399C11.5523 5.87399 12 6.32171 12 6.87399Z"
    })
  });
};
// pure annotation needed for tree-shaking
var FaceHappy = /* #__PURE__ */withIcon(IconFaceHappy);

var IconFaceNeutral = function IconFaceNeutral() {
  return jsx("g", {
    children: jsx("path", {
      d: "M14.5 8.00001C14.5 11.5899 11.5898 14.5 8 14.5C4.41015 14.5 1.5 11.5899 1.5 8.00001C1.5 4.41015 4.41015 1.5 8 1.5C11.5898 1.5 14.5 4.41015 14.5 8.00001ZM8 16C12.4183 16 16 12.4183 16 8.00001C16 3.58173 12.4183 0 8 0C3.58172 0 0 3.58173 0 8.00001C0 12.4183 3.58172 16 8 16ZM5 9.5242C4.58579 9.5242 4.25 9.85999 4.25 10.2742C4.25 10.6884 4.58579 11.0242 5 11.0242H11C11.4142 11.0242 11.75 10.6884 11.75 10.2742C11.75 9.85999 11.4142 9.5242 11 9.5242H5ZM5 7.87399C5.55228 7.87399 6 7.42628 6 6.87399C6 6.32171 5.55228 5.87399 5 5.87399C4.44772 5.87399 4 6.32171 4 6.87399C4 7.42628 4.44772 7.87399 5 7.87399ZM12 6.87399C12 7.42628 11.5523 7.87399 11 7.87399C10.4477 7.87399 10 7.42628 10 6.87399C10 6.32171 10.4477 5.87399 11 5.87399C11.5523 5.87399 12 6.32171 12 6.87399Z"
    })
  });
};
// pure annotation needed for tree-shaking
var FaceNeutral = /* #__PURE__ */withIcon(IconFaceNeutral);

var IconFaceSad = function IconFaceSad() {
  return jsx("g", {
    children: jsx("path", {
      d: "M14.5 8.00001C14.5 11.5899 11.5898 14.5 8 14.5C4.41015 14.5 1.5 11.5899 1.5 8.00001C1.5 4.41015 4.41015 1.5 8 1.5C11.5898 1.5 14.5 4.41015 14.5 8.00001ZM8 16C12.4183 16 16 12.4183 16 8.00001C16 3.58173 12.4183 0 8 0C3.58172 0 0 3.58173 0 8.00001C0 12.4183 3.58172 16 8 16ZM6.12454 11.2564C6.5289 10.6482 7.21811 10.25 7.99999 10.25C8.78188 10.25 9.47109 10.6482 9.87545 11.2564C10.1048 11.6013 10.5703 11.695 10.9153 11.4657C11.2602 11.2363 11.3539 10.7708 11.1246 10.4259C10.454 9.41731 9.30508 8.75 7.99999 8.75C6.69491 8.75 5.546 9.41731 4.87544 10.4259C4.6461 10.7708 4.73981 11.2363 5.08474 11.4657C5.42967 11.695 5.8952 11.6013 6.12454 11.2564ZM5 7.87399C5.55228 7.87399 6 7.42628 6 6.87399C6 6.32171 5.55228 5.87399 5 5.87399C4.44772 5.87399 4 6.32171 4 6.87399C4 7.42628 4.44772 7.87399 5 7.87399ZM12 6.87399C12 7.42628 11.5523 7.87399 11 7.87399C10.4477 7.87399 10 7.42628 10 6.87399C10 6.32171 10.4477 5.87399 11 5.87399C11.5523 5.87399 12 6.32171 12 6.87399Z"
    })
  });
};
// pure annotation needed for tree-shaking
var FaceSad = /* #__PURE__ */withIcon(IconFaceSad);

var IconFaceVeryHappy = function IconFaceVeryHappy() {
  return jsx("g", {
    children: jsx("path", {
      d: "M14.5 8.00001C14.5 11.5899 11.5898 14.5 8 14.5C4.41015 14.5 1.5 11.5899 1.5 8.00001C1.5 4.41015 4.41015 1.5 8 1.5C11.5898 1.5 14.5 4.41015 14.5 8.00001ZM8 16C12.4183 16 16 12.4183 16 8.00001C16 3.58173 12.4183 0 8 0C3.58172 0 0 3.58173 0 8.00001C0 12.4183 3.58172 16 8 16ZM10.5818 11.3255C11.3106 10.7355 11.8116 9.91404 12 9H4C4.18838 9.91404 4.68937 10.7355 5.41824 11.3255C6.14711 11.9154 7.05912 12.2377 8 12.2377C8.94088 12.2377 9.85289 11.9154 10.5818 11.3255ZM5.24999 6.25C4.98985 6.25 4.76017 6.38182 4.62454 6.58581C4.39521 6.93074 3.92967 7.02445 3.58474 6.79512C3.23981 6.56578 3.1461 6.10024 3.37544 5.75531C3.77727 5.15094 4.46664 4.75 5.24999 4.75C6.03334 4.75 6.72272 5.15094 7.12455 5.75531C7.35389 6.10024 7.26018 6.56578 6.91525 6.79512C6.57032 7.02445 6.10478 6.93074 5.87544 6.58581C5.73982 6.38182 5.51013 6.25 5.24999 6.25ZM10.1245 6.58581C10.2602 6.38182 10.4899 6.25 10.75 6.25C11.0101 6.25 11.2398 6.38182 11.3754 6.58581C11.6048 6.93074 12.0703 7.02445 12.4152 6.79512C12.7602 6.56578 12.8539 6.10024 12.6245 5.75531C12.2227 5.15094 11.5333 4.75 10.75 4.75C9.96664 4.75 9.27727 5.15094 8.87544 5.75531C8.6461 6.10024 8.73981 6.56578 9.08474 6.79512C9.42967 7.02445 9.89521 6.93074 10.1245 6.58581Z"
    })
  });
};
// pure annotation needed for tree-shaking
var FaceVeryHappy = /* #__PURE__ */withIcon(IconFaceVeryHappy);

var IconVerySad = function IconVerySad() {
  return jsx("g", {
    children: jsx("path", {
      d: "M14.5 8.00001C14.5 11.5899 11.5898 14.5 8 14.5C4.41015 14.5 1.5 11.5899 1.5 8.00001C1.5 4.41015 4.41015 1.5 8 1.5C11.5898 1.5 14.5 4.41015 14.5 8.00001ZM8 16C12.4183 16 16 12.4183 16 8.00001C16 3.58173 12.4183 0 8 0C3.58172 0 0 3.58173 0 8.00001C0 12.4183 3.58172 16 8 16ZM4.83314 4.82807C4.46203 4.64407 4.01203 4.79576 3.82804 5.16686C3.64405 5.53797 3.79573 5.98797 4.16684 6.17196L4.70313 6.43785L4.17121 6.69728C3.79892 6.87885 3.64431 7.32785 3.82589 7.70015C4.00746 8.07244 4.45647 8.22705 4.82876 8.04547L6.73148 7.11747C6.98842 6.99216 7.15177 6.73168 7.1527 6.44581C7.15363 6.15995 6.99197 5.89841 6.73586 5.77143L4.83314 4.82807ZM12.172 5.16686C11.988 4.79576 11.538 4.64407 11.1669 4.82807L9.26414 5.77143C9.00803 5.89841 8.84637 6.15995 8.8473 6.44581C8.84823 6.73168 9.01158 6.99216 9.26852 7.11747L11.1712 8.04547C11.5435 8.22705 11.9925 8.07244 12.1741 7.70015C12.3557 7.32785 12.2011 6.87885 11.8288 6.69728L11.2969 6.43785L11.8332 6.17196C12.2043 5.98797 12.356 5.53797 12.172 5.16686ZM6.12454 11.2564C6.5289 10.6482 7.21811 10.25 7.99999 10.25C8.78188 10.25 9.47109 10.6482 9.87545 11.2564C10.1048 11.6013 10.5703 11.695 10.9153 11.4657C11.2602 11.2363 11.3539 10.7708 11.1246 10.4259C10.454 9.41731 9.30508 8.75 7.99999 8.75C6.69491 8.75 5.546 9.41731 4.87544 10.4259C4.6461 10.7708 4.73981 11.2363 5.08474 11.4657C5.42967 11.695 5.8952 11.6013 6.12454 11.2564Z"
    })
  });
};
// pure annotation needed for tree-shaking
var FaceVerySad = /* #__PURE__ */withIcon(IconVerySad);

var IconFilter = function IconFilter() {
  return jsx("g", {
    children: jsx("path", {
      d: "M.752 0 .646.007a.75.75 0 0 0-.444 1.254l5.804 6.243v7.745a.75.75 0 0 0 1.04.692l2.489-1.04.1-.052a.75.75 0 0 0 .36-.641V7.493l5.802-6.231A.75.75 0 0 0 15.248 0H.752zm12.772 1.5L8.696 6.688l-.07.088a.75.75 0 0 0-.131.423l-.001 6.51-.987.412v-6.91L7.5 7.097a.75.75 0 0 0-.192-.399L2.472 1.5h11.052z"
    })
  });
};
// pure annotation needed for tree-shaking
var Filter = /* #__PURE__ */withIcon(IconFilter);

var IconFlag = function IconFlag() {
  return jsx("g", {
    children: jsx("path", {
      d: "M1.75 0a.75.75 0 0 1 .744.648l.007.102C3.258.402 4.183.23 5.266.23c1.018 0 1.686.254 2.861.953l.475.287.386.224c.721.402 1.154.536 1.737.536 1.303 0 2.321-.289 3.076-.851a.751.751 0 0 1 1.192.495L15 1.98v7a.75.75 0 0 1-.302.601c-1.034.771-2.366 1.149-3.973 1.149-1.018 0-1.687-.254-2.861-.953l-.475-.287-.387-.224c-.72-.403-1.153-.536-1.736-.536-1.28 0-2.192.278-2.765.8v5.721a.75.75 0 0 1-1.494.102L1 15.25V.75A.75.75 0 0 1 1.75 0zm3.516 1.73c-1.28 0-2.192.278-2.765.8v5.225c.757-.353 1.682-.525 2.765-.525 1.018 0 1.686.254 2.861.953l.475.287.386.224c.721.402 1.154.536 1.737.536 1.103 0 2.001-.207 2.709-.611l.065-.041V3.247l-.183.072c-.677.244-1.436.379-2.273.406l-.318.005c-1.018 0-1.687-.254-2.861-.953l-.475-.287-.387-.224c-.72-.403-1.153-.536-1.736-.536z"
    })
  });
};
// pure annotation needed for tree-shaking
var Flag = /* #__PURE__ */withIcon(IconFlag);

var IconFlask = function IconFlask() {
  return jsx("g", {
    children: jsx("path", {
      d: "M11.29 0a.71.71 0 0 1 0 1.418h-.79v3.126a6.001 6.001 0 1 1-5 0V1.418h-.79A.71.71 0 0 1 4.71 0h6.58zM9 1.418H7v4.044a.2.2 0 0 1-.143.191c-.368.111-.643.21-.823.298a4.519 4.519 0 0 0-2.066 2.05L6.25 8a.75.75 0 0 1 0 1.5H3.527a4.55 4.55 0 0 0 0 1H6.25a.75.75 0 1 1 0 1.5l-2.282.001a4.5 4.5 0 1 0 6.048-6.025 5.915 5.915 0 0 0-.873-.322A.2.2 0 0 1 9 5.463L9 1.418z"
    })
  });
};
// pure annotation needed for tree-shaking
var Flask = /* #__PURE__ */withIcon(IconFlask);

var IconFolder = function IconFolder() {
  return jsx("g", {
    children: jsx("path", {
      d: "m1.05 1.001 4.743.016c.396.001.754.224.931.57l.052.12.84 2.301 7.335.01c.541 0 .986.41 1.043.935l.006.114v8.883c0 .541-.41.987-.936 1.044L14.95 15H1.05a1.05 1.05 0 0 1-1.044-.936L0 13.95v-8.9c0-.104.015-.206.044-.301H0V2.051c0-.45.284-.84.76-1l.135-.037L1.05 1zm.45 4.5V13.5h13V5.516l-13-.015zm0-2.999-.001 1.499 4.518.005-.543-1.491L1.5 2.502z"
    })
  });
};
// pure annotation needed for tree-shaking
var Folder = /* #__PURE__ */withIcon(IconFolder);

var IconFolderArrow = function IconFolderArrow() {
  return jsx("g", {
    children: jsx("path", {
      d: "m1.064 5.01 4.57.007c.384 0 .7.283.751.648l.007.102v.673l5.546-.012c.505 0 .928.35 1.036.816l.02.119.006.114V9l1.938.023c.548.001.999.41 1.056.936l.006.115c0 .131-.026.261-.074.383l-.057.119-2.706 4.879a1.064 1.064 0 0 1-.805.537l-.129.008H1.064c-.549 0-1-.41-1.058-.935L0 14.951V6.059c0-.54.415-.985.948-1.042l.116-.007zm3.185 5.494-2.21 3.997h9.92l2.208-3.98-9.918-.017zM1.519 6.51v5.8l1.526-2.757c.166-.3.468-.499.807-.539l.129-.007 7.5-.009V7.925l-5.85.013a.758.758 0 0 1-.731-.55l-.02-.098-.008-.101v-.675L1.52 6.51zM12.25 0a.75.75 0 0 1 .743.648L13 .749v2.52l.72-.719.084-.072a.75.75 0 0 1 1.049 1.048l-.073.084-1.92 1.919-.084.072a.747.747 0 0 1-.382.144l-.144.003-.057-.002-.092-.013-.073-.018-.067-.025-.072-.034-.085-.055a.785.785 0 0 1-.084-.072L9.8 3.61a.749.749 0 0 1 .976-1.132l.084.072.64.64V.75a.75.75 0 0 1 .75-.75z"
    })
  });
};
// pure annotation needed for tree-shaking
var FolderArrow = /* #__PURE__ */withIcon(IconFolderArrow);

var IconFolderPencil = function IconFolderPencil() {
  return jsx("g", {
    children: jsx("path", {
      d: "m1.05 1.001 4.743.016c.396.001.754.224.931.57l.052.12.839 2.301 1.087.001a.75.75 0 0 1 .1 1.493L8.7 5.51l-7.2-.008v7.998h13V9.046a.75.75 0 0 1 .648-.743l.102-.007a.75.75 0 0 1 .743.649l.007.101v4.904c0 .541-.41.987-.936 1.044L14.95 15H1.05a1.05 1.05 0 0 1-1.044-.936L0 13.95v-8.9L.003 5H0V2.051c0-.45.284-.84.76-1l.135-.037L1.05 1zm12.815 1.19.084.074 1.827 1.827a.753.753 0 0 1 .073.98l-.073.085-5.222 5.183a.747.747 0 0 1-.276.174l-.106.03-2.277.45a.756.756 0 0 1-.9-.79l.013-.097.45-2.278a.748.748 0 0 1 .134-.301l.073-.084 5.223-5.184a.751.751 0 0 1 .977-.068zM11.602 5.66 8.889 8.353l-.188.947.95-.188 2.71-2.69-.76-.763zm1.816-1.803-.75.743.76.763.751-.745-.76-.76zM1.5 2.502v1.499l4.518.005-.544-1.491L1.5 2.502z"
    })
  });
};
// pure annotation needed for tree-shaking
var FolderPencil = /* #__PURE__ */withIcon(IconFolderPencil);

var IconFolderUser = function IconFolderUser() {
  return jsx("g", {
    children: jsx("path", {
      d: "M1.05 15a1.05 1.05 0 0 1-1.044-.936L0 13.95v-8.9c0-.104.015-.206.044-.301H0V2.051c0-.45.284-.84.76-1l.135-.037L1.05 1l4.743.016c.396.001.754.224.931.57l.052.12.84 2.301 7.335.01c.541 0 .986.41 1.043.935l.006.114v8.883c0 .541-.41.987-.936 1.044L14.95 15H1.05zm.45-9.499V13.5h2.81c.364-.965 1.103-2 2.518-2h2.844c1.415 0 2.154 1.035 2.519 2H14.5V5.516l-13-.015zm6.75.999c1.105 0 2 .824 2 1.84 0 1.018-.895 2.66-2 2.66s-2-1.642-2-2.66c0-1.016.895-1.84 2-1.84zM1.5 2.502l-.001 1.499 4.518.005-.543-1.491L1.5 2.502z"
    })
  });
};
// pure annotation needed for tree-shaking
var FolderUser = /* #__PURE__ */withIcon(IconFolderUser);

var IconFootprint = function IconFootprint() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8.178 11.968c.16-.517.685-.824 1.208-.725l.112.028 2.425.736c.558.17.872.757.701 1.312l-.042.113-.055.109-.77 1.312A2.338 2.338 0 0 1 9.062 15.9a1.988 1.988 0 0 1-1.368-2.341l.039-.144.445-1.448zm1.313.87-.314 1.017a.488.488 0 0 0 .171.532l.073.046.083.033c.327.1.678-.014.885-.276l.063-.093.483-.82-1.444-.439zM4.93 9.445c.584 0 1.058.47 1.058 1.05v1.515c0 1.099-.897 1.99-2.003 1.99-1.082 0-2.02-.74-2.269-1.786l-.35-1.478a1.054 1.054 0 0 1 1.029-1.291h2.535zm-.454 1.501h-1.51l.22.925a.82.82 0 0 0 .799.628l.088-.008a.49.49 0 0 0 .404-.481v-1.064zm4.735-5.16c.626-2.037 2.806-3.626 4.134-3.223 1.305.396 2.054 2.89 1.431 4.913a31.233 31.233 0 0 1-1.336 3.554 1.06 1.06 0 0 1-1.157.608l-.118-.029-2.43-.737-.121-.046a1.051 1.051 0 0 1-.608-.767c-.28-1.492-.212-2.92.205-4.273zm3.693-1.788c-.405-.123-1.716.815-2.188 2.048l-.061.178-.087.307-.072.31a7.27 7.27 0 0 0-.105 2.264l.043.338.013.075 1.77.538.131-.302c.374-.893.702-1.799.984-2.717.408-1.324-.075-2.931-.428-3.039zM3.815 0C5.203 0 6.82 2.153 6.82 4.283c0 1.415-.356 2.8-1.062 4.146a1.057 1.057 0 0 1-.807.557l-.13.008H2.28c-.534 0-.984-.397-1.049-.924A31.158 31.158 0 0 1 1 4.283C1 2.167 2.45 0 3.815 0zm0 1.5c-.37 0-1.305 1.398-1.305 2.783 0 .96.047 1.921.142 2.884l.036.326h1.85l.036-.068a7.393 7.393 0 0 0 .707-2.507l.022-.317.007-.318c0-1.38-1.053-2.782-1.495-2.782z"
    })
  });
};
// pure annotation needed for tree-shaking
var Footprint = /* #__PURE__ */withIcon(IconFootprint);

var IconFullscreen = function IconFullscreen() {
  return jsxs("g", {
    children: [jsx("path", {
      d: "M1.5 9.001 1.05 9l-.114.006A1.05 1.05 0 0 0 0 10.05v4.899l.006.114A1.05 1.05 0 0 0 1.05 16h4.9l.114-.006A1.05 1.05 0 0 0 7 14.949V14.5H1.5V9.001Zm0-2.002h-.45a1.05 1.05 0 0 1-1.044-.936L0 5.95v-4.9C0 .509.41.063.936.006L1.05 0l4.903.016c.54.001.984.41 1.04.936L7 1.066V1.5H1.5v5.499Zm13 0h.45l.114-.006A1.05 1.05 0 0 0 16 5.949V1.066l-.006-.114a1.05 1.05 0 0 0-1.04-.936L10.05 0l-.114.006A1.05 1.05 0 0 0 9 1.05v.45h5.5v5.499ZM9 14.5v.449l.006.114A1.05 1.05 0 0 0 10.05 16h4.9l.114-.006A1.05 1.05 0 0 0 16 14.949v-4.883l-.006-.114a1.05 1.05 0 0 0-1.04-.936l-.454-.002V14.5H9Z"
    }), jsx("path", {
      d: "M0 9h1.5v1.5H0zm0-3.5h1.5V7H0zM14.5 9H16v1.5h-1.5zm0-3.5H16V7h-1.5zM9 16v-1.5h1.5V16zm-3.5 0v-1.5H7V16zM9 1.5V0h1.5v1.5zm-3.5 0V0H7v1.5z"
    })]
  });
};
// pure annotation needed for tree-shaking
var Fullscreen = /* #__PURE__ */withIcon(IconFullscreen);

var IconGear = function IconGear() {
  return jsx("g", {
    children: jsx("path", {
      d: "M9 0a.75.75 0 0 1 .744.648l.118.851.112.031c.419.128.823.296 1.207.502l.098.054.686-.518a.75.75 0 0 1 .9-.005l.084.072 1.416 1.416a.75.75 0 0 1 .068.983l-.522.69.056.098c.204.384.371.787.498 1.205l.03.11.857.119a.75.75 0 0 1 .641.642L16 7v2.002a.75.75 0 0 1-.648.743l-.86.119-.031.11a6.718 6.718 0 0 1-.5 1.202l-.055.096.526.694a.75.75 0 0 1 .005.9l-.072.084-1.416 1.416a.75.75 0 0 1-.984.067l-.696-.528-.095.054c-.383.204-.785.371-1.201.498l-.11.03-.119.866a.75.75 0 0 1-.643.641l-.1.007H6.999a.75.75 0 0 1-.743-.648l-.12-.866-.108-.03a6.718 6.718 0 0 1-1.201-.497l-.096-.055-.696.528a.75.75 0 0 1-.9.005l-.084-.072-1.416-1.416a.75.75 0 0 1-.067-.984l.525-.694-.054-.096a6.718 6.718 0 0 1-.5-1.201l-.032-.111-.86-.119a.75.75 0 0 1-.64-.643L0 9.001V6.999a.75.75 0 0 1 .648-.743l.855-.119.032-.11c.127-.418.294-.821.498-1.205l.055-.099-.52-.689a.75.75 0 0 1-.005-.9l.072-.083 1.416-1.416a.75.75 0 0 1 .984-.067l.685.519.098-.055c.385-.206.79-.374 1.208-.502l.112-.032.118-.85a.75.75 0 0 1 .642-.641L7 0zm-.654 1.5h-.693l-.097.708a.75.75 0 0 1-.495.605l-.101.028a5.22 5.22 0 0 0-1.87.776.75.75 0 0 1-.87-.026l-.57-.432-.491.49.433.574a.75.75 0 0 1 .084.765l-.057.103a5.22 5.22 0 0 0-.772 1.867.75.75 0 0 1-.633.597l-.714.098v.693l.717.1a.75.75 0 0 1 .605.494l.028.1a5.22 5.22 0 0 0 .774 1.862.75.75 0 0 1-.027.87l-.438.578.49.49.58-.439a.75.75 0 0 1 .766-.084l.103.057a5.22 5.22 0 0 0 1.86.77.75.75 0 0 1 .596.634l.099.72h.693l.101-.721a.75.75 0 0 1 .494-.605l.102-.028a5.22 5.22 0 0 0 1.86-.77.75.75 0 0 1 .868.027l.579.44.49-.491-.437-.579a.75.75 0 0 1-.084-.766l.057-.103a5.22 5.22 0 0 0 .774-1.861.75.75 0 0 1 .633-.595l.716-.1v-.693l-.713-.098a.75.75 0 0 1-.605-.495l-.028-.102a5.22 5.22 0 0 0-.772-1.867.75.75 0 0 1 .027-.868l.432-.574-.49-.49-.57.432a.75.75 0 0 1-.767.084l-.103-.058a5.22 5.22 0 0 0-1.87-.776.75.75 0 0 1-.595-.632L8.346 1.5zM8 4.24a3.753 3.753 0 1 1 0 7.506A3.753 3.753 0 0 1 8 4.24zM8 5.74a2.252 2.252 0 1 0 0 4.504A2.252 2.252 0 0 0 8 5.74z"
    })
  });
};
// pure annotation needed for tree-shaking
var Gear = /* #__PURE__ */withIcon(IconGear);

var IconGlobe = function IconGlobe() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm2.191 11.002H5.81C6.262 13.094 7.148 14.5 8 14.5c.852 0 1.738-1.406 2.191-3.498zM4.273 11h-2.04a6.527 6.527 0 0 0 3.001 2.883c-.376-.708-.676-1.564-.886-2.52L4.273 11zm9.494 0h-2.04c-.21 1.102-.538 2.086-.96 2.883a6.531 6.531 0 0 0 2.864-2.634l.136-.249zm-9.702-4.5H1.674a6.518 6.518 0 0 0 0 3h2.392a17.003 17.003 0 0 1 0-3zm6.36-.001h-4.85a15.066 15.066 0 0 0 0 3.001h4.85a15.065 15.065 0 0 0 0-3.001zm3.901 0h-2.391a17.004 17.004 0 0 1 0 3h2.39a6.518 6.518 0 0 0 .001-3zM5.234 2.116l-.064.03A6.528 6.528 0 0 0 2.232 5h2.04c.212-1.103.539-2.087.962-2.884zM8 1.5l-.096.006c-.822.1-1.659 1.479-2.095 3.493h4.382C9.756 2.99 8.923 1.614 8.102 1.507L8 1.5zm2.766.616.077.148c.386.77.686 1.701.884 2.736h2.04a6.527 6.527 0 0 0-3.001-2.884z"
    })
  });
};
// pure annotation needed for tree-shaking
var Globe = /* #__PURE__ */withIcon(IconGlobe);

var IconGovBuilding = function IconGovBuilding() {
  return jsx("g", {
    children: jsx("path", {
      d: "M15.143 14.5c.41 0 .744.336.744.75s-.333.75-.744.75H.746a.747.747 0 0 1-.744-.75c0-.414.333-.75.744-.75h14.397zM11.17 6h1.49v6h.992c.411 0 .745.336.745.75s-.334.75-.745.75H2.235a.747.747 0 0 1-.744-.75c0-.414.333-.75.744-.75h.993V6h1.49v6H7.2V6h1.489v6h2.482V6zM7.552.113a.74.74 0 0 1 .675-.057l.106.054 7.31 4.5c.61.376.387 1.297-.285 1.383L15.254 6H.746C.032 6-.256 5.103.269 4.673l.084-.06 7.2-4.5zm.396 1.517L3.358 4.5h9.252L7.948 1.63z"
    })
  });
};
// pure annotation needed for tree-shaking
var GovBuilding = /* #__PURE__ */withIcon(IconGovBuilding);

var IconGraduationHat = function IconGraduationHat() {
  return jsx("g", {
    children: jsx("path", {
      d: "M7.693 2.068a.75.75 0 0 1 .494-.042l.12.042 7.252 3.248a.75.75 0 0 1 .104 1.312l-.104.056-.31.138.001 3.204a.75.75 0 0 1-1.493.102l-.007-.102V7.494L13 7.83v3.929C13 12.903 10.38 14 8 14c-2.298 0-4.836-1.037-4.992-2.135L3 11.759v-3.93L.446 6.684a.75.75 0 0 1-.104-1.312l.104-.056 7.247-3.248zM4.5 8.5v3.036l.03.024c.14.103.337.216.573.325l.184.082c.79.332 1.75.538 2.713.538.965 0 1.926-.204 2.715-.535.254-.106.473-.22.64-.326l.113-.079.032-.026V8.502l-3.193 1.43a.75.75 0 0 1-.494.043l-.12-.042L4.5 8.5zM8 3.573 2.586 6 8 8.427 13.417 6 8 3.573z"
    })
  });
};
// pure annotation needed for tree-shaking
var GraduationHat = /* #__PURE__ */withIcon(IconGraduationHat);

var IconGrid = function IconGrid() {
  return jsx("g", {
    children: jsx("path", {
      d: "m1.05 0 13.901.017c.541 0 .986.41 1.043.936l.006.114V14.95c0 .541-.41.987-.936 1.044L14.95 16H1.05a1.05 1.05 0 0 1-1.044-.936L0 14.95V1.05C0 .51.41.064.936.007L1.051 0zm3.7 11.25H1.5v3.25h3.25v-3.25zm5 0h-3.5v3.25h3.5v-3.25zm4.75 0h-3.25v3.25h3.25v-3.25zM4.75 6.253H1.5V9.75h3.25V6.254zm5 0h-3.5V9.75h3.5V6.254zm4.75 0h-3.25V9.75h3.25V6.254zm-13-4.753v3.253h3.25v-3.25L1.5 1.501zm4.75.004v3.25h3.5V1.508l-3.5-.004zm5 .006v3.243h3.25V1.516l-3.25-.005z"
    })
  });
};
// pure annotation needed for tree-shaking
var Grid = /* #__PURE__ */withIcon(IconGrid);

var IconGridCarets = function IconGridCarets() {
  return jsx("g", {
    children: jsx("path", {
      d: "M6.565 9c.111 0 .222.055.307.163.155.197.17.506.042.723l-.042.063-3.063 3.888-.05.054a.366.366 0 0 1-.525-.004l-.045-.05L.13 9.951.085 9.89a.68.68 0 0 1 0-.661l.042-.063.05-.053a.379.379 0 0 1 .26-.11V9h6.127zm9 0c.111 0 .222.055.307.163.155.197.17.506.042.723l-.042.063-3.063 3.888-.05.054a.366.366 0 0 1-.525-.004l-.045-.05-3.06-3.886-.043-.062a.68.68 0 0 1 0-.661l.042-.063.05-.053a.379.379 0 0 1 .26-.11V9h6.127zm-9-7c.111 0 .222.055.307.163.155.197.17.506.042.723l-.042.063-3.063 3.888-.05.054a.366.366 0 0 1-.525-.004l-.045-.05L.13 2.951.085 2.89a.68.68 0 0 1 0-.661l.042-.063.05-.053a.379.379 0 0 1 .26-.11V2h6.127zm9 0c.111 0 .222.055.307.163.155.197.17.506.042.723l-.042.063-3.063 3.888-.05.054a.366.366 0 0 1-.525-.004l-.045-.05-3.06-3.886-.043-.062a.68.68 0 0 1 0-.661l.042-.063.05-.053a.379.379 0 0 1 .26-.11V2h6.127z"
    })
  });
};
// pure annotation needed for tree-shaking
var GridCarets = /* #__PURE__ */withIcon(IconGridCarets);

var IconGridCircles = function IconGridCircles() {
  return jsx("g", {
    children: jsx("path", {
      d: "M13.5 11a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zm-11 0a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM8 11a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zm5.5 1.667a.833.833 0 1 0 0 1.666.833.833 0 0 0 0-1.666zm-11 0a.833.833 0 1 0 0 1.666.833.833 0 0 0 0-1.666zm5.5 0a.833.833 0 1 0 0 1.666.833.833 0 0 0 0-1.666zM2.5 5.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zm11 0a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM8 5.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM2.5 7.167a.833.833 0 1 0 0 1.666.833.833 0 0 0 0-1.666zm11 0a.833.833 0 1 0 0 1.666.833.833 0 0 0 0-1.666zm-5.5 0a.833.833 0 1 0 0 1.666.833.833 0 0 0 0-1.666zM2.5 0a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zm11 0a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM8 0a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM2.5 1.667a.833.833 0 1 0 0 1.666.833.833 0 0 0 0-1.666zm11 0a.833.833 0 1 0 0 1.666.833.833 0 0 0 0-1.666zm-5.5 0a.833.833 0 1 0 0 1.666.833.833 0 0 0 0-1.666z"
    })
  });
};
// pure annotation needed for tree-shaking
var GridCircles = /* #__PURE__ */withIcon(IconGridCircles);

var IconUngroup = function IconUngroup() {
  return jsx("g", {
    children: jsx("path", {
      d: "m1.05 0 13.901.017c.541 0 .986.41 1.043.936l.006.114V14.95c0 .541-.41.987-.936 1.044L14.95 16H1.05a1.05 1.05 0 0 1-1.044-.936L0 14.95V1.05C0 .51.41.064.936.007L1.05 0zm6.2 8.75H1.5v5.75h5.75V8.75zm7.25 0H8.75v5.75h5.75V8.75zM1.5 1.5v5.748h5.75V1.507L1.5 1.501zm7.25.007V7.25h5.75V1.516l-5.75-.008z"
    })
  });
};
// pure annotation needed for tree-shaking
var Group = /* #__PURE__ */withIcon(IconUngroup);

var IconHappy = function IconHappy() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm0 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zm3.738 7.315a.75.75 0 0 1 .386.988 4.5 4.5 0 0 1-8.248 0A.75.75 0 1 1 5.25 9.2a3 3 0 0 0 5.5 0 .75.75 0 0 1 .988-.386zM4.5 5.5a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"
    })
  });
};
// pure annotation needed for tree-shaking
var Happy = /* #__PURE__ */withIcon(IconHappy);

var IconHappyFilled = function IconHappyFilled() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm3.738 8.815a.75.75 0 0 0-.988.386 3 3 0 0 1-5.5 0 .75.75 0 1 0-1.374.602 4.5 4.5 0 0 0 8.248 0 .75.75 0 0 0-.386-.988zM4.5 5.5a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm7 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"
    })
  });
};
// pure annotation needed for tree-shaking
var HappyFilled = /* #__PURE__ */withIcon(IconHappyFilled);

var IconHealth = function IconHealth() {
  return jsx("g", {
    children: jsx("path", {
      d: "M3.75 0a.75.75 0 0 1 0 1.5H2.528v3.765a3.985 3.985 0 0 0 7.966.199l.005-.2-.001-3.764H9.25a.75.75 0 0 1 0-1.5h2a.75.75 0 0 1 .707 1.001L11.999 1v4.265A5.486 5.486 0 0 1 7.25 10.7v1.549a2.25 2.25 0 0 0 4.07 1.323 2.125 2.125 0 1 1 1.358.668 3.75 3.75 0 0 1-6.923-1.792l-.005-.199v-1.553a5.487 5.487 0 0 1-4.717-5.211l-.005-.221V1l.015.001A.749.749 0 0 1 1.75 0h2zm9.125 11.5a.625.625 0 1 0 0 1.25.625.625 0 0 0 0-1.25z"
    })
  });
};
// pure annotation needed for tree-shaking
var Health = /* #__PURE__ */withIcon(IconHealth);

var IconHeart = function IconHeart() {
  return jsx("g", {
    children: jsx("path", {
      d: "M11.834 1c1.105 0 2.164.453 2.945 1.259A4.362 4.362 0 0 1 16 5.296a4.37 4.37 0 0 1-1.058 2.86l-.163.177-6.244 6.438a.74.74 0 0 1-.986.076l-.085-.076L1.22 8.333c-1.627-1.678-1.627-4.397 0-6.074A4.075 4.075 0 0 1 6.95 2.1l.161.158.889.915.888-.915A4.102 4.102 0 0 1 11.834 1zm0 1.5c-.634 0-1.247.237-1.729.668l-.14.135L8.502 4.81a.7.7 0 0 1-1.005 0L6.034 3.303a2.578 2.578 0 0 0-3.737 0c-1.018 1.05-1.06 2.741-.127 3.845l.127.14 5.702 5.881 5.704-5.881a2.854 2.854 0 0 0 .79-1.789l.007-.203c0-.751-.288-1.469-.798-1.993a2.602 2.602 0 0 0-1.868-.803z"
    })
  });
};
// pure annotation needed for tree-shaking
var Heart = /* #__PURE__ */withIcon(IconHeart);

var IconHeartFilled = function IconHeartFilled() {
  return jsx("g", {
    children: jsx("path", {
      d: "M14.78 2.259A4.102 4.102 0 0 0 11.833 1a4.102 4.102 0 0 0-2.946 1.259L8 3.174l-.889-.915a4.077 4.077 0 0 0-5.89 0c-1.628 1.677-1.628 4.396 0 6.074l6.243 6.438a.741.741 0 0 0 1.071 0l6.244-6.438A4.362 4.362 0 0 0 16 5.296c0-1.14-.44-2.232-1.22-3.037z"
    })
  });
};
// pure annotation needed for tree-shaking
var HeartFilled = /* #__PURE__ */withIcon(IconHeartFilled);

var IconHeartHand = function IconHeartHand() {
  return jsx("g", {
    children: jsx("path", {
      d: "m5.206 7.517.148.015c1.126.226 2.032.723 2.691 1.487l.037.045 1.213.001c.838 0 1.553.524 1.835 1.261l.042.122 1.95-1.018a1.973 1.973 0 0 1 2.531.62l.089.14c.532.92.215 2.096-.707 2.627l-.722.412-.687.382-.967.521-.601.312-.568.283-.534.254-.255.117-.485.211c-1.093.462-1.921.691-2.504.691-.557 0-1.178-.12-1.885-.342-.157-.05-.316-.103-.481-.162l-.512-.191-1.512-.624-.199-.078-.278-.101H.752a.752.752 0 0 1-.745-.649L0 13.752V8.268c0-.38.283-.694.65-.744l.102-.007h4.454zm-1.461 1.5v4.219l.206.08 1.174.49c.432.174.804.31 1.153.42.574.18 1.055.273 1.434.273.204 0 .483-.054.835-.163l.32-.106c.112-.04.23-.085.354-.133l.39-.16.424-.186.459-.212.492-.239.526-.265.559-.291.592-.318.624-.344.996-.565a.423.423 0 0 0 .156-.578.467.467 0 0 0-.535-.214l-.085.035-3.095 1.616-.018.007-.023.027a1.96 1.96 0 0 1-1.242.568l-.147.005H6.476a.751.751 0 0 1-.102-1.493l.102-.007h2.818a.46.46 0 0 0 .083-.91l-.083-.007H7.712a.753.753 0 0 1-.62-.326c-.404-.587-.995-.98-1.807-1.185l-.165-.038H3.745zm-1.504 0h-.737v3.984h.737V9.016zM11.905 0a2.61 2.61 0 0 1 1.986 4.302l-.14.152-3.913 3.91a.475.475 0 0 1-.597.061l-.075-.06-3.913-3.911a2.607 2.607 0 0 1 0-3.69A2.612 2.612 0 0 1 8.81.637l.136.127.557.556.557-.556A2.61 2.61 0 0 1 11.905 0zm0 1.5c-.251 0-.493.086-.689.242l-.093.083-1.125 1.123a.703.703 0 0 1-.905.074l-.087-.074L7.88 1.825a1.105 1.105 0 0 0-1.563 0 1.11 1.11 0 0 0-.086 1.472l.086.096L9.5 6.575l3.186-3.182c.178-.178.29-.41.318-.659l.007-.125a1.109 1.109 0 0 0-1.107-1.108z"
    })
  });
};
// pure annotation needed for tree-shaking
var HeartHand = /* #__PURE__ */withIcon(IconHeartHand);

var IconHelp = function IconHelp() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm0 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zm-.035 10.09c.375 0 .686.28.735.643l.006.101v.321a.743.743 0 0 1-.74.745.743.743 0 0 1-.735-.644l-.007-.101v-.32c0-.412.332-.746.741-.746zm0-8.94C9.603 2.65 11 4.052 11 5.697c0 .597-.258 1.285-.62 1.812a2.06 2.06 0 0 1-.381.407l-.134.107-.478.367c-.466.377-.669.714-.68 1.282a.743.743 0 0 1-.758.728.743.743 0 0 1-.725-.76c.021-.988.395-1.676 1.071-2.274l.215-.179.534-.41a.73.73 0 0 0 .116-.115c.204-.297.357-.706.357-.965 0-.822-.732-1.557-1.552-1.557-.76 0-1.407.626-1.476 1.401l-.006.138a.743.743 0 0 1-.742.745A.743.743 0 0 1 5 5.68C5 4.03 6.332 2.65 7.965 2.65z"
    })
  });
};
// pure annotation needed for tree-shaking
var Help = /* #__PURE__ */withIcon(IconHelp);

var IconHelpFilled = function IconHelpFilled() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm-.035 11.59a.743.743 0 0 0-.741.744v.321l.007.101c.049.364.359.644.734.644.41 0 .741-.334.741-.745v-.32l-.006-.102a.743.743 0 0 0-.735-.644zm0-8.94C6.332 2.65 5 4.029 5 5.68c0 .41.332.744.741.744.41 0 .742-.333.742-.745l.006-.138c.07-.775.716-1.401 1.476-1.401.82 0 1.552.735 1.552 1.557 0 .26-.153.668-.357.965a.73.73 0 0 1-.116.115l-.534.41-.215.18c-.676.597-1.05 1.285-1.071 2.273a.743.743 0 0 0 .725.76.743.743 0 0 0 .757-.728c.012-.568.215-.905.681-1.282l.478-.367.134-.107c.156-.132.28-.26.38-.407.363-.527.621-1.215.621-1.812C11 4.052 9.603 2.65 7.965 2.65z"
    })
  });
};
// pure annotation needed for tree-shaking
var HelpFilled = /* #__PURE__ */withIcon(IconHelpFilled);

var IconHouse = function IconHouse() {
  return jsx("g", {
    children: jsx("path", {
      d: "M4.245 1a.75.75 0 0 1 .102 1.494l-.102.007h-.741v2.135l4.012-3.454a.75.75 0 0 1 .881-.072l.098.072L15.74 7.43a.75.75 0 0 1-.891 1.203l-.09-.066-.752-.65v6.034c0 .541-.409.987-.935 1.044l-.115.006H3.054a1.05 1.05 0 0 1-1.045-.936l-.006-.114V7.909l-.763.657a.75.75 0 0 1-.98 0l-.078-.08a.75.75 0 0 1 0-.979l.079-.078 1.742-1.501V1.75a.75.75 0 0 1 .649-.743L2.754 1h1.491zm3.76 1.74L3.503 6.616V13.5h2.001V7.757a.75.75 0 0 1 .65-.744l.101-.006h3.501a.75.75 0 0 1 .744.648l.006.102v5.742h2.001V6.692l.005-.066L8.005 2.74zm1 5.767h-2v4.992h2V8.507z"
    })
  });
};
// pure annotation needed for tree-shaking
var House = /* #__PURE__ */withIcon(IconHouse);

var IconImage = function IconImage() {
  return jsx("g", {
    children: jsx("path", {
      d: "M15 0a1 1 0 0 1 1 1v11.939l.03.03L16 13V15a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1V1a1 1 0 0 1 1-1h14zM5.5 9.061l-4 3.999v1.44h13v-.939l-3.056-3.056-1.975 1.58a.75.75 0 0 1-.914.019l-.085-.074L5.5 9.061zm9-7.561h-13v9.438L4.97 7.47a.75.75 0 0 1 .976-.073l.084.073 3.025 3.024 1.976-1.58a.75.75 0 0 1 .914-.018l.085.074 2.47 2.469V1.5zm-5 2a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zm0 1.5a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"
    })
  });
};
// pure annotation needed for tree-shaking
var Image = /* #__PURE__ */withIcon(IconImage);

var IconImageArrow = function IconImageArrow() {
  return jsx("g", {
    children: jsx("path", {
      d: "M15 0a1 1 0 0 1 1 1v8.204l-1.5-.3V1.5h-13v9.438L4.97 7.47a.75.75 0 0 1 .976-.073l.084.073 1.828 1.827.531 2.653L5.5 9.061l-4 3.999v1.44h7.199l.3 1.5H1a1 1 0 0 1-1-1V1a1 1 0 0 1 1-1h14zm-4.998 9.504 4.52.883a.423.423 0 0 1 .217.714l-.926.926 2.059 2.058a.423.423 0 0 1 0 .598l-1.187 1.187a.423.423 0 0 1-.598 0l-2.059-2.059-.925.927a.423.423 0 0 1-.714-.218L9.506 10a.423.423 0 0 1 .496-.496zM9.5 3.5a2.5 2.5 0 0 1 1.263 4.658L8.122 7.63a.423.423 0 0 0-.396.13A2.5 2.5 0 0 1 9.5 3.5zm0 1.5a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"
    })
  });
};
// pure annotation needed for tree-shaking
var ImageArrow = /* #__PURE__ */withIcon(IconImageArrow);

var IconInbox = function IconInbox() {
  return jsx("g", {
    children: jsx("path", {
      d: "M12.074 1a.75.75 0 0 1 .614.32l.054.09 3.177 6.25c.035.084.06.173.072.266l.009.141v4.883a2.05 2.05 0 0 1-1.897 2.044L13.95 15H2.05a2.05 2.05 0 0 1-2.044-1.897L0 12.95v-4.9c0-.198.055-.383.15-.541l2.805-6.073a.75.75 0 0 1 .572-.428L3.636 1h8.438zM1.5 8.5v4.45c0 .273.2.5.46.543l.09.007h11.9c.273 0 .5-.2.543-.46l.007-.09V8.516l-3.045-.005a4.004 4.004 0 0 1-3.287 2.467l-.212.017L7.75 11a4.002 4.002 0 0 1-3.549-2.152l-.091-.187-.068-.158L1.5 8.5zm10.113-6.001H4.115L2.036 7.001l2.553.003a.75.75 0 0 1 .698.478l.03.099a2.501 2.501 0 0 0 4.864.005.75.75 0 0 1 .628-.568l.103-.007 2.996.004-2.295-4.516z"
    })
  });
};
// pure annotation needed for tree-shaking
var Inbox = /* #__PURE__ */withIcon(IconInbox);

var IconInfo = function IconInfo() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm0 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zm.775 5.16.007.104v4.034l.26-.289a.753.753 0 0 1 .969-.128l.088.067a.74.74 0 0 1 .129.962l-.068.088-1.567 1.748c-.434.483-1.22.22-1.302-.39l-.007-.105v-4.03l-.225.25a.753.753 0 0 1-.97.13l-.087-.068a.74.74 0 0 1-.13-.961l.067-.088 1.534-1.713c.434-.484 1.22-.222 1.302.388zM8.033 2.5c.38 0 .693.28.742.642l.007.101v1.045c0 .41-.335.744-.749.744a.747.747 0 0 1-.742-.643l-.007-.1V3.242c0-.41.335-.743.75-.743z"
    })
  });
};
// pure annotation needed for tree-shaking
var Info = /* #__PURE__ */withIcon(IconInfo);

var IconInfoFilled = function IconInfoFilled() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm.775 6.632c-.081-.613-.868-.877-1.302-.39L5.94 7.963l-.067.088a.746.746 0 0 0 .13.967l.088.067c.305.2.72.15.969-.13l.224-.252.001 4.498.007.106c.082.613.868.877 1.302.39l1.567-1.756.068-.088a.746.746 0 0 0-.129-.967l-.088-.067a.75.75 0 0 0-.97.128l-.26.29.001-4.5zm-.742-4.181a.748.748 0 0 0-.749.747v1.05l.007.102a.749.749 0 0 0 1.491-.101V3.198l-.007-.101a.749.749 0 0 0-.742-.646z"
    })
  });
};
// pure annotation needed for tree-shaking
var InfoFilled = /* #__PURE__ */withIcon(IconInfoFilled);

var IconInsights = function IconInsights() {
  return jsx("g", {
    children: jsx("path", {
      d: "M11.134.003h-5.29c-.318 0-.601.198-.709.495l-3.09 8.514-.028.098c-.1.459.25.91.737.91l2.51-.001-1.566 4.368c-.158.522.02 1.036.424 1.344a1.35 1.35 0 0 0 1.771-.128l7.951-10.387.06-.089a.752.752 0 0 0-.66-1.118h-2.773l1.349-2.943a.752.752 0 0 0-.686-1.063ZM9.96 1.505 8.613 4.449l-.036.094a.752.752 0 0 0 .722.97l2.423-.001-6.101 7.976L7.046 9.52l.028-.098a.752.752 0 0 0-.738-.907H3.828l2.544-7.011h3.589Z",
      clipRule: "evenodd"
    })
  });
};
// pure annotation needed for tree-shaking
var Insights = /* #__PURE__ */withIcon(IconInsights);

var IconJumpToQuestion = function IconJumpToQuestion() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm0 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zm4.286 7.465c.26.261.283.67.07.957l-.07.083-2.519 2.53-.082.07a.73.73 0 0 1-.86.008l-.092-.078-2.519-2.53-.07-.083a.738.738 0 0 1-.008-.865l.078-.092.082-.07a.73.73 0 0 1 .861-.008l.092.078 2 2.011 2.002-2.01.082-.072a.73.73 0 0 1 .953.071zm-5.1-5.57.081.07 2.519 2.53a.737.737 0 0 1 0 1.04.73.73 0 0 1-.953.07l-.082-.07L6.75 5.024l-2.001 2.01a.73.73 0 0 1-.953.072l-.082-.071a.738.738 0 0 1-.07-.957l.07-.083 2.519-2.53a.73.73 0 0 1 .952-.07z"
    })
  });
};
// pure annotation needed for tree-shaking
var JumpToQuestion = /* #__PURE__ */withIcon(IconJumpToQuestion);

var IconJumpToQuestionFilled = function IconJumpToQuestionFilled() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm3.333 8.894-.082.071-2.001 2.011-2.001-2.01a.73.73 0 0 0-.953-.072l-.082.071a.738.738 0 0 0-.07.957l.07.083 2.519 2.53a.73.73 0 0 0 .952.07l.082-.07 2.519-2.53a.737.737 0 0 0 0-1.04.73.73 0 0 0-.953-.07zm-4.148-5.5a.73.73 0 0 0-.952.071l-2.519 2.53-.07.083a.738.738 0 0 0 .07.957l.082.07a.73.73 0 0 0 .953-.07l2-2.011 2.002 2.01.082.072a.73.73 0 0 0 .953-.071.737.737 0 0 0 0-1.04l-2.519-2.53z"
    })
  });
};
// pure annotation needed for tree-shaking
var JumpToQuestionFilled = /* #__PURE__ */withIcon(IconJumpToQuestionFilled);

var IconKey = function IconKey() {
  return jsx("g", {
    children: jsx("path", {
      d: "M5.622 0c3.106 0 5.623 2.44 5.623 5.45a5.29 5.29 0 0 1-.567 2.386l4.836 4.807a.728.728 0 0 1 .19.328l.019.095.27 2.065a.777.777 0 0 1-.753.869l-.114-.006-2.078-.27a.74.74 0 0 1-.353-.144l-.072-.063-5.004-4.972a5.768 5.768 0 0 1-1.997.353C2.517 10.898 0 8.458 0 5.45 0 2.44 2.517 0 5.622 0zm3.404 9.787 4.504 4.477.83.107-.107-.826-4.464-4.437a5.594 5.594 0 0 1-.763.679zm-3.404-8.3c-2.258 0-4.089 1.773-4.089 3.962s1.831 3.963 4.09 3.963c2.258 0 4.088-1.774 4.088-3.963s-1.83-3.963-4.089-3.963zm-.51 1.485c1.128 0 2.044.887 2.044 1.982 0 1.094-.916 1.981-2.045 1.981s-2.044-.887-2.044-1.981c0-1.095.915-1.982 2.044-1.982zm0 1.486a.503.503 0 0 0-.512.496c0 .273.229.495.511.495a.503.503 0 0 0 .511-.495.503.503 0 0 0-.51-.496z"
    })
  });
};
// pure annotation needed for tree-shaking
var Key = /* #__PURE__ */withIcon(IconKey);

var IconKeyboard = function IconKeyboard() {
  return jsx("g", {
    children: jsx("path", {
      d: "m1.05 2 13.901.017c.541 0 .986.41 1.043.936l.006.114v9.883c0 .541-.41.987-.936 1.044L14.95 14H1.05a1.05 1.05 0 0 1-1.044-.936L0 12.95v-9.9c0-.54.41-.986.936-1.043L1.051 2zm.45 1.501V12.5h13V3.516l-13-.015zM11.25 9.5a.75.75 0 1 1 0 1.5h-6.5a.75.75 0 1 1 0-1.5h6.5zM8 7a.75.75 0 1 1 0 1.5A.75.75 0 0 1 8 7zm2.75 0a.75.75 0 1 1 0 1.5.75.75 0 0 1 0-1.5zm-5.5 0a.75.75 0 1 1 0 1.5.75.75 0 0 1 0-1.5zm1.5-2a.75.75 0 1 1 0 1.5.75.75 0 0 1 0-1.5zm-2.5 0a.75.75 0 1 1 0 1.5.75.75 0 0 1 0-1.5zm5 0a.75.75 0 1 1 0 1.5.75.75 0 0 1 0-1.5zm2.5 0a.75.75 0 1 1 0 1.5.75.75 0 0 1 0-1.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var Keyboard = /* #__PURE__ */withIcon(IconKeyboard);

var IconLaunchpad = function IconLaunchpad() {
  return jsx("g", {
    children: jsx("path", {
      d: "M3.333 12c.369 0 .667.299.667.667v2.666a.667.667 0 0 1-.667.667H.667A.667.667 0 0 1 0 15.333v-2.666A.667.667 0 0 1 .667 12h2.666zm6 0a.667.667 0 0 1 .667.667v2.666a.667.667 0 0 1-.667.667H6.667A.667.667 0 0 1 6 15.333v-2.666A.667.667 0 0 1 6.667 12h2.666zm6 0c.369 0 .667.299.667.667v2.666a.667.667 0 0 1-.667.667h-2.666a.667.667 0 0 1-.667-.667v-2.666a.667.667 0 0 1 .667-.667h2.666zm-12-6c.369 0 .667.299.667.667v2.666a.667.667 0 0 1-.667.667H.667A.667.667 0 0 1 0 9.333V6.667C0 6.299.298 6 .667 6h2.666zm6 0a.667.667 0 0 1 .667.667v2.666a.667.667 0 0 1-.667.667H6.667A.667.667 0 0 1 6 9.333V6.667C6 6.299 6.298 6 6.667 6h2.666zm6 0c.369 0 .667.299.667.667v2.666a.667.667 0 0 1-.667.667h-2.666A.667.667 0 0 1 12 9.333V6.667c0-.368.299-.667.667-.667h2.666zm-12-6C3.702 0 4 .298 4 .667v2.666A.667.667 0 0 1 3.333 4H.667A.667.667 0 0 1 0 3.333V.667C0 .298.298 0 .667 0h2.666zm6 0A.667.667 0 0 1 10 .667v2.666A.667.667 0 0 1 9.333 4H6.667A.667.667 0 0 1 6 3.333V.667C6 .298 6.298 0 6.667 0h2.666zm6 0c.369 0 .667.298.667.667v2.666a.667.667 0 0 1-.667.667h-2.666A.667.667 0 0 1 12 3.333V.667C12 .298 12.3 0 12.667 0h2.666z"
    })
  });
};
// pure annotation needed for tree-shaking
var Launchpad = /* #__PURE__ */withIcon(IconLaunchpad);

var IconLightbulb = function IconLightbulb() {
  return jsx("g", {
    children: jsx("g", {
      children: jsx("path", {
        d: "M8 1.5a4 4 0 0 0-3.633 5.675c.265.574.537.808 1.011 1.214.18.154.387.332.634.563a.75.75 0 0 1-1.024 1.096 13.166 13.166 0 0 0-.45-.396c-.549-.467-1.12-.954-1.533-1.85a5.5 5.5 0 1 1 9.99 0c-.444.963-1.001 1.427-1.623 1.944-.124.103-.252.209-.38.32a.75.75 0 0 1-.984-1.132c.172-.15.323-.276.459-.39.586-.492.872-.733 1.166-1.37A4.001 4.001 0 0 0 8 1.5ZM6.5 11a1 1 0 0 0-1 1v1.5a2.5 2.5 0 0 0 4.95.5h-2.7a.75.75 0 1 1 0-1.5h2.75V12a1 1 0 0 0-1-1h-3Z"
      })
    })
  });
};
// pure annotation needed for tree-shaking
var Lightbulb = /* #__PURE__ */withIcon(IconLightbulb);

var IconLighting = function IconLighting() {
  return jsx("g", {
    children: jsx("path", {
      d: "m11.6086 7.25h-5.4067l5.937-5.9365a.75.75 0 0 0 -1.0605-1.0606l-7.2173 7.2171a.75.75 0 0 0 .53 1.28h5.407l-5.937 5.9365a.75.75 0 1 0 1.0605 1.0605l7.2173-7.217a.75.75 0 0 0 -.53-1.28z"
    })
  });
};
// pure annotation needed for tree-shaking
var Lighting = /* #__PURE__ */withIcon(IconLighting);

var IconLink = function IconLink() {
  return jsx("g", {
    children: jsx("path", {
      d: "M9.43 1.1a3.843 3.843 0 0 1 5.388.004l.1.103a3.965 3.965 0 0 1 .037 5.404l-.14.145-2.69 2.656a3.843 3.843 0 0 1-5.387-.003l-.1-.104a.744.744 0 0 1 .04-1.06.767.767 0 0 1 1.073.041l.06.062a2.311 2.311 0 0 0 3.125.108l.114-.106 2.69-2.656c.922-.91.986-2.355.175-3.341l-.148-.166-.023-.022a2.311 2.311 0 0 0-3.124-.108l-.115.105-2.69 2.657a.767.767 0 0 1-1.074 0 .744.744 0 0 1-.069-.985l.068-.077L9.43 1.1zM3.874 6.588a3.843 3.843 0 0 1 5.388.003l.1.104a.744.744 0 0 1-.04 1.06.767.767 0 0 1-1.073-.041l-.06-.062a2.311 2.311 0 0 0-3.125-.108l-.114.106-2.69 2.656a2.477 2.477 0 0 0-.175 3.341l.148.166.023.022a2.311 2.311 0 0 0 3.124.108l.115-.105 2.69-2.657a.767.767 0 0 1 1.074 0c.272.27.295.691.069.985l-.068.077L6.57 14.9a3.843 3.843 0 0 1-5.388-.004l-.1-.103a3.965 3.965 0 0 1-.037-5.404l.14-.145 2.69-2.656z"
    })
  });
};
// pure annotation needed for tree-shaking
var Link = /* #__PURE__ */withIcon(IconLink);

var IconList = function IconList() {
  return jsx("g", {
    children: jsx("path", {
      d: "M15.254 13.5c.412 0 .746.334.746.746v.008a.746.746 0 0 1-.746.746H.746A.746.746 0 0 1 0 14.254v-.008c0-.412.334-.746.746-.746h14.508zm0-4.15c.412 0 .746.334.746.746v.008a.746.746 0 0 1-.746.746H.746A.746.746 0 0 1 0 10.104v-.008c0-.412.334-.746.746-.746h14.508zm0-4.2c.412 0 .746.334.746.746v.008a.746.746 0 0 1-.746.746H.746A.746.746 0 0 1 0 5.904v-.008c0-.412.334-.746.746-.746h14.508zm0-4.15c.412 0 .746.334.746.746v.008a.746.746 0 0 1-.746.746H.746A.746.746 0 0 1 0 1.754v-.008C0 1.334.334 1 .746 1h14.508z"
    })
  });
};
// pure annotation needed for tree-shaking
var List = /* #__PURE__ */withIcon(IconList);

var IconListBulleted = function IconListBulleted() {
  return jsx("g", {
    children: jsx("path", {
      d: "M1.25 12.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5zm14.004.5c.412 0 .746.334.746.746v.008a.746.746 0 0 1-.746.746H5.746A.746.746 0 0 1 5 13.754v-.008c0-.412.334-.746.746-.746h9.508zM1.25 6.75a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5zm14.004.5c.412 0 .746.334.746.746v.008a.746.746 0 0 1-.746.746H5.746A.746.746 0 0 1 5 8.004v-.008c0-.412.334-.746.746-.746h9.508zM1.25 1a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5zm14.004.5c.412 0 .746.334.746.746v.008a.746.746 0 0 1-.746.746H5.746A.746.746 0 0 1 5 2.254v-.008c0-.412.334-.746.746-.746h9.508z"
    })
  });
};
// pure annotation needed for tree-shaking
var ListBulleted = /* #__PURE__ */withIcon(IconListBulleted);

var IconListChecks = function IconListChecks() {
  return jsx("g", {
    children: jsx("path", {
      d: "M2.5 12a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zm12.75.75a.75.75 0 1 1 0 1.5h-7.5a.75.75 0 1 1 0-1.5h7.5zM5.766 6.708c.274.26.309.68.097.98l-.07.086-2.84 2.991a.752.752 0 0 1-1.042.047l-.077-.08-1.656-1.96A.755.755 0 0 1 .267 7.71a.752.752 0 0 1 .983.008l.078.08L2.44 9.116l2.262-2.381a.752.752 0 0 1 1.064-.027zm9.484.542a.75.75 0 1 1 0 1.5h-7.5a.75.75 0 0 1 0-1.5h7.5zM5.766 1.208c.274.26.309.68.097.98l-.07.086-2.84 2.991a.752.752 0 0 1-1.042.047l-.077-.08-1.656-1.96A.755.755 0 0 1 .267 2.21a.752.752 0 0 1 .983.008l.078.08L2.44 3.616l2.262-2.381a.752.752 0 0 1 1.064-.027zm9.484.542a.75.75 0 1 1 0 1.5h-7.5a.75.75 0 0 1 0-1.5h7.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var ListChecks = /* #__PURE__ */withIcon(IconListChecks);

var IconLocation = function IconLocation() {
  return jsx("g", {
    children: jsx("path", {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M12.75 6.15384C12.75 8.04925 11.5027 10.155 9.98003 11.954C9.26634 12.7973 8.54864 13.5076 8 14.014C7.45136 13.5076 6.73365 12.7973 6.01996 11.954C4.49734 10.155 3.25 8.04925 3.25 6.15384C3.25 4.92714 3.7447 3.74589 4.63299 2.87127C5.52206 1.99587 6.73278 1.5 8 1.5C9.26722 1.5 10.4779 1.99587 11.367 2.87127C12.2553 3.74589 12.75 4.92714 12.75 6.15384ZM14.25 6.15384C14.25 11.077 8 16 8 16C8 16 1.75 11.077 1.75 6.15384C1.75 4.52174 2.40848 2.95649 3.58058 1.80242C4.75269 0.64835 6.34239 0 8 0C9.6576 0 11.2473 0.64835 12.4194 1.80242C13.5915 2.95649 14.25 4.52174 14.25 6.15384ZM7.52423 4.84568C7.6753 4.78244 7.83694 4.75 8 4.75C8.16305 4.75 8.32469 4.78244 8.47577 4.84568C8.62686 4.90893 8.76467 5.00185 8.88111 5.11951C8.99756 5.2372 9.09035 5.37733 9.15379 5.53212C9.21698 5.68627 9.24972 5.85171 9.25 6.019C9.24942 6.10011 9.24281 6.18104 9.23024 6.26112C9.16476 6.54156 9.00875 6.79057 8.7885 6.96926C8.56386 7.1515 8.2856 7.25 7.99979 7.25C7.71399 7.25 7.43573 7.1515 7.21108 6.96926C6.99094 6.79066 6.83497 6.54178 6.76944 6.2615C6.75698 6.18142 6.75047 6.10049 6.75 6.01938C6.75023 5.85196 6.78298 5.68639 6.84621 5.53212C6.90965 5.37733 7.00244 5.2372 7.11889 5.11951C7.23533 5.00184 7.37314 4.90893 7.52423 4.84568ZM8 3.25C7.63776 3.25 7.27926 3.32211 6.94504 3.46201C6.61083 3.60191 6.30767 3.80675 6.05268 4.06444C5.79769 4.32211 5.59585 4.62759 5.45827 4.96323C5.32071 5.29886 5.25 5.65831 5.25 6.02113L5.25001 6.02468C5.25081 6.19339 5.26522 6.36177 5.2931 6.52815L5.29618 6.54651L5.30016 6.56471C5.43546 7.18223 5.77554 7.73617 6.26605 8.13412C6.75672 8.53219 7.3683 8.75 7.99979 8.75C8.63129 8.75 9.24287 8.53219 9.73354 8.13412C10.2241 7.73617 10.5641 7.18223 10.6994 6.56471L10.7033 6.54691L10.7064 6.52894C10.7344 6.36258 10.749 6.1942 10.75 6.02547H10.75V6.02113C10.75 5.65831 10.6793 5.29886 10.5417 4.96323C10.4042 4.62759 10.2023 4.32211 9.94732 4.06444C9.69233 3.80675 9.38917 3.60191 9.05496 3.46201C8.72073 3.32211 8.36223 3.25 8 3.25Z"
    })
  });
};
// pure annotation needed for tree-shaking
var Location = /* #__PURE__ */withIcon(IconLocation);

var IconLock = function IconLock() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8.002 0a4.01 4.01 0 0 1 3.993 3.811l.005.2v1.997l1.951.004c.541 0 .986.41 1.043.936l.006.114v7.887c0 .542-.41.988-.936 1.045L13.95 16H2.05a1.05 1.05 0 0 1-1.044-.936L1 14.949V7.046c0-.542.41-.988.936-1.045l.114-.006L4 5.997V3.993A3.993 3.993 0 0 1 7.792.005L8.002 0zM2.5 7.496V14.5h11V7.511l-11-.015zM8 8.984a.75.75 0 0 1 .743.649l.007.102v2.503a.75.75 0 0 1-1.493.102l-.007-.102V9.735a.75.75 0 0 1 .75-.75zm0-7.483A2.494 2.494 0 0 0 5.505 3.83l-.005.163V6l5 .007V4.011a2.51 2.51 0 0 0-2.336-2.504L8 1.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var Lock = /* #__PURE__ */withIcon(IconLock);

var IconLogoDrive = function IconLogoDrive() {
  return jsx("g", {
    children: jsx("path", {
      d: "M16 10.673 13.51 15H3.801l2.49-4.327H16zM4.792 2.1 7.34 6.538 2.509 14.92 0 10.6 4.792 2.1zm5.72-1.1 5.178 8.872h-5.007L5.505 1h5.007z"
    })
  });
};
// pure annotation needed for tree-shaking
var LogoDrive = /* #__PURE__ */withIcon(IconLogoDrive);

var IconLogoFacebook = function IconLogoFacebook() {
  return jsx("g", {
    children: jsx("path", {
      d: "M16,8c0-4.4-3.6-8-8-8S0,3.6,0,8c0,4,2.9,7.3,6.8,7.9v-5.6h-2V8h2V6.3c0-2,1.2-3.1,3-3.1 c0.9,0,1.8,0.2,1.8,0.2v2h-1c-1,0-1.3,0.6-1.3,1.2V8h2.2l-0.4,2.3H9.2V16C13.1,15.4,16,12,16,8z"
    })
  });
};
// pure annotation needed for tree-shaking
var LogoFacebook = /* #__PURE__ */withIcon(IconLogoFacebook);

var IconLogoGmail = function IconLogoGmail() {
  return jsx("g", {
    children: jsx("path", {
      d: "M14.398 2H1.602C.718 2.001.002 2.673 0 3.501V12.5c.003.828.719 1.499 1.602 1.501h12.796c.883-.002 1.6-.673 1.602-1.501V3.5c-.002-.828-.718-1.5-1.602-1.501zm0 10.5h-1.597V5.9L8 8.749 3.199 5.9v6.6H1.602V3.5h.957L8 6.651l5.441-3.15h.957V12.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var LogoGmail = /* #__PURE__ */withIcon(IconLogoGmail);

var IconLogoGoldie = function IconLogoGoldie() {
  return jsx("g", {
    children: jsx("path", {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M14.11 8.791c-.151.001-.303.02-.451.057a5.835 5.835 0 0 0-4.906-4.359 1.92 1.92 0 0 1 .852-1.765L9.476 2.4s-1.577.492-1.76 1.847c-.078-.369-.814-.83-1.178-.917l-.181.293c.326.198.546.532.6.911a5.835 5.835 0 0 0-4.611 4.314 1.887 1.887 0 0 0-2.057.832 1.903 1.903 0 0 0 .147 2.221c.522.63 1.388.854 2.148.553.161.403.367.788.614 1.146l1.568-1.061-.013-.018a3.889 3.889 0 0 1-.711-2.107 2.812 2.812 0 0 1 .713-2.23 2.096 2.096 0 0 1 3.143.402h.208a2.096 2.096 0 0 1 3.143-.402c.55.604.81 1.418.713 2.23a3.888 3.888 0 0 1-.71 2.107l-.014.018 1.568 1.061c.247-.358.453-.742.614-1.146a1.884 1.884 0 0 0 2.043-.442c.53-.546.684-1.358.389-2.06a1.89 1.89 0 0 0-1.742-1.16ZM1.859 11.248a.559.559 0 0 1-.558-.56.559.559 0 0 1 .87-.463c.005.294.025.588.06.88a.556.556 0 0 1-.372.143Zm12.29 0a.557.557 0 0 1-.372-.143c.034-.292.054-.586.059-.88a.554.554 0 0 1 .87.465.56.56 0 0 1-.558.559Z"
    })
  });
};
// pure annotation needed for tree-shaking
var LogoGoldie = /* #__PURE__ */withIcon(IconLogoGoldie);

var IconLogoGoogle = function IconLogoGoogle() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8.269.001a8.072 8.072 0 0 1 5.515 2.037l.153.14-1.564 1.579A5.876 5.876 0 0 0 8.35 2.184 5.959 5.959 0 0 0 4.14 3.89a5.656 5.656 0 0 0-1.692 3.947v.178a5.643 5.643 0 0 0 1.7 4.1 5.949 5.949 0 0 0 4.194 1.694c2.415 0 4.972-1.256 5.312-4.192l.017-.162H8.267V7.28h7.593c.1.475.146.959.136 1.443a6.946 6.946 0 0 1-2.163 5.265 7.333 7.333 0 0 1-5.445 2.003C3.802 16.03.049 12.444 0 7.976.084 3.575 3.75.04 8.269.001z"
    })
  });
};
// pure annotation needed for tree-shaking
var LogoGoogle = /* #__PURE__ */withIcon(IconLogoGoogle);

var IconLogoGooglePlus = function IconLogoGooglePlus() {
  return jsx("g", {
    children: jsx("path", {
      d: "M5.09 3a4.93 4.93 0 0 1 3.412 1.307L7.12 5.614a2.895 2.895 0 0 0-2.03-.771c-1.752.03-3.157 1.434-3.157 3.156 0 1.72 1.405 3.125 3.158 3.155 1.378.095 2.613-.828 2.887-2.156H5.091V7.284h4.807c.052.273.08.55.08.828a4.562 4.562 0 0 1-1.337 3.509 4.743 4.743 0 0 1-3.55 1.375C2.279 12.996 0 10.758 0 7.998 0 5.238 2.28 3 5.09 3zm9.455 2.857v1.427H16v1.428h-1.455v1.427h-1.454V8.712h-1.455V7.284h1.455V5.857h1.454z"
    })
  });
};
// pure annotation needed for tree-shaking
var LogoGooglePlus = /* #__PURE__ */withIcon(IconLogoGooglePlus);

var LogoInstagram = function LogoInstagram() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0C5.827 0 5.555.01 4.702.048 3.85.088 3.27.222 2.76.42a3.918 3.918 0 0 0-1.417.923c-.445.444-.72.89-.923 1.417-.198.51-.333 1.09-.372 1.942C.008 5.555 0 5.827 0 8s.01 2.445.048 3.298c.04.851.174 1.432.372 1.942.204.525.478.973.923 1.417.444.444.89.72 1.417.923.51.197 1.09.333 1.942.372.853.04 1.125.048 3.298.048s2.445-.01 3.298-.048c.851-.04 1.432-.175 1.942-.372a3.932 3.932 0 0 0 1.417-.923c.444-.444.72-.89.923-1.417.197-.51.333-1.09.372-1.942.04-.853.048-1.125.048-3.298s-.01-2.445-.048-3.298c-.04-.851-.175-1.433-.372-1.942a3.926 3.926 0 0 0-.923-1.417A3.897 3.897 0 0 0 13.24.42c-.51-.198-1.09-.333-1.942-.372C10.445.008 10.173 0 8 0Zm0 1.44c2.135 0 2.39.01 3.233.047.78.037 1.204.166 1.485.277.375.145.64.318.921.597.28.28.453.546.598.921.109.281.24.705.275 1.485.038.844.047 1.097.047 3.233s-.01 2.39-.05 3.233c-.04.78-.17 1.204-.28 1.485-.15.375-.32.64-.6.921-.279.28-.549.453-.92.598-.28.109-.71.24-1.49.275-.849.038-1.099.047-3.239.047-2.14 0-2.39-.01-3.24-.05-.78-.04-1.21-.17-1.49-.28-.38-.15-.64-.32-.92-.6-.28-.279-.46-.549-.6-.92-.11-.28-.239-.71-.28-1.49-.03-.84-.04-1.099-.04-3.229s.01-2.39.04-3.24c.041-.78.17-1.21.28-1.49.14-.38.32-.64.6-.92.28-.28.54-.46.92-.6.28-.11.7-.24 1.48-.28.85-.03 1.1-.04 3.24-.04l.03.02Zm0 2.452a4.108 4.108 0 1 0 0 8.215 4.108 4.108 0 0 0 0-8.215Zm0 6.775a2.666 2.666 0 1 1 0-5.334 2.666 2.666 0 1 1 0 5.334Zm5.23-6.937a.96.96 0 1 1-1.92-.001.96.96 0 0 1 1.92.001Z"
    })
  });
};
// pure annotation needed for tree-shaking
var LogoInstagram$1 = /* #__PURE__ */withIcon(LogoInstagram);

var IconLogoLinkedIn = function IconLogoLinkedIn() {
  return jsx("g", {
    children: jsx("path", {
      d: "M14.816 0H1.18A1.168 1.168 0 0 0 0 1.154v13.691A1.168 1.168 0 0 0 1.18 16h13.636A1.17 1.17 0 0 0 16 14.845V1.154A1.17 1.17 0 0 0 14.816 0zM4.746 13.633H2.37V5.998h2.376v7.635zM3.559 4.955a1.376 1.376 0 1 1-.002-2.752 1.376 1.376 0 0 1 .002 2.752zm10.076 8.679h-2.373V9.92c0-.886-.015-2.025-1.233-2.025-1.235 0-1.423.965-1.423 1.961v3.777H6.235V5.998H8.51v1.044h.032a2.493 2.493 0 0 1 2.246-1.233c2.402 0 2.846 1.581 2.846 3.637v4.188z"
    })
  });
};
// pure annotation needed for tree-shaking
var LogoLinkedIn = /* #__PURE__ */withIcon(IconLogoLinkedIn);

var IconX$1 = function IconX() {
  return jsx("g", {
    children: jsx("path", {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M5.042 1H0l5.978 7.613L.382 15h2.586L7.2 10.17l3.758 4.786H16L9.848 7.12l.011.013 5.297-6.045H12.57l-3.933 4.49L5.042 1ZM2.783 2.333h1.57l8.864 11.29h-1.57L2.783 2.332Z"
    })
  });
};
// pure annotation needed for tree-shaking
var LogoX = /* #__PURE__ */withIcon(IconX$1);

var IconLogoMailChimp = function IconLogoMailChimp() {
  return jsx("g", {
    children: jsx("path", {
      d: "M.498 8.641C-2.06 6.608 5.938-1.766 8.494.336c.013.01.87.802.872.804.003-.001.473-.19.476-.19 2.243-.875 4.063-.452 4.065.944 0 .726-.49 1.572-1.277 2.34.285.25.514.64.645 1.085.11.329.13.662.14.877.013.296.028.985.03.997l.316.083c.602.16 1.03.372 1.24.579.21.207.314.408.352.643.035.189.03.524-.235.899 0 0 .067.141.134.34.067.2.114.367.122.391.244 0 .626.264.626.9 0 .634-.28 1.352-.345 1.511-1.006 2.27-3.406 3.535-6.265 3.454-2.665-.074-4.938-1.399-5.933-3.559-.602 0-1.221-.248-1.693-.642-.497-.414-.803-.95-.863-1.51a2.45 2.45 0 0 1 .156-1.195zm11.54-3.74c-.113-.004-.169.015-.192.019-.215.033-.343.142-.502.267-.471.37-.869.43-1.311.412-.265-.01-.545-.05-.866-.067l-.188-.01c-.74-.036-1.536.566-1.668 1.423-.183 1.191.733 1.807.997 2.168.034.044.073.104.073.162 0 .07-.048.125-.094.17v.002l-.136.142c-.648.735-.844 1.814-.577 2.723.035.121.08.237.134.348.668 1.47 2.741 2.156 4.767 1.533.271-.084.528-.187.77-.306.454-.21.85-.499 1.178-.83.537-.53.857-1.105.98-1.813.062-.379-.027-.526-.149-.596-.129-.076-.283-.05-.283-.05s-.07-.453-.27-.865c-.59.44-1.35.747-1.929.904-.668.18-1.571.32-2.58.263-.56-.043-.93-.197-1.069.23 1.279.44 2.631.252 2.631.252a.047.047 0 0 1 .052.04.048.048 0 0 1-.03.047s-1.039.455-2.687-.025c.046.365.423.528.603.594.228.083.476.121.476.121 2.044.331 3.956-.77 4.387-1.047.032-.02.053 0 .027.036-.025.036-.026.038-.042.057-.527.64-1.943 1.381-3.786 1.381-.803 0-1.606-.266-1.901-.676-.457-.636-.023-1.564.74-1.468l.334.036c.953.1 2.332-.026 3.47-.523 1.04-.455 1.432-.956 1.373-1.36a.57.57 0 0 0-.18-.332c-.186-.172-.483-.306-.983-.439-.165-.043-.277-.072-.398-.109-.214-.066-.32-.12-.344-.5a82.886 82.886 0 0 1-.053-.986c-.02-.42-.073-.993-.45-1.23a.659.659 0 0 0-.324-.097zM3.25 8.324a1.655 1.655 0 0 0-.387.026c-.922.175-1.443.914-1.34 1.874.093.869 1.025 1.601 1.889 1.623.09.002.178-.004.265-.018.934-.15 1.179-1.106 1.024-2.045-.174-1.06-.935-1.434-1.451-1.46zm-.168.615.111.006c.27.032.494.186.632.433.183.329.02.673-.077.879l-.029.061c-.06.137-.064.256-.009.336a.25.25 0 0 0 .207.099c.042 0 .08-.008.113-.015.048-.01.097-.022.128.014.011.012.028.039.009.083-.035.075-.17.179-.365.171-.2-.015-.423-.151-.452-.492-.015-.169.052-.374.094-.48a.435.435 0 0 0-.18-.54.513.513 0 0 0-.372-.066.478.478 0 0 0-.304.198c-.058.084-.093.19-.112.247l-.012.034c-.042.107-.11.139-.155.133-.022-.003-.052-.016-.07-.065-.053-.134-.01-.514.258-.793a.841.841 0 0 1 .696-.237zm8.095-.534c.054-.103.218-.13.368-.061.149.07.226.208.173.31-.053.103-.218.13-.367.061-.15-.069-.228-.208-.174-.31zM7.862.62C6.569.29 3.777 2.105 1.994 4.502 1.272 5.472.24 7.19.734 8.073c.06.11.404.391.59.536.309-.423.815-.73 1.389-.834.73-1.848 1.95-3.55 3.564-4.722 1.199-.942 2.49-1.617 2.49-1.617S8.073.676 7.863.62zm4.45 7.304c.12.002.217.13.214.287-.002.157-.103.282-.224.28-.122-.002-.218-.13-.215-.287.003-.157.103-.282.224-.28zm-2.644.12c.303-.034.526.099.576.179.022.033.013.055.007.066-.025.036-.077.029-.188.017a1.483 1.483 0 0 0-.708.07s-.111.041-.16.041a.054.054 0 0 1-.037-.013.048.048 0 0 1-.017-.038c0-.042.041-.103.108-.158a.823.823 0 0 1 .42-.164zm-.964-.442a1.253 1.253 0 0 1 1.158-.132.981.981 0 0 1 .437.331c.04.063.049.11.022.136-.04.04-.144-.006-.313-.076a1.449 1.449 0 0 0-.654-.132c-.375.022-.7.176-.794.167-.039-.006-.056-.021-.061-.043-.015-.065.092-.174.205-.251zm3.266-1.245c.235-.036.35.195.434.575.057.256.046.491-.017.628a1.47 1.47 0 0 0-.351 0 1.245 1.245 0 0 1-.226-.51c-.085-.382-.077-.657.16-.693zM9.857 2.763c-.663.039-1.42.338-1.855.617-.021.014-.049-.004-.043-.028.034-.154.14-.356.196-.451.013-.022-.013-.047-.037-.035-.699.337-1.48.936-2.113 1.626-.022.025.011.06.038.04.547-.376 1.298-.725 2.281-.95 1.102-.254 2.162-.148 2.81-.008.032.007.053-.046.024-.061-.428-.226-1.085-.38-1.55-.383-.024 0-.037-.025-.023-.042.08-.102.19-.203.292-.276.023-.016.009-.05-.02-.049z"
    })
  });
};
// pure annotation needed for tree-shaking
var LogoMailChimp = /* #__PURE__ */withIcon(IconLogoMailChimp);

var IconLogoMessenger = function IconLogoMessenger() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 .003C3.703-.112.123 3.227 0 7.466a7.263 7.263 0 0 0 2.896 5.747V16l2.723-1.407A8.53 8.53 0 0 0 8 14.929c4.297.115 7.877-3.225 8-7.463-.123-4.239-3.703-7.578-8-7.463zm.833 10.07-2.1-2.214L2.679 10l4.344-4.57 2.1 2.07 4.053-2.142-4.343 4.714z"
    })
  });
};
// pure annotation needed for tree-shaking
var LogoMessenger = /* #__PURE__ */withIcon(IconLogoMessenger);

var IconLogoOffice = function IconLogoOffice() {
  return jsx("g", {
    children: jsx("path", {
      d: "M10.007 0 1 3.322v9.598l.004.001 3.223-1.284v-7.56l5.835-1.287v11.259l-9.047-1.123L10.007 16 15 14.46V1.607z"
    })
  });
};
// pure annotation needed for tree-shaking
var LogoOffice = /* #__PURE__ */withIcon(IconLogoOffice);

var IconLogoOutlook = function IconLogoOutlook() {
  return jsx("g", {
    children: jsx("path", {
      d: "M9.39 0v16L0 14.165V1.615L9.39 0zM16 5.205l-.004 5.499c.065 1.016-.712 1.124-.712 1.124h-5.31V7.619c.54.631 1.483.724 2.136.21L16 5.205zm-11.282-.18c-1.18 0-2.138 1.27-2.138 2.837 0 1.568.958 2.838 2.138 2.838 1.181 0 2.138-1.27 2.138-2.838 0-1.567-.957-2.838-2.138-2.838zm-.025 1.151c.568 0 1.028.753 1.028 1.682 0 .928-.46 1.681-1.028 1.681-.568 0-1.028-.753-1.028-1.681 0-.93.46-1.682 1.028-1.682zm10.634-2.928a.589.589 0 0 1 .63.64l-4.84 3.216-1.144-.728V3.248z"
    })
  });
};
// pure annotation needed for tree-shaking
var LogoOutlook = /* #__PURE__ */withIcon(IconLogoOutlook);

var IconLogoPinterest = function IconLogoPinterest() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 0 0-3.21 15.327 7.026 7.026 0 0 1 .138-1.837l1.03-4.359a3.073 3.073 0 0 1-.255-1.265c0-1.186.687-2.071 1.542-2.071a1.071 1.071 0 0 1 1.08 1.201 17.22 17.22 0 0 1-.708 2.838 1.238 1.238 0 0 0 1.263 1.541c1.516 0 2.536-1.947 2.536-4.253a2.995 2.995 0 0 0-3.328-3.066A3.788 3.788 0 0 0 4.15 7.888a2.31 2.31 0 0 0 .527 1.569.388.388 0 0 1 .115.446l-.163.642a.276.276 0 0 1-.4.2A3.081 3.081 0 0 1 2.59 7.688c0-2.272 1.917-4.997 5.717-4.997a4.78 4.78 0 0 1 5.064 4.582c0 3.138-1.744 5.483-4.316 5.483a2.294 2.294 0 0 1-1.954-.997s-.464 1.843-.563 2.199a6.697 6.697 0 0 1-.805 1.714A8 8 0 1 0 8 0z"
    })
  });
};
// pure annotation needed for tree-shaking
var LogoPinterest = /* #__PURE__ */withIcon(IconLogoPinterest);

var IconLogoWhatsApp = function IconLogoWhatsApp() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8.103 0a7.883 7.883 0 0 1 6.838 3.891 7.739 7.739 0 0 1 0 7.815 7.883 7.883 0 0 1-6.838 3.891 8.033 8.033 0 0 1-3.791-.963L0 16l1.376-4.153A8.934 8.934 0 0 1 .234 7.799C.234 3.492 3.757 0 8.103 0zm0 1.248c-3.65 0-6.61 2.933-6.61 6.55a7.403 7.403 0 0 0 1.288 3.887l-.806 2.385 2.485-.804a7.271 7.271 0 0 0 3.643 1.084c3.65 0 6.61-2.933 6.61-6.551 0-3.618-2.96-6.551-6.61-6.551zM6.049 4.27c.109.025.198.1.243.2.077.172.6 1.39.638 1.483a.51.51 0 0 1-.022.534c-.163.208-.503.597-.503.597a.239.239 0 0 0-.018.312c.294.478.652.916 1.064 1.3a5.08 5.08 0 0 0 1.523.935c.328.106.4-.036.525-.196l.167-.211.139-.174.21-.265a.31.31 0 0 1 .4-.076c.1.045.344.16.61.287l.267.128.678.324c.096.02.165.103.166.2.078.43-.051.872-.35 1.194-.52.478-1.23.7-1.931.6a7.592 7.592 0 0 1-3.041-1.47A9.905 9.905 0 0 1 4.64 7.317a2.549 2.549 0 0 1-.298-1.363 2.15 2.15 0 0 1 .763-1.551.927.927 0 0 1 .324-.134z"
    })
  });
};
// pure annotation needed for tree-shaking
var LogoWhatsApp = /* #__PURE__ */withIcon(IconLogoWhatsApp);

var IconLogoYahooMail = function IconLogoYahooMail() {
  return jsx("g", {
    children: jsx("path", {
      d: "M0 4.408h3.052l1.776 4.474 1.8-4.474H9.6L5.126 15h-2.99l1.224-2.807L0 4.408zm13.059 3.575H9.73L12.685 1H16l-2.941 6.983zm-2.456.669c1.02 0 1.848.814 1.848 1.819s-.827 1.82-1.848 1.82c-1.021 0-1.848-.815-1.848-1.82 0-1.005.827-1.82 1.848-1.82z"
    })
  });
};
// pure annotation needed for tree-shaking
var LogoYahooMail = /* #__PURE__ */withIcon(IconLogoYahooMail);

var IconLogoYouTube = function IconLogoYouTube() {
  return jsx("g", {
    children: jsx("path", {
      d: "M15.665 3.876c-.184-.737-.722-1.314-1.414-1.515A44.68 44.68 0 0 0 8 2.004a44.68 44.68 0 0 0-6.25.358c-.692.2-1.231.778-1.415 1.515C.105 5.237-.008 6.617 0 8a23.834 23.834 0 0 0 .335 4.123c.184.738.723 1.315 1.414 1.515A44.68 44.68 0 0 0 8 13.996a44.68 44.68 0 0 0 6.25-.358c.693-.2 1.231-.778 1.415-1.515.23-1.36.343-2.74.335-4.123a23.834 23.834 0 0 0-.335-4.124zM6.42 10.518V5.482L10.581 8l-4.16 2.518z"
    })
  });
};
// pure annotation needed for tree-shaking
var LogoYouTube = /* #__PURE__ */withIcon(IconLogoYouTube);

var IconMegaphone = function IconMegaphone() {
  return jsx("g", {
    children: jsx("path", {
      d: "M15.25 1a.75.75 0 0 1 .75.75v12.49a.75.75 0 0 1-1.5 0l-.001-1.091-3.76-.816v1.917a.75.75 0 0 1-.81.747l-.1-.014-4.995-1.085a.75.75 0 0 1-.584-.628l-.007-.104-.001-2.243-.909-.197a.75.75 0 0 1-.55-.49l-1.672.914a.75.75 0 0 1-1.104-.553L0 10.493V5.497a.75.75 0 0 1 1.016-.702l.095.044 1.671.915a.75.75 0 0 1 .551-.49l11.166-2.425V1.75a.75.75 0 0 1 .75-.75h.001zM5.743 11.248v1.313l3.496.759-.001-1.313-3.495-.759zm8.506-6.82L4.243 6.6v2.789l10.006 2.172V4.427zM1.499 6.76v2.469l1.242-.679V7.438L1.5 6.76z"
    })
  });
};
// pure annotation needed for tree-shaking
var Megaphone = /* #__PURE__ */withIcon(IconMegaphone);

var IconMenu = function IconMenu() {
  return jsx("g", {
    children: jsx("path", {
      d: "M15.254 12.5c.412 0 .746.334.746.746v.008a.746.746 0 0 1-.746.746H.746A.746.746 0 0 1 0 13.254v-.008c0-.412.334-.746.746-.746h14.508zm0-5.25c.412 0 .746.334.746.746v.008a.746.746 0 0 1-.746.746H.746A.746.746 0 0 1 0 8.004v-.008c0-.412.334-.746.746-.746h14.508zm0-5.25c.412 0 .746.334.746.746v.008a.746.746 0 0 1-.746.746H.746A.746.746 0 0 1 0 2.754v-.008C0 2.334.334 2 .746 2h14.508z"
    })
  });
};
// pure annotation needed for tree-shaking
var Menu = /* #__PURE__ */withIcon(IconMenu);

var IconMinimize = function IconMinimize() {
  return jsxs("g", {
    children: [jsx("path", {
      d: "M6.06441 6.99384C6.59049 6.93683 7 6.49124 7 5.95V0L5.5 0.000960827V5.5H0V7H1.5V6.98578L5.95 7L6.06441 6.99384Z"
    }), jsx("path", {
      d: "M6.06441 9.00616C6.59049 9.06317 7 9.50876 7 10.05V16L5.5 15.999V10.5H0V9H1.5V9.01422L5.95 9L6.06441 9.00616Z"
    }), jsx("path", {
      d: "M9 5.95C9 6.49124 9.40951 6.93683 9.93559 6.99384L10.05 7L14.5 6.98578V7H16V5.5H10.5V0.000960827L9 0V5.95Z"
    }), jsx("path", {
      d: "M9.93559 9.00616C9.40951 9.06317 9 9.50876 9 10.05V16L10.5 15.999V10.5H16V9H14.5V9.01422L10.05 9L9.93559 9.00616Z"
    })]
  });
};
// pure annotation needed for tree-shaking
var Minimize = /* #__PURE__ */withIcon(IconMinimize);

var IconMinus = function IconMinus() {
  return jsx("g", {
    children: jsx("path", {
      d: "M1.75 7.25h12.5a.75.75 0 1 1 0 1.5H1.75a.75.75 0 0 1 0-1.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var Minus = /* #__PURE__ */withIcon(IconMinus);

var IconMinusCircle = function IconMinusCircle() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm0 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zm3.75 5.75a.75.75 0 1 1 0 1.5h-7.5a.75.75 0 0 1 0-1.5h7.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var MinusCircle = /* #__PURE__ */withIcon(IconMinusCircle);

var IconMobile = function IconMobile() {
  return jsx("g", {
    children: jsx("path", {
      d: "m4.05 0 7.902.017A1.05 1.05 0 0 1 13 1.067V14.95c0 .58-.47 1.05-1.05 1.05h-7.9C3.47 16 3 15.53 3 14.95V1.05C3 .47 3.47 0 4.05 0zm7.45 12h-7v2.5h7V12zm-2.75.5a.75.75 0 1 1 0 1.5h-1.5a.75.75 0 1 1 0-1.5h1.5zM11.5 4h-7v6.5h7V4zM4.5 1.5V2.5h7v-.985l-7-.014z"
    })
  });
};
// pure annotation needed for tree-shaking
var Mobile = /* #__PURE__ */withIcon(IconMobile);

var IconMore = function IconMore() {
  return jsx("g", {
    children: jsx("path", {
      d: "M2 6a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm12 0a2 2 0 1 1 0 4 2 2 0 0 1 0-4zM8 6a2 2 0 1 1 0 4 2 2 0 0 1 0-4z"
    })
  });
};
// pure annotation needed for tree-shaking
var More = /* #__PURE__ */withIcon(IconMore);

var IconMoreAndroid = function IconMoreAndroid() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 12a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm0-6a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm0-6a2 2 0 1 1 0 4 2 2 0 0 1 0-4z"
    })
  });
};
// pure annotation needed for tree-shaking
var MoreAndroid = /* #__PURE__ */withIcon(IconMoreAndroid);

var IconMove = function IconMove() {
  return jsx("g", {
    children: jsx("path", {
      d: "M5 12a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm6 0a2 2 0 1 1 0 4 2 2 0 0 1 0-4zM5 6a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm6 0a2 2 0 1 1 0 4 2 2 0 0 1 0-4zM5 0a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm6 0a2 2 0 1 1 0 4 2 2 0 0 1 0-4z"
    })
  });
};
// pure annotation needed for tree-shaking
var Move = /* #__PURE__ */withIcon(IconMove);

var IconMoveToGroup = function IconMoveToGroup() {
  return jsx("g", {
    children: jsx("path", {
      d: "m8.304.008 6.649.016c.54.002.984.41 1.04.936l.007.114v6.63c0 .108-.016.211-.046.31.02.064.034.131.04.2l.006.107v6.63c0 .54-.41.986-.935 1.043L14.95 16H1.057a1.05 1.05 0 0 1-1.043-.935l-.006-.114V8.304c0-.54.409-.986.935-1.043l.114-.006 6.197.006V1.058c0-.541.41-.987.935-1.043l.115-.007zM1.507 8.754v5.747h5.747V8.76l-5.747-.007zm7.246.008V14.5H14.5V8.77l-5.748-.008zm0-7.254v5.746h5.748V1.522l-5.748-.014zM1.196.147 1.28.22l3.225 3.225V1.757a.75.75 0 1 1 1.5 0v3.748a.5.5 0 0 1-.41.492l-.09.008H1.757a.75.75 0 1 1 0-1.5h1.688L.22 1.28A.75.75 0 0 1 1.196.147z"
    })
  });
};
// pure annotation needed for tree-shaking
var MoveToGroup = /* #__PURE__ */withIcon(IconMoveToGroup);

var IconNest = function IconNest() {
  return jsx("g", {
    children: jsx("path", {
      d: "m1.05 0 13.901.015c.54 0 .986.41 1.043.936l.006.114V5.95c0 .541-.41.987-.936 1.044L14.95 7H4.483l-.001 4.75H9v-1.7c0-.54.41-.986.936-1.043l.114-.006 4.903.014c.54.001.984.41 1.04.935l.007.115v4.885c0 .541-.41.987-.936 1.044L14.95 16h-4.9a1.05 1.05 0 0 1-1.044-.936L9 14.95v-1.7H3.733a.75.75 0 0 1-.743-.648l-.007-.102V7H1.05a1.05 1.05 0 0 1-1.044-.936L0 5.95v-4.9C0 .51.41.063.936.006L1.051 0zm9.45 10.501V14.5h4v-3.987l-4-.012zM1.5 1.5v4h13V1.515L1.5 1.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var Nest = /* #__PURE__ */withIcon(IconNest);

var IconNews = function IconNews() {
  return jsx("g", {
    children: jsx("path", {
      d: "m4.55 1 10.402.017c.54.001.985.41 1.042.936l.006.114V12.25a2.75 2.75 0 0 1-2.582 2.745L13.25 15H3.499l-.001-.002L2.25 15A2.25 2.25 0 0 1 0 12.75V5.298a.75.75 0 0 1 .747-.75l2.751-.012V2.05c0-.54.41-.986.937-1.043L4.549 1zm.448 1.501V13.5h8.252a1.25 1.25 0 0 0 1.225-.998l.019-.124.006-.128V2.516l-9.502-.015zm-1.5 3.533L1.5 6.045v6.705l.007.102a.75.75 0 0 0 .743.648l1.248-.002V6.034zM12.254 10c.378 0 .69.28.74.645l.006.11a.746.746 0 0 1-.746.745H7.246a.746.746 0 0 1-.74-.645l-.006-.11c0-.411.334-.745.746-.745h5.008zm0-3c.378 0 .69.28.74.645l.006.11a.746.746 0 0 1-.746.745H7.246a.746.746 0 0 1-.74-.645l-.006-.11c0-.411.334-.745.746-.745h5.008zm0-3c.378 0 .69.28.74.645l.006.11a.746.746 0 0 1-.746.745H7.246a.746.746 0 0 1-.74-.645l-.006-.11c0-.411.334-.745.746-.745h5.008z"
    })
  });
};
// pure annotation needed for tree-shaking
var News = /* #__PURE__ */withIcon(IconNews);

var IconNoWifi = function IconNoWifi() {
  return jsx("g", {
    children: jsx("path", {
      d: "M6.26 10.216 8 11.956l1.74-1.74a.738.738 0 1 1 1.044 1.044L9.044 13l1.74 1.74a.738.738 0 1 1-1.044 1.044L8 14.044l-1.74 1.74a.738.738 0 1 1-1.044-1.044L6.956 13l-1.74-1.74a.738.738 0 1 1 1.044-1.044zm1.756-6.205a7.694 7.694 0 0 1 5.9 2.75.755.755 0 0 1-.088 1.06.745.745 0 0 1-1.054-.087 6.202 6.202 0 0 0-4.758-2.219c-1.78 0-3.438.754-4.612 2.053a.745.745 0 0 1-1.057.05.755.755 0 0 1-.05-1.062 7.69 7.69 0 0 1 5.72-2.545zm0-4.011c2.98 0 5.768 1.23 7.779 3.362a.755.755 0 0 1-.028 1.063.745.745 0 0 1-1.057-.028 9.17 9.17 0 0 0-6.694-2.893 9.31 9.31 0 0 0-6.728 2.87.745.745 0 0 1-1.057.025.755.755 0 0 1-.024-1.064C2.208 1.231 5.04 0 8.017 0z"
    })
  });
};
// pure annotation needed for tree-shaking
var NoWifi = /* #__PURE__ */withIcon(IconNoWifi);

var IconNumber1 = function IconNumber1() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm.994 11.902h-1.47V5.547l-1.765.436V4.677l3.236-.766v7.99z"
    })
  });
};
// pure annotation needed for tree-shaking
var Number1 = /* #__PURE__ */withIcon(IconNumber1);

var IconNumber10 = function IconNumber10() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm2.156 3.974c-1.913 0-2.96 1.689-2.96 3.895 0 2.195 1.047 3.895 2.96 3.895 1.914 0 2.961-1.7 2.961-3.895 0-2.207-1.047-3.895-2.96-3.895zm-4.31.012-3.096.731v1.25l1.688-.416v6.078h1.407V3.986zm4.31 1.17c1.103 0 1.565 1.125 1.565 2.713 0 1.587-.462 2.713-1.565 2.713-1.092 0-1.553-1.126-1.553-2.713 0-1.587.461-2.713 1.553-2.713z"
    })
  });
};
// pure annotation needed for tree-shaking
var Number10 = /* #__PURE__ */withIcon(IconNumber10);

var IconNumber2 = function IconNumber2() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm2.872 11.902H5.35v-1.083C7.599 9.1 9.2 7.994 9.2 6.463a1.218 1.218 0 0 0-1.271-1.377 1.171 1.171 0 0 0-1.213 1.247c.002.273.084.539.235.765l-1.224.495a2.145 2.145 0 0 1-.4-1.342 2.44 2.44 0 0 1 2.649-2.354 2.43 2.43 0 0 1 2.684 2.53c0 1.813-1.919 3.296-3.32 4.215h3.532v1.26z"
    })
  });
};
// pure annotation needed for tree-shaking
var Number2 = /* #__PURE__ */withIcon(IconNumber2);

var IconNumber3 = function IconNumber3() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm-.202 12.043a2.636 2.636 0 0 1-2.85-2.036l1.26-.483c.078.805.77 1.41 1.579 1.378A1.267 1.267 0 0 0 9.21 9.63c0-.777-.588-1.2-1.47-1.2h-.802V7.38h.743c.918 0 1.318-.482 1.318-1.236a1.086 1.086 0 0 0-1.19-1.072 1.384 1.384 0 0 0-1.388 1.284l-1.237-.495a2.582 2.582 0 0 1 2.673-1.965c1.554 0 2.543.812 2.543 2.13a1.727 1.727 0 0 1-1.495 1.825v.024a1.81 1.81 0 0 1 1.706 1.836c0 1.46-1.236 2.33-2.813 2.33z"
    })
  });
};
// pure annotation needed for tree-shaking
var Number3 = /* #__PURE__ */withIcon(IconNumber3);

var IconNumber4 = function IconNumber4() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm1.518 4.037H8.094L4.598 8.57V9.7h3.507v2.202h1.413V9.7h1.507V8.63H9.518V4.036zm-1.412 1.65v2.942H5.869v-.047l2.19-2.896h.047z"
    })
  });
};
// pure annotation needed for tree-shaking
var Number4 = /* #__PURE__ */withIcon(IconNumber4);

var IconNumber5 = function IconNumber5() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm-.03 12.043A2.674 2.674 0 0 1 5.12 9.959l1.283-.458A1.54 1.54 0 0 0 7.97 10.89a1.375 1.375 0 0 0 1.436-1.531 1.297 1.297 0 0 0-1.33-1.436 1.4 1.4 0 0 0-1.084.458H5.804l.211-4.343h4.392v1.236h-3.25l-.105 2.072c.394-.326.889-.505 1.4-.506a2.325 2.325 0 0 1 2.379 2.496 2.659 2.659 0 0 1-2.861 2.707z"
    })
  });
};
// pure annotation needed for tree-shaking
var Number5 = /* #__PURE__ */withIcon(IconNumber5);

var IconNumber6 = function IconNumber6() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm.527 3.89-.183.007c-1.997 0-3.263 1.646-3.34 4.039L5 8.17a4.675 4.675 0 0 0 .695 2.626 2.722 2.722 0 0 0 2.39 1.247 2.56 2.56 0 0 0 2.719-2.66 2.36 2.36 0 0 0-2.278-2.51l-.182.001a2.038 2.038 0 0 0-1.89 1.149l-.065.147h-.035v-.2c0-1.66.707-2.908 1.978-2.908a1.452 1.452 0 0 1 1.364.873l.049.127 1.035-.682a2.456 2.456 0 0 0-2.253-1.49zm-.501 4.104a1.368 1.368 0 0 1 1.437 1.303l-.001.145a1.369 1.369 0 0 1-1.436 1.448 1.36 1.36 0 0 1-1.401-1.448 1.36 1.36 0 0 1 1.4-1.448z"
    })
  });
};
// pure annotation needed for tree-shaking
var Number6 = /* #__PURE__ */withIcon(IconNumber6);

var IconNumber7 = function IconNumber7() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm2.953 5.12-2.755 6.782H6.667l2.755-6.651H6.608v1.258H5.337V4.038h5.616V5.12z"
    })
  });
};
// pure annotation needed for tree-shaking
var Number7 = /* #__PURE__ */withIcon(IconNumber7);

var IconNumber8 = function IconNumber8() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm-.05 3.897c-1.612 0-2.648.882-2.648 2.13a1.823 1.823 0 0 0 1.17 1.775l.149.05v.024c-.907.196-1.56.99-1.578 1.918 0 1.378 1.154 2.249 2.908 2.249 1.766 0 2.92-.871 2.92-2.249a2.003 2.003 0 0 0-1.42-1.877l-.158-.041v-.024a1.823 1.823 0 0 0 1.319-1.825c0-1.248-1.036-2.13-2.661-2.13zm0 4.52c.884 0 1.473.459 1.473 1.271 0 .813-.589 1.26-1.472 1.26-.872 0-1.46-.448-1.46-1.26s.588-1.27 1.46-1.27zm0-3.46a1.13 1.13 0 0 1 1.283 1.05l.001.126a1.145 1.145 0 0 1-1.283 1.19 1.136 1.136 0 0 1-1.27-1.065v-.125a1.121 1.121 0 0 1 1.27-1.177z"
    })
  });
};
// pure annotation needed for tree-shaking
var Number8 = /* #__PURE__ */withIcon(IconNumber8);

var IconNumber9 = function IconNumber9() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zM7.94 3.897a2.562 2.562 0 0 0-2.72 2.66 2.36 2.36 0 0 0 2.279 2.51l.182-.002a2.036 2.036 0 0 0 1.89-1.148l.064-.147h.036v.2c0 1.66-.707 2.908-1.979 2.908a1.45 1.45 0 0 1-1.364-.873l-.048-.128-1.036.683a2.457 2.457 0 0 0 2.253 1.49l.184-.007c1.995 0 3.262-1.646 3.339-4.04l.004-.233a4.664 4.664 0 0 0-.695-2.625 2.72 2.72 0 0 0-2.39-1.248zm.059 1.153a1.36 1.36 0 0 1 1.4 1.448 1.36 1.36 0 0 1-1.4 1.449A1.37 1.37 0 0 1 6.56 6.643v-.145A1.37 1.37 0 0 1 7.999 5.05z"
    })
  });
};
// pure annotation needed for tree-shaking
var Number9 = /* #__PURE__ */withIcon(IconNumber9);

var IconOpenMinimizeLeft = function IconOpenMinimizeLeft() {
  return jsx("g", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    children: jsx("path", {
      d: "M0 13.25a.75.75 0 0 1 .746-.75h8.508a.75.75 0 0 1 0 1.5H.746A.75.75 0 0 1 0 13.25ZM0 2.75A.75.75 0 0 1 .746 2h8.508a.75.75 0 0 1 0 1.5H.746A.75.75 0 0 1 0 2.75ZM0 8a.75.75 0 0 1 .746-.75h4.508a.75.75 0 0 1 0 1.5H.746A.75.75 0 0 1 0 8ZM15.772 13.708a.75.75 0 0 1-.956 1.151l-.085-.07-6.499-6.25a.75.75 0 0 1-.076-.995l.076-.085 6.499-6.248a.751.751 0 0 1 1.115.999l-.074.082L9.836 8l5.936 5.709Z"
    })
  });
};
// pure annotation needed for tree-shaking
var OpenMinimizeLeft = /* #__PURE__ */withIcon(IconOpenMinimizeLeft);

var OpenMinimizeRight = function OpenMinimizeRight() {
  return jsx("g", {
    children: jsx("path", {
      fillRule: "evenodd",
      d: "M6 13.25a.75.75 0 0 1 .746-.75h8.508a.75.75 0 0 1 0 1.5H6.746A.75.75 0 0 1 6 13.25ZM6 2.75A.75.75 0 0 1 6.746 2h8.508a.75.75 0 0 1 0 1.5H6.746A.75.75 0 0 1 6 2.75ZM10 8a.75.75 0 0 1 .746-.75h4.508a.75.75 0 0 1 0 1.5h-4.508A.75.75 0 0 1 10 8ZM.23 13.708a.75.75 0 0 0 .956 1.151l.085-.07 6.499-6.25a.75.75 0 0 0 .076-.995l-.076-.085L1.27 1.21a.751.751 0 0 0-1.115.999l.074.082L6.166 8 .23 13.708Z",
      clipRule: "evenodd"
    })
  });
};
// pure annotation needed for tree-shaking
var OpenMinimizeRight$1 = /* #__PURE__ */withIcon(OpenMinimizeRight);

var IconParagraph = function IconParagraph() {
  return jsx("g", {
    children: jsx("path", {
      d: "M11.75 0c.05 0 .098.005.145.014l2.354.005a.75.75 0 0 1 .1 1.493l-.102.007-1.747-.004V15.25a.75.75 0 1 1-1.5 0V8.994H8.5v6.256a.75.75 0 1 1-1.5 0V8.994H5.497A4.497 4.497 0 0 1 1 4.497 4.492 4.492 0 0 1 5.28.011l.218-.005 2.139.002a.756.756 0 0 1 .227 0l3.743.006A.754.754 0 0 1 11.75 0zM11 1.513l-2.5-.004v5.985H11V1.513zm-5.504-.007A2.992 2.992 0 0 0 2.5 4.497c0 1.596 1.247 2.901 2.82 2.992l.177.005H7V1.507l-1.504-.001z"
    })
  });
};
// pure annotation needed for tree-shaking
var Paragraph = /* #__PURE__ */withIcon(IconParagraph);

var IconPencilBox = function IconPencilBox() {
  return jsx("g", {
    children: jsx("path", {
      d: "m1.052 3.004 6.199.01a.75.75 0 0 1 .1 1.493l-.103.007L1.5 4.505V14.5h10.004V8.758a.75.75 0 0 1 .649-.743l.102-.007a.75.75 0 0 1 .743.648l.007.102v6.192c0 .541-.41.987-.936 1.044l-.114.006H1.05a1.05 1.05 0 0 1-1.044-.936L0 14.95V4.054c0-.54.41-.986.936-1.043l.116-.007zM13.656.22l2.124 2.124a.751.751 0 0 1 0 1.062l-6.27 6.27a.751.751 0 0 1-.424.212l-2.479.354a.751.751 0 0 1-.85-.85l.355-2.478a.751.751 0 0 1 .212-.424l6.27-6.27a.751.751 0 0 1 1.062 0zM11.31 3.626 7.563 7.374l-.177 1.24 1.239-.178 3.747-3.748-1.062-1.062zm1.814-1.813-.752.751 1.062 1.062.752-.75-1.062-1.063z"
    })
  });
};
// pure annotation needed for tree-shaking
var PencilBox = /* #__PURE__ */withIcon(IconPencilBox);

var IconPencilPaper = function IconPencilPaper() {
  return jsx("g", {
    children: jsx("path", {
      d: "M14.25 14.5a.75.75 0 1 1 0 1.5H1.75a.75.75 0 1 1 0-1.5h12.5zM9.981.223l4.296 4.296a.76.76 0 0 1 0 1.073l-7.685 7.686a.759.759 0 0 1-.536.222l-4.295-.001a.76.76 0 0 1-.76-.76L1 8.444c0-.201.08-.394.222-.536L8.908.222a.76.76 0 0 1 1.073 0zM2.537 8.74l-.019.02.001 3.22h3.222l.018-.018L2.537 8.74zm4.778-4.778L3.61 7.666l3.222 3.222 3.704-3.704-3.222-3.222zm2.13-2.129L8.388 2.888l3.222 3.222 1.056-1.055-3.223-3.222z"
    })
  });
};
// pure annotation needed for tree-shaking
var PencilPaper = /* #__PURE__ */withIcon(IconPencilPaper);

var IconPhone = function IconPhone() {
  return jsx("g", {
    children: jsx("path", {
      d: "M4.112.001c.327-.014.647.103.888.325.16.15.295.324.4.516l1.48 2.589a1.79 1.79 0 0 1-.289 2.154l-.822.823.31.419c.105.138.213.275.323.41l.335.399.639.71c.688.689 1.43 1.32 2.22 1.891l.822-.823a1.79 1.79 0 0 1 2.154-.289l2.586 1.48c.192.105.367.24.516.4.222.242.338.561.324.889-.019.403-.166.867-.354 1.3a7.81 7.81 0 0 1-.774 1.367c-1.149 1.63-3.23 1.712-4.802 1.048-2.476-1.046-4.455-2.862-5.441-3.766a18.444 18.444 0 0 0-.37-.336l-.018-.016L4 11.252l-.011-.013-.302-.327C2.681 9.822 1.33 8.359.364 5.93c-.626-1.574-.545-3.65 1.082-4.799A7.865 7.865 0 0 1 2.812.356c.433-.188.896-.335 1.3-.355Zm-.054 1.516-.001.001c-.222.049-.438.12-.645.215-.386.171-.755.38-1.1.624-.836.59-1.042 1.791-.554 3.02.859 2.16 2.036 3.437 3.035 4.52l.293.32.18.18c.115.101.249.224.4.361 1.008.921 2.774 2.535 4.986 3.469 1.205.509 2.404.305 2.993-.53.255-.362.47-.75.623-1.101.12-.274.186-.494.215-.646a1.256 1.256 0 0 0-.069-.042l-2.587-1.479a.29.29 0 0 0-.348.047l-1.672 1.673-.502-.301-.001-.001-.002-.001-.005-.003-.014-.009a14.45 14.45 0 0 1-.887-.615A19.755 19.755 0 0 1 6.3 9.394l-.014-.014-.674-.75-.005-.005c-.38-.437-.738-.892-1.074-1.364-.12-.169-.235-.34-.346-.515l-.02-.032-.005-.01c-.002-.003-.002-.003.003-.006L4.16 6.7l-.301-.503L5.53 4.524a.29.29 0 0 0 .046-.349L4.1 1.587a1.328 1.328 0 0 0-.042-.07Z"
    })
  });
};
// pure annotation needed for tree-shaking
var Phone = /* #__PURE__ */withIcon(IconPhone);

var IconPin = function IconPin() {
  return jsx("g", {
    children: jsx("path", {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M14.6324 4.99956C15.1225 5.48973 15.1225 6.28446 14.6324 6.77462C14.529 6.87796 14.4087 6.96219 14.277 7.02384L14.1417 7.07784L12.741 7.54475L12.6838 7.57148L12.6344 7.61055L10.5381 9.7066L10.5797 9.84399C10.729 10.3987 10.8014 10.9715 10.7955 11.5456L10.7825 11.8901L10.7506 12.2341C10.7186 12.4997 10.5879 12.8383 10.3495 13.2904L10.2408 13.4912L10.0143 13.8847L9.74482 14.3284C9.41147 14.8666 8.70486 15.0325 8.16678 14.699L8.05873 14.6231L7.96013 14.5353L5.29105 11.8655L2.39744 14.7602C2.07775 15.0799 1.55945 15.0799 1.23976 14.7602C0.946719 14.4672 0.922299 14.0073 1.1665 13.6864L1.23976 13.6026L4.13337 10.7079L1.47046 8.04576C1.0527 7.62801 1.02477 6.96805 1.38681 6.51801L1.47036 6.42496L1.57753 6.3304L1.69562 6.24991L2.17215 5.97324C2.99707 5.50693 3.56028 5.26462 3.9653 5.22887C4.60996 5.17199 5.25868 5.21472 5.88762 5.35461L6.20034 5.43264L6.29483 5.46035L8.41057 3.3423L8.42868 3.31646L8.45543 3.2593L8.92235 1.85858C9.14157 1.20095 9.85241 0.845541 10.5101 1.06475C10.6487 1.11097 10.7778 1.18091 10.8919 1.27113L11.0007 1.36797L14.6324 4.99956ZM13 5.76548L10.2302 3L9.95733 3.818C9.90082 3.98727 9.8211 4.14726 9.72088 4.29358L9.61393 4.43517L9.49393 4.56667L6.64876 7.40809L6.14773 7.21201C5.47631 6.94925 4.75082 6.84623 4.03187 6.90958L4.00656 6.91369L3.9339 6.93515L3.72213 7.02226L3.41863 7.16938L3 7.39242L8.61628 13L8.8304 12.6194L8.94271 12.4046L9.02036 12.2417L9.06248 12.1346L9.06992 12.1032C9.15062 11.434 9.08561 10.7547 8.8797 10.1151L8.78285 9.84362L8.58669 9.34361L11.4319 6.50172C11.6003 6.3335 11.7983 6.19846 12.0155 6.10302L12.1818 6.03897L13 5.76548Z"
    })
  });
};
// pure annotation needed for tree-shaking
var Pin = /* #__PURE__ */withIcon(IconPin);

var IconPlus = function IconPlus() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 1a.75.75 0 0 1 .75.75v5.5h5.5a.75.75 0 1 1 0 1.5h-5.5v5.5a.75.75 0 1 1-1.5 0v-5.5h-5.5a.75.75 0 0 1 0-1.5h5.5v-5.5A.75.75 0 0 1 8 1z"
    })
  });
};
// pure annotation needed for tree-shaking
var Plus = /* #__PURE__ */withIcon(IconPlus);

var IconPlusCircle = function IconPlusCircle() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm0 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zm0 2a.75.75 0 0 1 .75.75v3h3a.75.75 0 1 1 0 1.5h-3v3a.75.75 0 1 1-1.5 0v-3h-3a.75.75 0 0 1 0-1.5h3v-3A.75.75 0 0 1 8 3.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var PlusCircle = /* #__PURE__ */withIcon(IconPlusCircle);

var IconPrinter = function IconPrinter() {
  return jsx("g", {
    children: jsx("path", {
      d: "m3.55 0 8.902.018c.54.001.985.41 1.042.936l.006.114v2.948l1.451.003c.541 0 .986.41 1.043.935L16 5.07v6.381c0 .541-.41.987-.936 1.044l-.114.006-1.45-.001v2.451c0 .541-.41.987-.936 1.044L12.45 16h-8.9a1.05 1.05 0 0 1-1.044-.936L2.5 14.95v-2.451l-1.45.001a1.05 1.05 0 0 1-1.044-.936L0 11.45v-6.4c0-.54.41-.986.936-1.043L1.051 4l1.449.002V1.05c0-.542.41-.987.936-1.044L3.55 0zM4 10.002V14.5h8v-4.483l-8-.016zm-2.5-4.5V11h1V9.55c0-.54.41-.986.936-1.043L3.55 8.5l8.902.017c.54.001.985.41 1.042.936l.006.114V11h1V5.517l-13-.016zM3 6.25a.75.75 0 1 1 0 1.5.75.75 0 0 1 0-1.5zm1-4.749v2.503l8 .01V1.517l-8-.016z"
    })
  });
};
// pure annotation needed for tree-shaking
var Printer = /* #__PURE__ */withIcon(IconPrinter);

var IconPulse = function IconPulse() {
  return jsx("g", {
    children: jsx("path", {
      d: "M5.956 1.548C6.154.82 7.157.82 7.387 1.49l.03.112 1.792 9.197 1.695-6.732c.163-.647 1.008-.762 1.353-.234l.055.1 1.666 3.57h1.271a.75.75 0 0 1 .744.649l.007.102a.75.75 0 0 1-.649.743l-.102.007h-1.748a.75.75 0 0 1-.628-.339l-.052-.094-.976-2.092-2 7.95c-.185.738-1.202.748-1.434.073l-.03-.113-1.823-9.346-1.612 5.907a.751.751 0 0 1-1.262.327l-.07-.083-1.593-2.191H.751a.75.75 0 0 1-.744-.648L0 8.254a.75.75 0 0 1 .649-.744l.102-.006h1.652c.206 0 .401.084.542.23l.065.079.899 1.234 2.047-7.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var Pulse = /* #__PURE__ */withIcon(IconPulse);

var IconQRCode = function IconQRCode() {
  return jsxs("g", {
    children: [jsx("path", {
      d: "m1.0506 9 4.9028 0.01567c0.54001 0.00173 0.98374 0.41088 1.0405 0.93584l0.00613 0.11419v4.8833c0 0.5413-0.40951 0.9869-0.93559 1.0439l-0.11441 0.0061h-4.9c-0.54124 0-0.98683-0.4095-1.0438-0.9356l-0.0061613-0.1144v-4.899c0-0.54124 0.40951-0.98683 0.93608-1.0438l0.11452-0.00616zm0.4494 1.5v3.999h4v-3.985l-4-0.014zm-0.45-10.5 4.9034 0.015672c0.54001 0.0017266 0.98374 0.41088 1.0405 0.93584l0.00613 0.11416v4.8834c0 0.54124-0.40951 0.98683-0.93559 1.0438l-0.11441 0.00616h-4.9c-0.54124 0-0.98683-0.40951-1.0438-0.93559l-0.0061613-0.11441v-4.899c0-0.54124 0.40951-0.98683 0.93559-1.0438l0.11441-0.0061613zm9 0 4.9034 0.015672c0.54 0.0017266 0.9837 0.41088 1.0405 0.93584l0.0061 0.11416v4.8834c0 0.54124-0.4095 0.98683-0.9356 1.0438l-0.1144 0.00616h-4.9c-0.54124 0-0.98683-0.40951-1.0438-0.93559l-0.00616-0.11441v-4.899c0-0.54124 0.40951-0.98683 0.93559-1.0438l0.11441-0.0061613zm-8.55 1.5v3.999h4v-3.985l-4-0.014zm9 0v3.999h4v-3.985l-4-0.014z"
    }), jsx("path", {
      d: "m9.5018 9.0018c-0.27682-9.7e-4 -0.50175 0.22317-0.50175 0.49999v0.99825c0 0.2761 0.22386 0.5 0.5 0.5h1c0.2761 0 0.5-0.2239 0.5-0.5v-0.99474c0-0.27546-0.2228-0.49904-0.4982-0.5l-1-0.0035zm2.5 2.5c-0.2769-1e-3 -0.5018 0.2231-0.5018 0.5v0.9982c0 0.2761 0.2239 0.5 0.5 0.5h1c0.2761 0 0.5-0.2239 0.5-0.5v-0.9947c0-0.2755-0.2228-0.4991-0.4982-0.5l-1-0.0035zm-3.0018 3c0-0.2769 0.22493-0.501 0.50175-0.5l1 0.0035c0.2754 9e-4 0.4982 0.2245 0.4982 0.5v0.9947c0 0.2761-0.2239 0.5-0.5 0.5h-1c-0.27614 0-0.5-0.2239-0.5-0.5v-0.9982zm5.5018-5.5c-0.2769-9.7e-4 -0.5018 0.22317-0.5018 0.49999v0.99825c0 0.2761 0.2239 0.5 0.5 0.5h1c0.2761 0 0.5-0.2239 0.5-0.5v-0.99474c0-0.27546-0.2228-0.49904-0.4982-0.5l-1-0.0035zm-0.5018 5.5c0-0.2769 0.2249-0.501 0.5018-0.5l1 0.0035c0.2754 9e-4 0.4982 0.2245 0.4982 0.5v0.9947c0 0.2761-0.2239 0.5-0.5 0.5h-1c-0.2761 0-0.5-0.2239-0.5-0.5v-0.9982z"
    })]
  });
};
// pure annotation needed for tree-shaking
var QRCode = /* #__PURE__ */withIcon(IconQRCode);

var IconQuestionBank = function IconQuestionBank() {
  return jsx("g", {
    children: jsx("path", {
      d: "M11 1a5 5 0 0 1 5 5v8a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1V6a5 5 0 0 1 5-5h6ZM4 8.5H1.5v5h13v-5H12v1.75a.75.75 0 0 1-1.5 0V8.5h-5v1.75a.75.75 0 0 1-1.5 0V8.5Zm7-6H5a3.5 3.5 0 0 0-3.495 3.308L1.5 6v1h13V6a3.5 3.5 0 0 0-3.308-3.495L11 2.5Z"
    })
  });
};
// pure annotation needed for tree-shaking
var QuestionBank = /* #__PURE__ */withIcon(IconQuestionBank);

var IconScatterPlot = function IconScatterPlot() {
  return jsx("g", {
    children: jsx("path", {
      d: "M.75 0a.75.75 0 0 1 .743.649L1.5.75v13.748h13.752a.75.75 0 0 1 .743.648l.007.102a.75.75 0 0 1-.649.744l-.101.007H1.05a1.05 1.05 0 0 1-1.044-.936L0 14.949V.751A.75.75 0 0 1 .75 0zm3.5 10a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5zm7-1a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5zm-4-3a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5zm2-4a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5zm5.5-2a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var ScatterPlot = /* #__PURE__ */withIcon(IconScatterPlot);

var IconScreenReader = function IconScreenReader() {
  return jsxs("g", {
    children: [jsx("path", {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M11.349.095A9.031 9.031 0 0 1 16 7.999a9.031 9.031 0 0 1-4.656 7.907.752.752 0 1 1-.73-1.32A7.524 7.524 0 0 0 14.495 8a7.524 7.524 0 0 0-3.876-6.586.754.754 0 0 1 .73-1.318ZM9.6 2.723a5.893 5.893 0 0 1 3.262 5.276 5.893 5.893 0 0 1-3.276 5.283.752.752 0 1 1-.668-1.35 4.385 4.385 0 0 0 2.439-3.933c0-1.68-.953-3.19-2.429-3.927a.754.754 0 0 1 .672-1.35Z"
    }), jsx("path", {
      d: "m4.202 9.563-1.08 3.032a.608.608 0 0 1-.31.347.594.594 0 0 1-.664-.103.615.615 0 0 1-.159-.662l.752-2.119c.06-.16.107-.323.141-.49.03-.17.053-.341.066-.513.029-.31.049-.646.065-.969.015-.322.026-.635.035-.894.01-.322-.075-.343-.33-.404l-.053-.013-2.17-.415a.597.597 0 0 1-.39-.253.62.62 0 0 1 .261-.91.594.594 0 0 1 .348-.039l2.332.445.279.03c.287.04.576.063.866.066.36-.007.72-.035 1.078-.082.108-.013.21-.026.313-.036l2.2-.42a.595.595 0 0 1 .453.088.608.608 0 0 1 .255.396.623.623 0 0 1-.267.63.597.597 0 0 1-.218.088l-2.103.403c-.07.016-.133.027-.188.035-.22.038-.328.058-.315.375.01.232.037.51.074.799.042.34.097.7.155 1.03.037.217.072.39.12.558.049.166.096.337.168.542l.737 2.072a.623.623 0 0 1-.16.662.599.599 0 0 1-.973-.244L4.437 9.569l-.12-.225-.121.219h.006ZM4.249 3c.246 0 .485.086.675.245a1.099 1.099 0 0 1 .265 1.347 1.071 1.071 0 0 1-.531.49c-.228.097-.481.11-.717.037a1.066 1.066 0 0 1-.576-.434 1.097 1.097 0 0 1 .131-1.367A1.059 1.059 0 0 1 4.25 3Z"
    })]
  });
};
// pure annotation needed for tree-shaking
var ScreenReader = /* #__PURE__ */withIcon(IconScreenReader);

var IconSearch = function IconSearch() {
  return jsx("g", {
    children: jsx("path", {
      d: "M6.5 0C10.09 0 13 2.912 13 6.503a6.477 6.477 0 0 1-1.404 4.037l4.183 4.18a.75.75 0 0 1-1.06 1.061l-4.183-4.18A6.502 6.502 0 0 1 0 6.503 6.502 6.502 0 0 1 6.5 0zm0 1.5c-2.761 0-5 2.24-5 5.003a5.001 5.001 0 1 0 10 0 5.001 5.001 0 0 0-5-5.002z"
    })
  });
};
// pure annotation needed for tree-shaking
var Search = /* #__PURE__ */withIcon(IconSearch);

var IconShapes = function IconShapes() {
  return jsx("g", {
    children: jsx("path", {
      d: "m10.055 9 4.897.006c.58.001 1.048.47 1.048 1.05v4.894c0 .58-.47 1.05-1.049 1.05h-4.896a1.05 1.05 0 0 1-1.049-1.05v-4.9c0-.58.47-1.05 1.05-1.05zm-6.692-.123a.749.749 0 0 1 1.296 0l3.225 5.545A1.05 1.05 0 0 1 6.977 16H1.049a1.05 1.05 0 0 1-.907-1.578zm7.142 1.624V14.5H14.5v-3.995l-3.996-.004zm-6.494.244L1.83 14.5h4.364L4.01 10.745zM8.007 0a3.748 3.748 0 0 1 3.747 3.75A3.748 3.748 0 0 1 8.007 7.5a3.748 3.748 0 0 1-3.746-3.75A3.748 3.748 0 0 1 8.007 0zm0 1.5A2.249 2.249 0 0 0 5.76 3.75 2.249 2.249 0 1 0 8.007 1.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var Shapes = /* #__PURE__ */withIcon(IconShapes);

var IconShare = function IconShare() {
  return jsx("g", {
    children: jsx("path", {
      d: "m1.05 1.97 5.925.008a.751.751 0 0 1 .1 1.497l-.102.007-5.474-.009v10.023h9.992v-2.483c0-.38.282-.695.648-.745l.102-.006a.75.75 0 0 1 .742.65l.007.101v2.934c0 .543-.41.99-.935 1.047l-.114.006H1.049c-.54 0-.986-.41-1.043-.938L0 13.947V3.022c0-.543.41-.99.935-1.047l.116-.006zm11.408-.809.082.075 3.256 3.47c.245.26.27.652.074.94l-.074.09-3.253 3.466a.748.748 0 0 1-1.059.032.754.754 0 0 1-.102-.976l.07-.086 2.062-2.199h-5.52A3.252 3.252 0 0 0 4.75 9.046l-.005.185v1.788a.75.75 0 0 1-1.492.102l-.006-.102V9.231a4.755 4.755 0 0 1 4.529-4.757l.217-.005h5.522l-2.067-2.202a.754.754 0 0 1-.044-.98l.075-.083a.748.748 0 0 1 .978-.043z"
    })
  });
};
// pure annotation needed for tree-shaking
var Share = /* #__PURE__ */withIcon(IconShare);

var IconShareAndroid = function IconShareAndroid() {
  return jsx("g", {
    children: jsx("path", {
      d: "M12.75 0a3.25 3.25 0 1 1-2.477 5.354L6.417 7.268a3.26 3.26 0 0 1-.003 1.478l3.842 1.92a3.25 3.25 0 1 1-.67 1.341l-3.844-1.92a3.25 3.25 0 1 1 .01-4.163L9.59 4.017A3.25 3.25 0 0 1 12.75 0zm0 11a1.75 1.75 0 1 0 0 3.5 1.75 1.75 0 0 0 0-3.5zm-9.5-4.75a1.75 1.75 0 1 0 0 3.5 1.75 1.75 0 0 0 0-3.5zm9.5-4.75a1.75 1.75 0 1 0 0 3.5 1.75 1.75 0 0 0 0-3.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var ShareAndroid = /* #__PURE__ */withIcon(IconShareAndroid);

var IconShareIOS = function IconShareIOS() {
  return jsx("g", {
    children: jsx("path", {
      d: "m2.051 6.52 3.201.004a.75.75 0 0 1 .1 1.491l-.102.007-2.75-.004v6.483h11V8.033l-2.736-.002a.75.75 0 0 1-.742-.649l-.007-.102a.75.75 0 0 1 .649-.741l.102-.007 3.185.004c.541 0 .986.41 1.043.935l.006.114v7.366c0 .54-.41.986-.936 1.043L13.95 16H2.05a1.05 1.05 0 0 1-1.044-.935L1 14.951V7.568c0-.54.41-.985.936-1.042l.115-.007zM8.423.13l.091.074 3.457 3.252a.749.749 0 0 1-.942 1.161l-.086-.07L8.75 2.484v7.825a.75.75 0 0 1-1.493.102l-.007-.102V2.483L5.053 4.551a.75.75 0 0 1-.978.043l-.082-.075a.749.749 0 0 1-.043-.978l.075-.081L7.485.204A.75.75 0 0 1 8.424.13z"
    })
  });
};
// pure annotation needed for tree-shaking
var ShareIOS = /* #__PURE__ */withIcon(IconShareIOS);

var IconShuffle = function IconShuffle() {
  return jsx("g", {
    children: jsx("path", {
      d: "m6.758 5.866.156.248.137.234.126.236.123.253.196.449.234.578.092.231c.602 1.527 1.061 2.127 2.405 2.77.607.29 1.665.49 3.15.581l-.646-.644-.075-.086a.758.758 0 0 1 .075-.993.773.773 0 0 1 1-.073l.087.073 1.957 1.942.074.085a.756.756 0 0 1 .149.395v.105a.757.757 0 0 1-.149.5l-.074.085-1.957 1.942a.773.773 0 0 1-1.087 0 .758.758 0 0 1-.075-.993l.075-.086.747-.743c-1.768-.096-3.058-.336-3.897-.737-1.66-.795-2.36-1.644-3.053-3.326l-.33-.826-.127-.305-.167-.37-.105-.209-.11-.197-.188-.304C4.446 5.05 2.918 4.333.807 4.498a.75.75 0 0 1-.116-1.496c2.646-.207 4.706.76 6.067 2.864zm-1.694 4.02a.748.748 0 0 1 1.055-.087.75.75 0 0 1 .087 1.057C4.86 12.445 2.995 13.153.691 12.973a.75.75 0 0 1 .116-1.496c1.842.144 3.234-.384 4.257-1.592zm8.668-8.736.086.073 1.957 1.942.074.085a.756.756 0 0 1 .149.395v.105a.757.757 0 0 1-.149.5l-.074.085-1.957 1.942a.773.773 0 0 1-1.087 0 .758.758 0 0 1-.075-.993l.075-.086.674-.67c-1.5.09-2.567.29-3.178.582-.548.262-.965.547-1.292.865a.748.748 0 0 1-1.058-.014.75.75 0 0 1 .013-1.06c.448-.437 1.001-.814 1.691-1.144.835-.4 2.118-.639 3.874-.736l-.724-.72-.075-.085a.758.758 0 0 1 .075-.993.773.773 0 0 1 1-.073z"
    })
  });
};
// pure annotation needed for tree-shaking
var Shuffle = /* #__PURE__ */withIcon(IconShuffle);

var IconShuffleBox = function IconShuffleBox() {
  return jsx("g", {
    children: jsx("path", {
      d: "m1.05 0 13.901.017c.541 0 .986.41 1.043.936l.006.114V14.95c0 .541-.41.987-.936 1.044L14.95 16H1.05a1.05 1.05 0 0 1-1.044-.936L0 14.95V1.05C0 .47.47 0 1.05 0zm.45 1.501V14.5h13V1.516l-13-.015zm9.952 1.152.084.072L13.28 4.47a.75.75 0 0 1 .074.975l-.072.084-1.745 1.754a.75.75 0 0 1-1.135-.974l.072-.084.426-.432c-.719.062-1.238.187-1.547.36-.687.385-1.013.763-1.38 1.567l-.12.277.035.084c.407.942.73 1.35 1.473 1.766.308.172.822.297 1.533.36l-.419-.421a.75.75 0 0 1-.072-.977l.072-.084a.75.75 0 0 1 .977-.072l.084.072 1.744 1.745a.75.75 0 0 1 .074.975l-.072.084-1.745 1.754a.75.75 0 0 1-1.135-.974l.072-.084.503-.507c-1.016-.07-1.794-.252-2.349-.563-.722-.404-1.188-.829-1.575-1.431l-.025.045c-.872 1.435-2.19 2.112-3.846 1.96a.75.75 0 0 1 .136-1.494c1.064.098 1.833-.284 2.408-1.211l.105-.18.116-.216.102-.212.121-.277.058-.141-.059-.141-.12-.276-.103-.21-.115-.214C5.246 6.099 4.449 5.66 3.318 5.765a.75.75 0 1 1-.136-1.494c1.671-.153 2.998.537 3.87 1.999.384-.6.848-1.022 1.568-1.425.554-.31 1.333-.492 2.35-.563l-.495-.496a.75.75 0 0 1-.072-.977l.072-.084a.75.75 0 0 1 .977-.072z"
    })
  });
};
// pure annotation needed for tree-shaking
var ShuffleBox = /* #__PURE__ */withIcon(IconShuffleBox);

var IconShuffleBoxFilled = function IconShuffleBoxFilled() {
  return jsx("g", {
    children: jsx("path", {
      d: "m1.05 0 13.901.017c.541 0 .986.41 1.043.936l.006.114V14.95c0 .541-.41.987-.936 1.044L14.95 16H1.05a1.05 1.05 0 0 1-1.044-.936L0 14.95V1.05C0 .51.41.064.936.007L1.05 0zm10.402 2.653a.75.75 0 0 0-.883-.007l-.094.08-.072.083a.75.75 0 0 0-.007.883l.08.094.495.496c-1.018.071-1.797.252-2.351.563-.72.403-1.184.826-1.568 1.426-.872-1.462-2.198-2.153-3.87-2a.75.75 0 0 0 .136 1.494c1.064-.098 1.833.284 2.408 1.211l.105.18.115.215.102.21.121.276.059.142-.058.14-.12.277-.103.212-.116.215-.105.18c-.575.928-1.344 1.31-2.408 1.212a.75.75 0 0 0-.136 1.494c1.577.144 2.847-.462 3.718-1.76l.128-.2.025-.045c.387.602.853 1.027 1.575 1.431.5.28 1.18.455 2.051.538l.298.025-.503.507-.072.084a.75.75 0 0 0 1.051 1.046l.084-.072 1.745-1.754.072-.084a.75.75 0 0 0 .006-.882l-.08-.093-1.744-1.745-.084-.072a.75.75 0 0 0-.883-.007l-.094.08-.072.083a.75.75 0 0 0-.007.883l.08.094.417.42c-.71-.062-1.225-.187-1.532-.36-.69-.385-1.017-.765-1.386-1.571l-.122-.277.12-.278c.367-.804.693-1.182 1.38-1.567.274-.153.716-.27 1.315-.337l.233-.023-.427.432-.072.084a.75.75 0 0 0 1.051 1.046l.084-.072 1.745-1.754.072-.084a.75.75 0 0 0 .006-.882l-.08-.093-1.744-1.745-.084-.072z"
    })
  });
};
// pure annotation needed for tree-shaking
var ShuffleBoxFilled = /* #__PURE__ */withIcon(IconShuffleBoxFilled);

var IconSlider = function IconSlider() {
  return jsx("g", {
    children: jsx("path", {
      d: "M7.23 4c1.377 0 2.591.694 3.312 1.75h3.205A2.252 2.252 0 0 1 16 8c0 1.19-.927 2.166-2.099 2.245l-.154.005h-3.205A4.003 4.003 0 0 1 7.23 12a4.003 4.003 0 0 1-3.312-1.75H2.253A2.252 2.252 0 0 1 .005 8.154L0 8c0-1.19.927-2.166 2.099-2.245l.154-.005h1.664A4.003 4.003 0 0 1 7.23 4zm0 1.5A2.502 2.502 0 0 0 4.726 8c0 1.38 1.121 2.5 2.504 2.5A2.502 2.502 0 0 0 9.733 8c0-1.38-1.12-2.5-2.503-2.5zm3.935 1.75a4.016 4.016 0 0 1 0 1.5h2.582a.75.75 0 0 0 .744-.648L14.498 8a.75.75 0 0 0-.65-.743l-.101-.007h-2.582zM3.224 8c0-.256.024-.507.07-.75h-1.04a.75.75 0 0 0-.745.648L1.502 8c0 .38.283.693.65.743l.101.007h1.041a4.016 4.016 0 0 1-.07-.75z"
    })
  });
};
// pure annotation needed for tree-shaking
var Slider = /* #__PURE__ */withIcon(IconSlider);

var IconSlidersVert = function IconSlidersVert() {
  return jsx("g", {
    children: jsx("path", {
      d: "M3.897 8c.4 0 .724.336.724.75v6.5c0 .414-.325.75-.724.75a.737.737 0 0 1-.725-.75v-6.5c0-.414.325-.75.725-.75zm8.206 4c.4 0 .725.336.725.75v2.5c0 .414-.325.75-.725.75a.737.737 0 0 1-.724-.75v-2.5c0-.414.325-.75.724-.75zm-2.172-1.5a.737.737 0 0 1-.724-.75c0-.414.324-.75.724-.75h1.448V.75c0-.414.325-.75.724-.75.4 0 .725.336.725.75V9h1.448c.4 0 .724.336.724.75s-.324.75-.724.75H9.93zM3.897 0c.4 0 .724.336.724.75V5h1.448c.4 0 .724.336.724.75s-.324.75-.724.75H1.724A.737.737 0 0 1 1 5.75c0-.414.324-.75.724-.75h1.448V.75c0-.414.325-.75.725-.75z"
    })
  });
};
// pure annotation needed for tree-shaking
var SlidersVert = /* #__PURE__ */withIcon(IconSlidersVert);

var IconStar = function IconStar() {
  return jsx("g", {
    children: jsx("path", {
      d: "m5.738 5.528-4.999.008-.103.007c-.634.085-.878.933-.34 1.345l4.06 3.105-1.548 5.028-.023.094c-.12.65.623 1.149 1.17.732L8 12.772l4.044 3.075.08.053c.564.332 1.272-.215 1.068-.879l-1.55-5.028 4.062-3.105.078-.069c.457-.454.152-1.282-.521-1.283l-5-.008L8.704.525a.735.735 0 0 0-1.408 0L5.738 5.528zm2.261-2.285 1.016 3.262.04.1a.738.738 0 0 0 .664.425l3.334.005-2.714 2.077-.078.069a.76.76 0 0 0-.183.756l1.02 3.314-2.656-2.018-.09-.06a.729.729 0 0 0-.794.06L4.9 13.25l1.022-3.314.024-.102a.757.757 0 0 0-.285-.723L2.946 7.035l3.335-.005a.74.74 0 0 0 .704-.525l1.014-3.262z"
    })
  });
};
// pure annotation needed for tree-shaking
var Star = /* #__PURE__ */withIcon(IconStar);

var IconStarFilled = function IconStarFilled() {
  return jsx("g", {
    children: jsx("path", {
      d: "m5.738 5.528-4.999.008-.103.007c-.634.085-.878.933-.34 1.345l4.06 3.105-1.548 5.028-.023.094c-.12.65.623 1.149 1.17.732L8 12.772l4.044 3.075.08.053c.564.332 1.272-.215 1.068-.879l-1.55-5.028 4.062-3.105.078-.069c.457-.454.152-1.282-.521-1.283l-5-.008L8.704.525a.735.735 0 0 0-1.408 0L5.738 5.528z"
    })
  });
};
// pure annotation needed for tree-shaking
var StarFilled = /* #__PURE__ */withIcon(IconStarFilled);

var IconTableCol = function IconTableCol() {
  return jsx("g", {
    children: jsx("path", {
      d: "M5.5 0v.005l9.451.012c.541 0 .986.41 1.043.936l.006.114V14.95c0 .541-.41.987-.936 1.044L14.95 16H1.05a1.05 1.05 0 0 1-1.044-.936L0 14.95V1.05C0 .51.41.064.936.007L1.05 0 4 .004V0h1.5zm-4 13.144V14.5h2.194L1.5 13.144zm4-11.638V14.5h9V1.516l-9-.01zm-4 8.287v1.709L4 13.047v-1.709L1.5 9.793zm0-3.299v1.657L4 9.696V8.039L1.5 6.494zm0-3.35v1.708L4 6.397V4.689L1.5 3.144zM4 1.504l-2.5-.003L4 3.047V1.504z"
    })
  });
};
// pure annotation needed for tree-shaking
var TableCol = /* #__PURE__ */withIcon(IconTableCol);

var IconTableRow = function IconTableRow() {
  return jsx("g", {
    children: jsx("path", {
      d: "M16 5.5h-.005l-.012 9.451c0 .541-.41.986-.936 1.043l-.114.006H1.05a1.05 1.05 0 0 1-1.044-.936L0 14.95V1.05C0 .509.41.063.936.006L1.05 0h13.9c.54 0 .986.41 1.043.936L16 1.05 15.996 4H16v1.5zm-13.144-4H1.5v2.194L2.856 1.5zm11.638 4H1.5v9h12.984l.01-9zm-8.287-4H4.498L2.953 4h1.709l1.545-2.5zm3.299 0H7.849L6.304 4h1.657l1.545-2.5zm3.35 0h-1.708L9.603 4h1.708l1.545-2.5zm1.64 2.5.003-2.5L12.953 4h1.543z"
    })
  });
};
// pure annotation needed for tree-shaking
var TableRow = /* #__PURE__ */withIcon(IconTableRow);

var IconTablet = function IconTablet() {
  return jsx("g", {
    children: jsx("path", {
      d: "m2.05 0 11.901.017c.541 0 .986.41 1.043.936l.006.114V14.95c0 .541-.41.987-.936 1.044L13.95 16H2.05a1.05 1.05 0 0 1-1.044-.936L1 14.95V1.05c0-.54.41-.986.936-1.043L2.05 0zM13.5 12h-11v2.5h11V12zm-4.75.5a.75.75 0 1 1 0 1.5h-1.5a.75.75 0 1 1 0-1.5h1.5zM13.5 4h-11v6.5h11V4zM2.5 1.5v.998h11v-.983l-11-.015z"
    })
  });
};
// pure annotation needed for tree-shaking
var Tablet = /* #__PURE__ */withIcon(IconTablet);

var IconTabletCheck = function IconTabletCheck() {
  return jsx("g", {
    children: jsx("path", {
      d: "M15 14.95c0 .541-.41.987-.936 1.044L13.95 16H2.05a1.05 1.05 0 0 1-1.044-.936L1 14.95V1.05c0-.54.41-.986.936-1.043L2.05 0l11.901.017c.541 0 .986.41 1.043.936l.006.114V14.95zm-1.5-1.45h-11v1h11v-1zm0-9.5h-11v8h11V4zm-2.424 1.672a.75.75 0 0 1 .072.977l-.072.084-3.486 3.486a.75.75 0 0 1-.977.072l-.084-.072-1.634-1.634A.75.75 0 0 1 5.87 7.45l.084.073L7.06 8.627l2.956-2.955a.75.75 0 0 1 1.06 0zM2.5 1.501v.998h11v-.983l-11-.015z"
    })
  });
};
// pure annotation needed for tree-shaking
var TabletCheck = /* #__PURE__ */withIcon(IconTabletCheck);

var IconTag = function IconTag() {
  return jsx("g", {
    children: jsx("path", {
      d: "M15.78.22c.269.269.291.69.067.985l-.067.077-1.66 1.66.695.696a.75.75 0 0 1 .208.398l.012.115.101 4.166a.751.751 0 0 1-.14.456l-.08.093-6.912 6.914a.75.75 0 0 1-.977.073l-.084-.073L.22 9.055a.751.751 0 0 1-.073-.978l.073-.084L7.132 1.08a.75.75 0 0 1 .426-.213L7.679.86l4.156.092a.75.75 0 0 1 .426.145l.088.075.71.709 1.66-1.66a.75.75 0 0 1 1.061 0zm-2.239 4.266-.482-.483-.709.71c.377.792.265 1.759-.336 2.447l-.105.113a2.251 2.251 0 1 1-3.185-3.186 2.252 2.252 0 0 1 2.567-.438l.706-.708-.496-.496-3.533-.078-6.156 6.157 5.662 5.663 6.153-6.156-.086-3.545zm-2.694.663A.75.75 0 1 0 9.786 6.21a.75.75 0 0 0 1.061-1.06z"
    })
  });
};
// pure annotation needed for tree-shaking
var Tag = /* #__PURE__ */withIcon(IconTag);

var IconTemplate = function IconTemplate() {
  return jsx("g", {
    children: jsx("path", {
      d: "m2.089 0 11.824.017c.56 0 1.022.41 1.08.936l.007.114V14.95c0 .541-.425.987-.97 1.044L13.91 16H2.09c-.561 0-1.023-.41-1.083-.936L1 14.95V1.05c0-.54.425-.986.97-1.043L2.09 0zM8 10.5H2.556v4H8v-4zm5.444 0H9.556v4h3.888v-4zM8 5.5H2.556V9H8V5.5zm5.444 0H9.556V9h3.888V5.5zM2.556 1.5v2.498h10.888V1.516L2.556 1.501z"
    })
  });
};
// pure annotation needed for tree-shaking
var Template = /* #__PURE__ */withIcon(IconTemplate);

var IconTextbox = function IconTextbox() {
  return jsx("g", {
    children: jsx("path", {
      d: "M15.3 5a.7.7 0 0 1 .694.605L16 5.7v4.6a.7.7 0 0 1-.605.694L15.3 11H.7a.7.7 0 0 1-.694-.605L0 10.3V5.7a.7.7 0 0 1 .605-.694L.7 5h14.6zm-.85 1.55H1.55v2.9h12.9v-2.9z"
    })
  });
};
// pure annotation needed for tree-shaking
var Textbox = /* #__PURE__ */withIcon(IconTextbox);

var IconTextboxMultiple = function IconTextboxMultiple() {
  return jsx("g", {
    children: jsx("path", {
      d: "M15.3 9a.7.7 0 0 1 .694.605L16 9.7v4.6a.7.7 0 0 1-.605.694L15.3 15H.7a.7.7 0 0 1-.694-.605L0 14.3V9.7a.7.7 0 0 1 .605-.694L.7 9h14.6zm-.85 1.55H1.55v2.9h12.9v-2.9zM15.3 1a.7.7 0 0 1 .694.605L16 1.7v4.6a.7.7 0 0 1-.605.694L15.3 7H.7a.7.7 0 0 1-.694-.605L0 6.3V1.7a.7.7 0 0 1 .605-.694L.7 1h14.6zm-.85 1.55H1.55v2.9h12.9v-2.9z"
    })
  });
};
// pure annotation needed for tree-shaking
var TextboxMultiple = /* #__PURE__ */withIcon(IconTextboxMultiple);

var IconThumbsUp = function IconThumbsUp() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8.76 1c1.721 0 2.224 1.5 1.997 3.442a3.652 3.652 0 0 1-.087.466l-.049.169h3.54c.965 0 1.756.743 1.833 1.687l.006.15c0 .57-.26 1.08-.668 1.417a1.83 1.83 0 0 1-.359 2.039 1.796 1.796 0 0 1-.619 2.37 1.742 1.742 0 0 1-1.52 2.255l-.142.005H7.128a4.998 4.998 0 0 1-2.529-.698c-.142.111-.314.186-.501.212l-.144.01H1.051a1.05 1.05 0 0 1-1.045-.936L0 13.474V6.302c0-.45.285-.841.762-1l.134-.038.155-.012 2.909.014c.257.001.493.095.675.25.64-.135 1.374-.685 2.019-1.418a.592.592 0 0 0 .054-.075l.063-.111.074-.154.087-.203.16-.405.25-.65.136-.337.125-.284C7.926 1.185 8.183 1 8.759 1zm.202 1.553-.068.138-.083.185-.153.383-.245.641-.128.314-.113.257c-.127.27-.24.448-.39.618-.83.94-1.767 1.656-2.777 1.886v5.806l.12.083c.577.38 1.166.587 1.774.628l.23.008h5.563a.24.24 0 0 0 .064-.473l-.064-.009h-.907a.75.75 0 0 1-.101-1.493l.101-.007h1.605a.298.298 0 0 0 .069-.587l-.069-.007h-1.598a.75.75 0 0 1-.102-1.494l.102-.006h1.865a.337.337 0 0 0 .068-.667l-.05-.006h-.661l-.029-.002-.81.001a.75.75 0 0 1-.103-1.493l.102-.007 1.987.001a.337.337 0 0 0 .068-.667l-.068-.006H9.5a.75.75 0 0 1-.665-1.1c.25-.475.392-.881.43-1.21.103-.883.008-1.45-.238-1.667l-.065-.048zm-7.46 4.2v6.27h2.002V6.764l-2.002-.011z"
    })
  });
};
// pure annotation needed for tree-shaking
var ThumbsUp = /* #__PURE__ */withIcon(IconThumbsUp);

var IconThumbsUpFilled = function IconThumbsUpFilled() {
  return jsx("g", {
    children: jsx("path", {
      d: "M10 2.509c.125.598.06 1.217-.048 1.737l-.068.298-.073.269-.166.567c-.02.08-.022.122 0 .122h5.105a1.249 1.249 0 1 1 0 2.494h-.5a1.249 1.249 0 1 1 0 2.494l-.5.001a1.249 1.249 0 1 1 0 2.494H13a.999.999 0 1 1 0 1.997L8.078 15l-1.526-.013-.13-.005c-.378-.023-.8-.155-1.263-.397A.3.3 0 0 1 5 14.32V6.172a.3.3 0 0 1 .122-.242c1.113-.812 1.834-1.54 2.162-2.182.15-.303.245-.653.328-1l.12-.51c.148-.606.32-1.117.769-1.226.33-.08 1.23.205 1.5 1.497zM2.8 6.001a.7.7 0 0 1 .7.699v7.084a.7.7 0 0 1-.7.699H.7a.7.7 0 0 1-.7-.699V6.7A.7.7 0 0 1 .7 6h2.1z"
    })
  });
};
// pure annotation needed for tree-shaking
var ThumbsUpFilled = /* #__PURE__ */withIcon(IconThumbsUpFilled);

var IconTimer = function IconTimer() {
  return jsx("g", {
    children: jsx("path", {
      d: "M9.75 0a.75.75 0 1 1 0 1.5h-1v.54a7.001 7.001 0 1 1-1.5 0V1.5h-1a.75.75 0 0 1 0-1.5h3.5zM8 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11zm3.224 2.526a.75.75 0 0 1 .073.976l-.073.084L9.842 8.47a2 2 0 1 1-1.06-1.06l1.382-1.383a.75.75 0 0 1 1.06 0zM8 8.75a.5.5 0 1 0 0 1 .5.5 0 0 0 0-1zm5.14-7.192c.513.344.991.738 1.426 1.177a.75.75 0 1 1-1.064 1.057 7.792 7.792 0 0 0-1.197-.987.75.75 0 1 1 .835-1.247z"
    })
  });
};
// pure annotation needed for tree-shaking
var Timer = /* #__PURE__ */withIcon(IconTimer);

var IconToggle = function IconToggle() {
  return jsx("g", {
    children: jsx("path", {
      d: "M5 13A5 5 0 0 1 5 3h6a5 5 0 0 1 .217 9.995L11 13H5zm2.43-8.5H5a3.5 3.5 0 0 0-.192 6.995L5 11.5l2.43.001A4.984 4.984 0 0 1 6 8c0-1.363.545-2.598 1.43-3.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var Toggle = /* #__PURE__ */withIcon(IconToggle);

var IconToolbox = function IconToolbox() {
  return jsx("g", {
    children: jsx("path", {
      d: "m6.053.001 3.901.015c.54.003.983.412 1.04.936l.006.114V3.29h4.3a.7.7 0 0 1 .694.605L16 3.99V15.3a.7.7 0 0 1-.605.694L15.3 16H.7a.7.7 0 0 1-.694-.605L0 15.3V3.99a.7.7 0 0 1 .605-.693L.7 3.29H5V1.051c0-.541.41-.987.938-1.044l.115-.006zM4 9.5H1.55v4.951h12.9V9.5H12v1.25a.75.75 0 1 1-1.5 0V9.5h-5v1.25a.75.75 0 1 1-1.5 0V9.5zM14.45 4.84H1.55V8h12.9V4.84zM6.5 1.503V3.29h3V1.514l-3-.011z"
    })
  });
};
// pure annotation needed for tree-shaking
var Toolbox = /* #__PURE__ */withIcon(IconToolbox);

var IconTranslation = function IconTranslation() {
  return jsxs("g", {
    children: [jsx("path", {
      d: "M6.01.784a.784.784 0 1 0-1.568 0V1.5H.785C.351 1.5 0 1.836 0 2.25S.351 3 .784 3h6.55a7.022 7.022 0 0 1-.754 2.03 7.23 7.23 0 0 1-1.016 1.394 7.229 7.229 0 0 1-1.322-2.01l-1.563.298a8.731 8.731 0 0 0 1.727 2.723 7.705 7.705 0 0 1-2.58 1.18c-.474.118-.774.6-.602 1.039.13.33.488.532.845.45l.153-.037a9.29 9.29 0 0 0 3.342-1.59A9.253 9.253 0 0 0 8.23 9.87l.55-1.405a7.678 7.678 0 0 1-2.058-1.03 8.73 8.73 0 0 0 1.24-1.698A8.469 8.469 0 0 0 8.927 3h1.266c.433 0 .784-.336.784-.75s-.35-.75-.784-.75H6.011V.784Z",
      fillRule: "nonzero"
    }), jsx("path", {
      d: "M7.903 14.988c-.19.49.188 1.012.735 1.012.327 0 .62-.195.734-.489l.61-1.566h3.889l.61 1.566a.784.784 0 0 0 .734.489c.547 0 .926-.522.735-1.012L13.266 8.09a1.428 1.428 0 0 0-1.34-.89c-.597 0-1.13.355-1.339.89l-2.684 6.898Zm2.663-2.543 1.36-3.497 1.361 3.497h-2.721Z"
    })]
  });
};
// pure annotation needed for tree-shaking
var Translation = /* #__PURE__ */withIcon(IconTranslation);

var IconTrash = function IconTrash() {
  return jsx("g", {
    children: jsx("path", {
      d: "M9.583 0a1.25 1.25 0 0 1 1.2.902l.03.125L11.171 3 15.25 3a.75.75 0 1 1 0 1.5h-1.298l-.673 10.335a1.25 1.25 0 0 1-1.117 1.162l-.13.006H3.968a1.25 1.25 0 0 1-1.232-1.039l-.015-.13L2.048 4.5.75 4.5a.75.75 0 0 1 0-1.5h4.079l.359-1.973A1.25 1.25 0 0 1 6.289.007L6.417 0h3.166zm2.867 4.5H3.551l.651 10.002h7.595L12.45 4.5zm-6.226 2c.4 0 .724.324.724.724v4.552a.724.724 0 0 1-1.448 0V7.224c0-.4.324-.724.724-.724zm3.526 0a.75.75 0 0 1 .75.75v4.5a.75.75 0 1 1-1.5 0v-4.5a.75.75 0 0 1 .75-.75zm-.376-5H6.626L6.352 3h3.294l-.272-1.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var Trash = /* #__PURE__ */withIcon(IconTrash);

var IconTruck = function IconTruck() {
  return jsx("g", {
    children: jsx("path", {
      d: "m8.001 1 6.95.009c.541 0 .986.41 1.043.936L16 2.06v10.388c0 .542-.41.988-.936 1.045l-.114.006h-1.328a2.251 2.251 0 0 1-4.244 0H6.872a2.251 2.251 0 0 1-4.244 0H1.05a1.05 1.05 0 0 1-1.044-.936L0 12.448V7.995c0-.138.037-.267.102-.378l2.584-4.495c.288-.5.8-.823 1.368-.872l.156-.006 3.04.012V1.75a.75.75 0 0 1 .65-.743L8 1zM4.75 11.998a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5zm6.75 0a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5zM8.75 2.501v5.494a.75.75 0 0 1-.648.744L8 8.746H1.5v3.252h1.128a2.251 2.251 0 0 1 4.247.01h2.5a2.251 2.251 0 0 1 4.247-.01h.878V2.51l-5.75-.008zM4.204 3.745a.25.25 0 0 0-.177.073l-.04.053-1.94 3.373 5.203.001V3.757l-3.046-.012z"
    })
  });
};
// pure annotation needed for tree-shaking
var Truck = /* #__PURE__ */withIcon(IconTruck);

var IconGroup = function IconGroup() {
  return jsx("g", {
    children: jsx("path", {
      d: "m1.05 9 4.903.016c.54.001.984.41 1.04.936l.007.114v4.883c0 .541-.41.987-.936 1.044l-.114.006h-4.9a1.05 1.05 0 0 1-1.044-.936L0 14.95V10.05c0-.541.41-.987.936-1.044L1.051 9zm9 0 4.903.016c.54.001.984.41 1.04.936l.007.114v4.883c0 .541-.41.987-.936 1.044l-.114.006h-4.9a1.05 1.05 0 0 1-1.044-.936L9 14.95V10.05c0-.541.41-.987.936-1.044L10.05 9zM1.5 10.5v3.999h4v-3.985l-4-.014zm9 0v3.999h4v-3.985l-4-.014zM1.05 0l4.903.016c.54.001.984.41 1.04.936L7 1.066v4.883c0 .541-.41.987-.936 1.044l-.114.006h-4.9a1.05 1.05 0 0 1-1.044-.936L0 5.95V1.05C0 .509.41.063.936.006L1.05 0zm9 0 4.903.016c.54.001.984.41 1.04.936l.007.114v4.883c0 .541-.41.987-.936 1.044l-.114.006h-4.9a1.05 1.05 0 0 1-1.044-.936L9 5.95V1.05c0-.541.41-.987.936-1.044L10.05 0zM1.5 1.5v3.999h4V1.514l-4-.014zm9 0v3.999h4V1.514l-4-.014z"
    })
  });
};
// pure annotation needed for tree-shaking
var Ungroup = /* #__PURE__ */withIcon(IconGroup);

var IconUnlock = function IconUnlock() {
  return jsx("g", {
    children: jsx("path", {
      d: "M12.002 0a4.009 4.009 0 0 1 3.993 3.81l.005.199V6.94a.75.75 0 0 1-1.493.102l-.007-.102V4.01a2.509 2.509 0 0 0-2.336-2.503L12 1.5a2.494 2.494 0 0 0-2.495 2.327l-.005.164v2.022l2.452.004c.54.001.985.41 1.042.936l.006.114v7.883c0 .541-.41.987-.936 1.044L11.95 16H1.05a1.05 1.05 0 0 1-1.044-.936L0 14.95v-7.9c0-.54.41-.986.936-1.043L1.05 6 8 6.011v-2.02A3.991 3.991 0 0 1 11.792.005l.21-.005zM1.5 7.501V14.5h10V7.516l-10-.015zM6.5 9a.75.75 0 0 1 .75.75v2.5a.75.75 0 1 1-1.5 0v-2.5A.75.75 0 0 1 6.5 9z"
    })
  });
};
// pure annotation needed for tree-shaking
var Unlock = /* #__PURE__ */withIcon(IconUnlock);

var IconUnnest = function IconUnnest() {
  return jsx("g", {
    children: jsx("path", {
      d: "m10.05 9 4.903.015c.54.001.984.41 1.04.935l.007.115v4.885c0 .541-.41.987-.936 1.044L14.95 16h-4.9a1.05 1.05 0 0 1-1.044-.936L9 14.95v-4.9c0-.54.41-.986.936-1.043l.114-.006zm-6.18.471a.759.759 0 0 1 0 1.07l-1.197 1.203h4.575c.415 0 .752.339.752.756 0 .38-.278.694-.64.748l-.112.008H2.673l1.196 1.204a.759.759 0 0 1 0 1.069.75.75 0 0 1-1.063 0l-2.66-2.673a.506.506 0 0 1-.057-.643l.058-.07 2.659-2.672a.75.75 0 0 1 1.063 0zm6.63 1.03V14.5h4v-3.987l-4-.012zM1.05.001l13.901.014c.54 0 .986.41 1.043.936l.006.114V5.95c0 .541-.41.987-.936 1.044L14.95 7H1.05a1.05 1.05 0 0 1-1.044-.936L0 5.95v-4.9C0 .51.41.063.936.006L1.051 0zM1.5 1.5v4h13V1.515L1.5 1.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var Unnest = /* #__PURE__ */withIcon(IconUnnest);

var IconUser = function IconUser() {
  return jsx("g", {
    children: jsx("path", {
      d: "m10.383 9 .123.014c.162.029.319.103.449.223.158.147.321.273.493.384l.202.122.199.104.219.095.262.097.328.107.417.128c1.117.337 1.685.641 2.184 1.378.162.24.295.638.417 1.24l.081.43.079.496.078.56.08.631a.886.886 0 0 1-.762.983l-.12.008H.888l-.121-.008A.887.887 0 0 1 0 15.121l.006-.112.08-.63.078-.561.079-.495.08-.432c.123-.601.256-.999.418-1.239.465-.688.992-.999 1.968-1.31l.633-.196.328-.107.262-.097.219-.095.199-.104.202-.122c.172-.11.335-.237.493-.384a.886.886 0 0 1 .538-.234L5.699 9a.89.89 0 0 1 .581.257l.107.132L8 11.609l1.645-2.265a.747.747 0 0 1 .263-.226c.11-.063.23-.101.353-.113L10.383 9zm.256 1.884-.174-.119-1.9 2.616a.7.7 0 0 1-1.132 0l-1.898-2.616-.204.138-.286.17-.199.103-.097.047-.3.127-.313.114-.352.115-.618.19c-.729.234-.947.376-1.182.723l-.011.023-.037.1-.05.17-.057.239-.03.143-.075.392-.074.466-.068.475 12.834.003-.061-.45-.073-.455-.075-.407-.057-.256-.05-.2-.043-.14-.037-.095c-.217-.322-.42-.474-1.027-.679l-.358-.113-.443-.136-.38-.125-.343-.13-.218-.094-.097-.047-.283-.15-.232-.142zM8 0c2.21 0 3.731 1.79 3.731 4 0 2.144-1.434 4.836-3.538 4.993L8 9C5.79 9 4.268 6.21 4.268 4c0-2.14 1.43-3.888 3.527-3.995L8 0zm0 1.5C6.705 1.5 5.768 2.532 5.768 4c0 1.744 1.185 3.5 2.232 3.5 1.047 0 2.231-1.756 2.231-3.5 0-1.468-.936-2.5-2.231-2.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var User = /* #__PURE__ */withIcon(IconUser);

var IconUserArrow = function IconUserArrow() {
  return jsx("g", {
    children: jsx("path", {
      d: "M9.243 9.271c.133.124.272.231.417.325l.17.104.168.088.184.08.221.082.277.09.352.11c.942.285 1.422.542 1.842 1.167.137.203.25.54.353 1.05l.068.365.067.42.066.475.067.534a.75.75 0 0 1-.643.832L12.75 15h-12l-.102-.007a.75.75 0 0 1-.648-.73l.005-.102.067-.534.066-.476.067-.419.068-.366c.104-.509.216-.846.353-1.049.388-.577.826-.84 1.632-1.102l.562-.174.277-.091.22-.082.185-.08.168-.088.17-.104c.145-.094.284-.2.417-.325a.75.75 0 0 1 1.199.25 1.992 1.992 0 0 0 2.591 0 .675.675 0 0 1 .137-.212.75.75 0 0 1 1.06-.038zM6.75 11.5a3.49 3.49 0 0 1-2.06-.67l-.039.027a4.138 4.138 0 0 1-.806.418l-.19.07-.336.111-.58.18c-.455.146-.664.258-.815.445l-.055.074-.027.066-.036.137-.044.204-.05.266-.107.671h10.29l-.107-.671-.05-.266-.044-.204-.036-.137-.016-.043-.011-.023c-.157-.233-.358-.355-.87-.52l-.58-.179-.336-.111a4.148 4.148 0 0 1-.831-.385l-.205-.129a3.49 3.49 0 0 1-2.059.669zm0-9.75C8.548 1.75 10 3.176 10 4.968c0 1.89-1.49 3.985-3.25 3.985-1.761 0-3.25-2.094-3.25-3.985 0-1.792 1.452-3.218 3.25-3.218zm0 1.5C5.774 3.25 5 4.01 5 4.968c0 1.15.949 2.485 1.75 2.485.8 0 1.75-1.335 1.75-2.485 0-.958-.774-1.718-1.75-1.718zm7.03-2.103.083.073 1.92 1.92.073.084a.747.747 0 0 1 .145.382l.002.144-.002.057-.012.092-.019.073-.024.067-.035.072-.055.085a.785.785 0 0 1-.072.084l-1.92 1.92a.75.75 0 0 1-1.134-.976l.073-.084.639-.64H11.5a.75.75 0 0 1-.102-1.493L11.5 3h2.022l-.72-.72-.072-.084a.75.75 0 0 1 1.05-1.049z"
    })
  });
};
// pure annotation needed for tree-shaking
var UserArrow = /* #__PURE__ */withIcon(IconUserArrow);

var IconUserFilled = function IconUserFilled() {
  return jsx("g", {
    children: jsx("path", {
      d: "M10.955 9.237c.158.147.321.273.493.384l.202.122.199.104.219.095.262.097.328.107.417.128c1.117.337 1.685.641 2.184 1.378.162.24.295.638.417 1.24l.081.43.079.496.078.56.08.631a.886.886 0 0 1-.762.983l-.12.008H.888l-.121-.008A.887.887 0 0 1 0 15.121l.006-.112.08-.63.078-.561.079-.495.08-.432c.123-.601.256-.999.418-1.239.465-.688.992-.999 1.968-1.31l.633-.196.328-.107.262-.097.219-.095.199-.104.202-.122c.172-.11.335-.237.493-.384a.891.891 0 0 1 1.235.021l.107.132L8 11.609l1.645-2.265a.747.747 0 0 1 .263-.226.89.89 0 0 1 1.048.12zM8 0c2.21 0 3.731 1.79 3.731 4S10.21 9 8 9C5.79 9 4.268 6.21 4.268 4S5.791 0 8 0z"
    })
  });
};
// pure annotation needed for tree-shaking
var UserFilled = /* #__PURE__ */withIcon(IconUserFilled);

var IconUserPlus = function IconUserPlus() {
  return jsx("g", {
    children: jsx("path", {
      d: "M9.243 9.271c.133.124.272.231.417.325l.17.104.168.088.184.08.221.082.277.09.352.11c.942.285 1.422.542 1.842 1.167.137.203.25.54.353 1.05l.068.365.067.42.066.475.067.534a.75.75 0 0 1-.643.832L12.75 15h-12l-.102-.007a.75.75 0 0 1-.648-.73l.005-.102.067-.534.066-.476.067-.419.068-.366c.104-.509.216-.846.353-1.049.388-.577.826-.84 1.632-1.102l.562-.174.277-.091.22-.082.185-.08.168-.088.17-.104c.145-.094.284-.2.417-.325a.75.75 0 0 1 1.199.25 1.992 1.992 0 0 0 2.591 0 .675.675 0 0 1 .137-.212.75.75 0 0 1 1.06-.038zM6.75 11.5a3.49 3.49 0 0 1-2.06-.67l-.039.027a4.138 4.138 0 0 1-.806.418l-.19.07-.336.111-.58.18c-.455.146-.664.258-.815.445l-.055.074-.027.066-.036.137-.044.204-.05.266-.107.671h10.29l-.107-.671-.05-.266-.044-.204-.036-.137-.016-.043-.011-.023c-.157-.233-.358-.355-.87-.52l-.58-.179-.336-.111a4.148 4.148 0 0 1-.831-.385l-.205-.129a3.49 3.49 0 0 1-2.059.669zm0-9.75C8.548 1.75 10 3.176 10 4.968c0 1.89-1.49 3.985-3.25 3.985-1.761 0-3.25-2.094-3.25-3.985 0-1.792 1.452-3.218 3.25-3.218zm0 1.5C5.774 3.25 5 4.01 5 4.968c0 1.15.949 2.485 1.75 2.485.8 0 1.75-1.335 1.75-2.485 0-.958-.774-1.718-1.75-1.718zM13.5 1a.75.75 0 0 1 .75.75v1h1a.75.75 0 1 1 0 1.5h-1v1a.75.75 0 1 1-1.5 0v-1h-1a.75.75 0 1 1 0-1.5h1v-1A.75.75 0 0 1 13.5 1z"
    })
  });
};
// pure annotation needed for tree-shaking
var UserPlus = /* #__PURE__ */withIcon(IconUserPlus);

var IconUsers = function IconUsers() {
  return jsx("g", {
    children: jsx("path", {
      d: "M3.455 7.717a.43.43 0 0 1 .078.07l.06.082.577.882a.2.2 0 0 1-.09.294l-1.182.501-.119.057-.202.081-.211.076-.36.126a.3.3 0 0 0-.192.21l-.06.235-.115.945a.2.2 0 0 0 .198.224H5.28l.122-1.114.065-.457.02-.108.017-.065a.088.088 0 0 1 .007-.017c.287-.464.61-.7 1.16-.913l.192-.07.436-.147.225-.08.18-.072.15-.072.137-.078.14-.091c.117-.083.23-.178.339-.288a.574.574 0 0 1 .753-.068.522.522 0 0 1 .096.084l.073.099L10.5 9.707l1.131-1.7a.529.529 0 0 1 .181-.169.573.573 0 0 1 .72.09c.11.11.222.205.34.288l.139.091.136.078.15.072.18.072.226.08.287.097c.768.252 1.159.48 1.501 1.033l.01.021.021.082.039.222.03.214.113.96.146 1.45.147 1.57c.04.364-.199.688-.524.736l-.083.006H5.611l-.083-.006c-.3-.044-.527-.324-.528-.654l.004-.083L5.126 13H.5l-.068-.005c-.245-.037-.43-.27-.432-.545l.004-.07.225-1.943.075-.563.06-.379.024-.12.025-.084c.266-.438.562-.633 1.11-.828l.357-.122.185-.067.147-.06.123-.06.112-.064.113-.077c.097-.069.189-.148.278-.24a.463.463 0 0 1 .617-.056zm8.882 1.934-.59.887a1.5 1.5 0 0 1-2.411.116l-.085-.116-.59-.887-.064.037-.1.053-.235.108-.235.093-.251.09-.397.132-.167.061c-.06.023-.11.043-.151.062l-.104.054-.041.028-.051.41-.088.788-.195 1.933h7.837l-.14-1.451-.113-1.06-.077-.614-.021-.017a1.193 1.193 0 0 0-.294-.14l-.26-.09-.315-.106-.28-.1-.266-.11-.15-.071-.1-.053-.066-.037zM10.5 1c1.628 0 2.75 1.343 2.75 3s-1.122 3.75-2.75 3.75S7.75 5.657 7.75 4s1.122-3 2.75-3zM5.05 2C6.382 2 7.3 3.12 7.3 4.5s-.918 3.125-2.25 3.125C3.718 7.625 2.8 5.881 2.8 4.5c0-1.38.918-2.5 2.25-2.5zm5.45.5c-.72 0-1.25.593-1.25 1.5 0 1.127.744 2.25 1.25 2.25S11.75 5.127 11.75 4c0-.907-.529-1.5-1.25-1.5zm-5.45 1c-.425 0-.75.371-.75 1 0 .406.148.902.383 1.262.175.269.313.363.367.363.054 0 .192-.094.367-.363.235-.36.383-.856.383-1.262 0-.629-.325-1-.75-1z"
    })
  });
};
// pure annotation needed for tree-shaking
var Users = /* #__PURE__ */withIcon(IconUsers);

var IconUserDeleted = function IconUserDeleted() {
  return jsx("g", {
    children: jsx("path", {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "m7.999 11.609-.517-.712-1.305.753 1.256 1.73a.701.701 0 0 0 1.133.001l1.899-2.616.174.119.232.141.283.151.097.047.218.095.343.129.38.125.443.136.357.113c.607.205.811.357 1.028.68l.037.094.042.14.051.2.057.256.075.407.073.456.061.449L1.582 14.5l.068-.475.074-.466.063-.331a2.24 2.24 0 0 1-1.244-1.163c-.079.217-.15.49-.22.826l-.08.432-.079.495-.078.56-.08.631-.006.112c.002.44.332.812.768.871l.12.008h14.223l.121-.008a.886.886 0 0 0 .762-.983l-.08-.63-.078-.561-.079-.495-.08-.432c-.123-.601-.256-.999-.418-1.239-.499-.737-1.067-1.041-2.184-1.378l-.417-.128-.329-.107-.261-.097-.219-.095-.199-.104-.202-.122a3.49 3.49 0 0 1-.493-.384.886.886 0 0 0-.288-.179l-1.405.811L8 11.61Zm-.215-2.618 2.93-1.691 3.074-1.776a.75.75 0 1 0-.75-1.299l-10.826 6.25a.75.75 0 1 0 .75 1.3l3.634-2.098 1.188-.686Zm2.446-4.876 1.446-.835C11.384 1.414 9.963 0 8 0l-.205.005C5.697.112 4.268 1.86 4.268 4c0 1.004.315 2.129.866 3.057l1.297-.749A4.548 4.548 0 0 1 5.768 4c0-1.468.937-2.5 2.232-2.5 1.295 0 2.231 1.032 2.231 2.5 0 .038 0 .077-.002.115Z"
    })
  });
};
// pure annotation needed for tree-shaking
var UserDeleted = /* #__PURE__ */withIcon(IconUserDeleted);

var IconUsersFilled = function IconUsersFilled() {
  return jsx("g", {
    children: jsx("path", {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M12.5314 8.92809C12.6401 9.03811 12.7526 9.13282 12.8708 9.2155L13.0096 9.3075L13.1462 9.38546L13.2965 9.4568L13.4766 9.52897L13.7022 9.6094L13.9893 9.70551C14.7568 9.95825 15.1477 10.1864 15.4903 10.7393C15.6017 10.9191 15.6931 11.2174 15.7775 11.6684L15.8329 11.9921L15.8872 12.3632L15.9411 12.7841L15.9956 13.2571C16.0358 13.6219 15.797 13.9462 15.472 13.994L15.3891 14H5.61094L5.52801 13.994C5.22803 13.9499 5.00147 13.6701 5 13.3404L5.00441 13.2571L5.05889 12.7841L5.11282 12.3632L5.16706 11.9921L5.22249 11.6684L5.25914 11.4844C5.33345 11.1346 5.41421 10.8934 5.50972 10.7393C5.79745 10.2749 6.11936 10.0396 6.66987 9.82637L6.86211 9.75584L7.29782 9.6094L7.52344 9.52897L7.70346 9.4568L7.85381 9.38546L7.9904 9.3075L8.12918 9.2155C8.24743 9.13282 8.35989 9.03811 8.46857 8.92809C8.67945 8.71462 8.99222 8.69438 9.22225 8.86026C9.25648 8.88344 9.28862 8.91133 9.31775 8.94374L9.39132 9.04251L10.4993 10.7067L11.6304 9.00772C11.6793 8.9341 11.7416 8.87724 11.811 8.83823C12.0372 8.69673 12.3305 8.72472 12.5314 8.92809ZM3.45457 8.71688C3.48257 8.7362 3.50887 8.75944 3.53271 8.78645L3.5929 8.86876L4.49943 10.2556C4.5288 10.3657 4.52713 10.4607 4.49443 10.5408C4.40993 10.7478 4.34154 10.9895 4.28017 11.28L4.23957 11.4844L4.18141 11.8233L4.12333 12.2186L4.067 12.6571L4.027 13H0.499862L0.432011 12.995C0.186572 12.9582 0.00120338 12.7251 0 12.4503L0.00360775 12.3809L0.0481851 11.9868L0.0923092 11.636L0.136689 11.3267L0.182035 11.057C0.251131 10.6812 0.325879 10.4326 0.417047 10.2827C0.678619 9.85271 0.974709 9.65843 1.52355 9.4632L1.88004 9.34116L2.06463 9.27414L2.21192 9.214L2.33493 9.15455L2.44669 9.08958L2.56024 9.01292C2.65699 8.94402 2.749 8.86509 2.83792 8.77341C3.01046 8.59551 3.26636 8.57865 3.45457 8.71688ZM10.5 2C12.0188 2 13.0652 3.34315 13.0652 5C13.0652 6.65685 12.0186 8.75 10.4998 8.75C8.981 8.75 7.93436 6.65685 7.93436 5C7.93436 3.34315 8.98121 2 10.5 2ZM4.5 3C5.74264 3 6.59882 4.11929 6.59882 5.5C6.59882 6.88071 5.74247 8.625 4.49983 8.625C3.25719 8.625 2.40084 6.88071 2.40084 5.5C2.40084 4.11929 3.25736 3 4.5 3Z",
      fill: "#333E48"
    })
  });
};
// pure annotation needed for tree-shaking
var UsersFilled = /* #__PURE__ */withIcon(IconUsersFilled);

var IconUsersGroup = function IconUsersGroup() {
  return jsx("g", {
    children: jsx("path", {
      d: "M9.461 8.123c.08.076.161.141.247.198l.1.063.1.054.109.05.13.05.164.055.209.066c.557.174.841.332 1.09.713l.011.038.021.138.044.447.081 1.08.089 1.425h2.31a.2.2 0 0 0 .012 0l.012-.001a.2.2 0 0 0 .174-.223l-.116-.945-.06-.242a.3.3 0 0 0-.18-.206l-1.51-.6a.2.2 0 0 1-.093-.295l.573-.878.06-.082a.43.43 0 0 1 .078-.07.462.462 0 0 1 .616.057c.089.092.271.224.747.448s.844.39 1.109.828l.025.085.024.12.06.378.075.563.224 1.944.004.07c-.001.274-.186.507-.431.544L15.5 14H11.98l-.035-.051.032.539a.455.455 0 0 1-.38.508l-.06.004h-7.1l-.06-.004a.454.454 0 0 1-.384-.45l.035-.558L4.02 14H.5l-.068-.005c-.245-.037-.43-.27-.431-.545l.004-.07.224-1.943.075-.563.06-.379.024-.12.025-.084c.265-.438.633-.604 1.109-.828.476-.224.658-.356.747-.448a.462.462 0 0 1 .616-.056.43.43 0 0 1 .078.07l.06.081.573.878a.2.2 0 0 1-.094.296l-1.508.599a.3.3 0 0 0-.18.206l-.061.242-.116.945a.2.2 0 0 0 .198.224h2.288l.102-1.62.095-1.192.028-.246.011-.058a.05.05 0 0 1 .005-.012c.21-.32.443-.483.843-.63l.14-.048.316-.101.163-.056.131-.05.11-.049.098-.054.101-.063c.086-.057.168-.122.247-.198a.433.433 0 0 1 .616.01l.054.069.804 1.147.821-1.171c.036-.051.081-.09.131-.117a.433.433 0 0 1 .523.062zm-.013 1.75-.807 1.152a.8.8 0 0 1-1.31 0l-.807-1.152-.036.017-.183.068-.523.171-.102 1.414-.124 1.957h4.863l-.14-2.233-.084-1.139-.363-.114-.216-.075-.168-.066zM7.993 1.001c1.097-.027 2.105.58 2.564 1.542.14.294.23.624.275.971a2.043 2.043 0 0 1 1.057-.263 2 2 0 0 1 1.86 1.119c.665 1.41-.204 3.925-1.86 3.925-.864 0-1.514-.684-1.844-1.538-.495.71-1.198 1.195-2.052 1.195-.86 0-1.566-.492-2.063-1.21-.328.861-.98 1.553-1.848 1.553-1.657 0-2.526-2.515-1.86-3.925a2 2 0 0 1 1.86-1.12c.385-.008.755.09 1.072.273.044-.35.135-.683.275-.98C5.889 1.58 6.896.974 7.993 1zM4.118 4.75h-.073c-.211-.006-.396.106-.47.26-.127.27-.11.805.084 1.264.159.374.331.52.423.52.092 0 .264-.146.422-.52.195-.46.212-.994.087-1.259a.492.492 0 0 0-.384-.26l-.089-.006zm7.807 0h-.073c-.211-.006-.397.106-.47.26-.128.27-.11.805.084 1.264.158.374.33.52.423.52.091 0 .264-.146.422-.52.195-.46.212-.994.087-1.259a.492.492 0 0 0-.384-.26l-.09-.006zM8.029 2.5h-.072c-.513-.013-.975.265-1.174.682-.567 1.202.31 3.269 1.21 3.269.9 0 1.777-2.067 1.213-3.263-.202-.423-.664-.701-1.177-.689z"
    })
  });
};
// pure annotation needed for tree-shaking
var UsersGroup = /* #__PURE__ */withIcon(IconUsersGroup);

var IconUsersGroupFilled = function IconUsersGroupFilled() {
  return jsx("g", {
    children: jsx("path", {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M2.68689 9.00182C2.70867 9.01534 2.72912 9.03161 2.74766 9.05052L2.79448 9.10813L3.49956 10.0789L3.54818 10.1541C3.61174 10.2525 3.6432 10.3673 3.63946 10.4829L3.63 10.5696L3.58829 10.8301L3.54765 11.1202L3.50711 11.4424L3.50083 11.5315L3.50832 11.6544C3.52339 11.7797 3.56073 11.8967 3.61624 12.0014L0.388781 12L0.336009 11.9965C0.145112 11.9708 0.000935961 11.8076 0 11.6152L0.00280602 11.5666L0.0374773 11.2908L0.0717961 11.0452L0.106314 10.8287L0.141583 10.6399C0.195324 10.3768 0.253461 10.2028 0.32437 10.0979C0.527815 9.79689 0.758107 9.6609 1.18498 9.52424L1.46225 9.43882L1.60583 9.3919L1.72038 9.3498L1.81606 9.30818L1.90298 9.26271L1.9913 9.20904C2.06655 9.16081 2.13811 9.10556 2.20727 9.04139C2.34147 8.91686 2.5405 8.90506 2.68689 9.00182ZM13.7927 9.04139C13.8619 9.10556 13.9334 9.16081 14.0087 9.20904L14.097 9.26271L14.1839 9.30818L14.2796 9.3498L14.3942 9.3919L14.5377 9.43882L14.7205 9.49488C15.2089 9.64231 15.4576 9.77539 15.6756 10.0979C15.7465 10.2028 15.8047 10.3768 15.8584 10.6399L15.8937 10.8287L15.9282 11.0452L15.9625 11.2908L15.9972 11.5666C16.0228 11.7794 15.8708 11.9686 15.664 11.9965L15.6112 12L12.3869 12.0013C12.4589 11.8696 12.4991 11.7204 12.5 11.5637L12.4932 11.445L12.4533 11.1271L12.4131 10.8396L12.3723 10.5827C12.3449 10.4354 12.3733 10.2832 12.4521 10.1558L12.4996 10.0789L13.2193 9.08783C13.2505 9.04489 13.2901 9.01173 13.3343 8.98897C13.4782 8.90643 13.6649 8.92275 13.7927 9.04139ZM9.4774 8.61873C9.55644 8.69207 9.63823 8.75522 9.72423 8.81033L9.82516 8.87167L9.9245 8.92364L10.0338 8.9712L10.1648 9.01931L10.5377 9.13701C11.0958 9.3055 11.3802 9.45759 11.6293 9.82619C11.7103 9.94609 11.7768 10.145 11.8382 10.4456L11.8785 10.6614L11.9179 10.9088L11.9572 11.1894L11.9968 11.5047C12.026 11.7479 11.8523 11.9641 11.616 11.996L11.5557 12H4.44432L4.38401 11.996C4.16584 11.9666 4.00107 11.7801 4 11.5603L4.00321 11.5047L4.04283 11.1894L4.08205 10.9088L4.1215 10.6614L4.16181 10.4456C4.22323 10.145 4.28967 9.94609 4.37071 9.82619C4.60322 9.48216 4.86641 9.32674 5.35426 9.17056L5.67114 9.07293L5.83523 9.01931L5.96615 8.9712L6.0755 8.92364L6.17484 8.87167L6.27577 8.81033C6.36177 8.75522 6.44356 8.69207 6.5226 8.61873C6.67596 8.47641 6.90343 8.46292 7.07073 8.57351C7.09562 8.58896 7.11899 8.60755 7.14018 8.62916L7.19369 8.69501L7.9995 9.8045L8.82208 8.67181C8.85771 8.62273 8.90301 8.58483 8.95347 8.55882C9.11794 8.46449 9.33129 8.48315 9.4774 8.61873ZM3.5 5C4.4665 5 5.13241 5.7835 5.13241 6.75C5.13241 7.7165 4.46636 8.9375 3.49987 8.9375C2.53337 8.9375 1.86732 7.7165 1.86732 6.75C1.86732 5.7835 2.5335 5 3.5 5ZM12.5 5C13.4665 5 14.1324 5.7835 14.1324 6.75C14.1324 7.7165 13.4664 8.9375 12.4999 8.9375C11.5334 8.9375 10.8673 7.7165 10.8673 6.75C10.8673 5.7835 11.5335 5 12.5 5ZM8 4C9.10457 4 9.86561 4.89543 9.86561 6C9.86561 7.10457 9.10442 8.5 7.99985 8.5C6.89528 8.5 6.13408 7.10457 6.13408 6C6.13408 4.89543 6.89543 4 8 4Z"
    })
  });
};
// pure annotation needed for tree-shaking
var UsersGroupFilled = /* #__PURE__ */withIcon(IconUsersGroupFilled);

var IconVariable = function IconVariable() {
  return jsx("g", {
    children: jsx("path", {
      d: "m1.051 0 4.95.006A.75.75 0 0 1 6.1 1.5l-.102.006L1.5 1.501V14.5H6a.75.75 0 0 1 .743.647l.007.102a.75.75 0 0 1-.648.744L6 15.999 1.05 16a1.05 1.05 0 0 1-1.044-.936L0 14.95V1.05C0 .51.41.064.936.007L1.05 0zm8.95.01 4.95.007c.541 0 .986.41 1.043.936l.006.114V14.95c0 .541-.41.987-.936 1.044L14.95 16H10a.75.75 0 0 1-.102-1.494L10 14.5l4.5.001V1.516L9.999 1.51a.75.75 0 0 1-.742-.649L9.25.76a.75.75 0 0 1 .751-.749z"
    })
  });
};
// pure annotation needed for tree-shaking
var Variable = /* #__PURE__ */withIcon(IconVariable);

var IconVideo = function IconVideo() {
  return jsx("g", {
    children: jsx("path", {
      d: "M15.994 2.643 16 2.75v10.494a.75.75 0 0 1-1.121.652l-.09-.06L11 10.892V12.8a.7.7 0 0 1-.7.7H.7a.7.7 0 0 1-.7-.7V3.2a.7.7 0 0 1 .7-.7h9.6a.7.7 0 0 1 .7.7v1.904l3.79-2.946a.75.75 0 0 1 1.204.485zM9.5 4h-8v8h8V4zm5 .284L11 7.004V8.99l3.5 2.721V4.284z"
    })
  });
};
// pure annotation needed for tree-shaking
var Video = /* #__PURE__ */withIcon(IconVideo);

var IconVideoEnd = function IconVideoEnd() {
  return jsx("g", {
    children: jsx("path", {
      d: "M14.25 0a.75.75 0 0 1 .75.75v14.5a.75.75 0 1 1-1.5 0V9.268l-11.385 6.63a.752.752 0 0 1-1.008-.237.682.682 0 0 1-.107-.365V.704C1 .315 1.329 0 1.734 0c.134 0 .266.035.38.102L13.5 6.731V.75a.75.75 0 0 1 .75-.75zM2.5 2.062v11.875L12.699 8 2.5 2.062z"
    })
  });
};
// pure annotation needed for tree-shaking
var VideoEnd = /* #__PURE__ */withIcon(IconVideoEnd);

var IconVideoNext = function IconVideoNext() {
  return jsx("g", {
    children: jsx("path", {
      d: "M1.734 0c.134 0 .266.035.38.102l12.533 7.296a.688.688 0 0 1 0 1.204L2.115 15.898a.752.752 0 0 1-1.008-.237.682.682 0 0 1-.107-.365V.704C1 .315 1.329 0 1.734 0zM2.5 2.062v11.875L12.699 8 2.5 2.062z"
    })
  });
};
// pure annotation needed for tree-shaking
var VideoNext = /* #__PURE__ */withIcon(IconVideoNext);

var IconVideoPrev = function IconVideoPrev() {
  return jsx("g", {
    children: jsx("path", {
      d: "M14.266 0a.758.758 0 0 0-.38.102L1.352 7.398a.688.688 0 0 0 0 1.204l12.532 7.296a.752.752 0 0 0 1.008-.237.682.682 0 0 0 .107-.365V.704C15 .315 14.671 0 14.266 0zM13.5 2.062v11.875L3.301 7.999 13.5 2.062z"
    })
  });
};
// pure annotation needed for tree-shaking
var VideoPrev = /* #__PURE__ */withIcon(IconVideoPrev);

var IconVideoStart = function IconVideoStart() {
  return jsx("g", {
    children: jsx("path", {
      d: "M1.75 16a.75.75 0 0 1-.75-.75V.75a.75.75 0 1 1 1.5 0v5.982L13.885.102a.752.752 0 0 1 1.008.237c.07.11.107.236.107.365v14.592c0 .389-.329.704-.734.704a.758.758 0 0 1-.38-.102L2.5 9.269v5.981a.75.75 0 0 1-.75.75zm11.75-2.062V2.063L3.301 8 13.5 13.938z"
    })
  });
};
// pure annotation needed for tree-shaking
var VideoStart = /* #__PURE__ */withIcon(IconVideoStart);

var IconVideoStop = function IconVideoStop() {
  return jsx("g", {
    children: jsx("path", {
      d: "M14.951.017 1.051 0C.47 0 0 .47 0 1.05v13.9C0 15.53.47 16 1.05 16h13.9c.58 0 1.05-.47 1.05-1.05V1.067c0-.58-.47-1.05-1.049-1.05zM1.5 1.501l13 .015V14.5h-13V1.501z"
    })
  });
};
// pure annotation needed for tree-shaking
var VideoStop = /* #__PURE__ */withIcon(IconVideoStop);

var IconWarning = function IconWarning() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8.496 1.136a1 1 0 0 1 .361.36l7.005 12.002A.998.998 0 0 1 14.998 15H1a1 1 0 0 1-.864-1.502L7.129 1.497a1 1 0 0 1 1.367-.361zm-.503 1.856L1.87 13.5h12.256L7.993 2.992zM8 10.5c.38 0 .693.26.743.596l.007.093v.489l-.007.226c-.05.337-.363.596-.743.596s-.693-.26-.743-.596l-.007-.226v-.489l.007-.093c.05-.337.363-.596.743-.596zM8 5c.38 0 .693.279.743.64l.007.1v3.52l-.007.1A.747.747 0 0 1 8 10a.747.747 0 0 1-.743-.64l-.007-.1V5.74l.007-.1A.747.747 0 0 1 8 5z"
    })
  });
};
// pure annotation needed for tree-shaking
var Warning = /* #__PURE__ */withIcon(IconWarning);

var IconWarningFilled = function IconWarningFilled() {
  return jsx("g", {
    children: jsx("path", {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M8.49642 1.1358C8.64582 1.22268 8.77013 1.34678 8.85721 1.49597L15.8621 13.4978C16.1403 13.9744 15.979 14.5861 15.5019 14.864C15.349 14.9531 15.1752 15 14.9982 15H1C0.447715 15 0 14.5527 0 14.001C0 13.8244 0.0468458 13.651 0.135766 13.4984L7.1291 1.49659C7.40695 1.01975 8.01912 0.858219 8.49642 1.1358ZM8 11.7546C7.6203 11.7546 7.30651 12.034 7.25685 12.3965L7.25 12.4973V12.7573L7.25685 12.8581C7.30651 13.2206 7.6203 13.5 8 13.5C8.3797 13.5 8.69349 13.2206 8.74315 12.8581L8.75 12.7573V12.4973L8.74315 12.3965C8.69349 12.034 8.3797 11.7546 8 11.7546ZM8 4.5C7.6203 4.5 7.30651 4.7794 7.25685 5.1419L7.25 5.24268V9.78661L7.25685 9.88739C7.30651 10.2499 7.6203 10.5293 8 10.5293C8.3797 10.5293 8.69349 10.2499 8.74315 9.88739L8.75 9.78661V5.24268L8.74315 5.1419C8.69349 4.7794 8.3797 4.5 8 4.5Z"
    })
  });
};
// pure annotation needed for tree-shaking
var WarningFilled = /* #__PURE__ */withIcon(IconWarningFilled);

var IconWorkgroup = function IconWorkgroup() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8.25 0a2.75 2.75 0 0 1 2.716 3.186 6.747 6.747 0 0 1 3.77 6.5 2.75 2.75 0 0 1-2.543 4.854A6.716 6.716 0 0 1 8 16a6.721 6.721 0 0 1-4.195-1.461 2.75 2.75 0 0 1-2.54-4.854A6.752 6.752 0 0 1 5.51 2.974 2.75 2.75 0 0 1 8.25 0zM6.036 4.382l-.024.009A5.248 5.248 0 0 0 2.75 9.25a2.75 2.75 0 0 1 2.262 4.315A5.21 5.21 0 0 0 8 14.5a5.226 5.226 0 0 0 2.99-.934 2.75 2.75 0 0 1 2.092-4.31l.168-.006a5.25 5.25 0 0 0-2.918-4.705 2.746 2.746 0 0 1-4.296-.163zM2.75 10.75a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5zm10.5 0a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5zm-5-9.25a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5z"
    })
  });
};
// pure annotation needed for tree-shaking
var Workgroup = /* #__PURE__ */withIcon(IconWorkgroup);

var IconWrench = function IconWrench() {
  return jsx("g", {
    children: jsx("path", {
      d: "M13.372.514a.756.756 0 0 1 .26 1.132l-.066.076-2.352 2.35.713.713 2.351-2.35a.756.756 0 0 1 1.209.194 4.787 4.787 0 0 1-5.651 6.74l-3.797 3.798a3.022 3.022 0 1 1-3.206-3.206L6.63 6.164a4.787 4.787 0 0 1 6.741-5.65zM4.091 11.909a1.511 1.511 0 1 0-2.137 2.137A1.511 1.511 0 0 0 4.09 11.91zm4.316-3.247a4.82 4.82 0 0 1-1.069-1.069l-2.78 2.78a3.028 3.028 0 0 1 1.068 1.07l2.78-2.781zm3.209-7.128-.18-.015A3.274 3.274 0 1 0 14.49 4.76l-.007-.197-.018-.178-2.003 2.003a.756.756 0 0 1-.908.122l-.083-.055-.078-.067-1.78-1.781a.756.756 0 0 1-.068-.992l.068-.077 2.004-2.005z"
    })
  });
};
// pure annotation needed for tree-shaking
var Wrench = /* #__PURE__ */withIcon(IconWrench);

var IconX = function IconX() {
  return jsx("g", {
    children: jsx("path", {
      d: "M12.802 2.147a.75.75 0 0 1 1.05 1.05l-.072.084L9.06 7.999l4.719 4.72a.75.75 0 0 1-.978 1.133l-.084-.072L8 9.06 3.281 13.78l-.085.072a.75.75 0 0 1-1.05-1.05l.073-.084L6.938 8 2.219 3.281a.75.75 0 0 1 .977-1.134l.085.072 4.718 4.719 4.72-4.719.083-.072z"
    })
  });
};
// pure annotation needed for tree-shaking
var X = /* #__PURE__ */withIcon(IconX);

var IconXCircle = function IconXCircle() {
  return jsx("g", {
    children: jsx("path", {
      d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm6.5 8a6.5 6.5 0 1 0-13 0 6.5 6.5 0 0 0 13 0zm-3.318-3.182a.75.75 0 0 1 0 1.06L9.061 8l2.121 2.121a.75.75 0 0 1-1.06 1.061L8 9.061l-2.121 2.121a.75.75 0 0 1-1.061-1.06L6.939 8 4.818 5.879a.75.75 0 0 1 1.06-1.061L8 6.939l2.121-2.121a.75.75 0 0 1 1.061 0z"
    })
  });
};
// pure annotation needed for tree-shaking
var XCircle = /* #__PURE__ */withIcon(IconXCircle);

export { A11yScreenReader as IconA11yScreenReader, Airplane as IconAirplane, Arrow180 as IconArrow180, Arrow360 as IconArrow360, ArrowBox as IconArrowBox, ArrowCombine as IconArrowCombine, ArrowDashed as IconArrowDashed, ArrowDown as IconArrowDown, ArrowExit as IconArrowExit, ArrowForward as IconArrowForward, ArrowIntegrate as IconArrowIntegrate, ArrowLeft as IconArrowLeft, ArrowOrder as IconArrowOrder, ArrowRandom as IconArrowRandom, ArrowRefresh as IconArrowRefresh, ArrowRight as IconArrowRight, ArrowSkip as IconArrowSkip, ArrowSpread as IconArrowSpread, ArrowUp as IconArrowUp, ArtificialIntelligence as IconArtificialIntelligence, Badge as IconBadge, Balloon as IconBalloon, Bell as IconBell, BestWorst as IconBestWorst, Blocks as IconBlocks, Books as IconBooks, Brush as IconBrush, Calendar as IconCalendar, CalendarEdit as IconCalendarEdit, Camera as IconCamera, CaretDown as IconCaretDown, CaretLeft as IconCaretLeft, CaretRight as IconCaretRight, CaretUp as IconCaretUp, CaretUpDown as IconCaretUpDown, Cart as IconCart, ChartBarHorz as IconChartBarHorz, ChartBarVert as IconChartBarVert, ChartConfidenceLevel as IconChartConfidenceLevel, ChartDonut as IconChartDonut, ChartDonutFilled as IconChartDonutFilled, ChartHorzStacked as IconChartHorzStacked, ChartLine as IconChartLine, ChartPie as IconChartPie, ChartSegment as IconChartSegment, ChartTracker as IconChartTracker, ChartVertStacked as IconChartVertStacked, Chat as IconChat, Check as IconCheck, CheckBox as IconCheckBox, CheckCircle as IconCheckCircle, CheckEnclosed as IconCheckEnclosed, ChevronDown as IconChevronDown, ChevronLeft as IconChevronLeft, ChevronRight as IconChevronRight, ChevronUp as IconChevronUp, Circle as IconCircle, Clock as IconClock, Clone as IconClone, Cloud as IconCloud, CloudArrow as IconCloudArrow, Comment as IconComment, CommentCheck as IconCommentCheck, CommentFilled as IconCommentFilled, CommentPlus as IconCommentPlus, Copyright as IconCopyright, CreditCard as IconCreditCard, Danger as IconDanger, Dashboard as IconDashboard, DashboardAdd as IconDashboardAdd, Desktop as IconDesktop, Distribute as IconDistribute, Divisions as IconDivisions, Document as IconDocument, DocumentArrow as IconDocumentArrow, DocumentClone as IconDocumentClone, DocumentDocx as IconDocumentDocx, DocumentList as IconDocumentList, DocumentPPTX as IconDocumentPPTX, DocumentPdf as IconDocumentPdf, DocumentPlus as IconDocumentPlus, DocumentSearch as IconDocumentSearch, DocumentUser as IconDocumentUser, DocumentUserFilled as IconDocumentUserFilled, DocumentX as IconDocumentX, Email as IconEmail, Error$1 as IconError, Eye as IconEye, FaceHappy as IconFaceHappy, FaceNeutral as IconFaceNeutral, FaceSad as IconFaceSad, FaceVeryHappy as IconFaceVeryHappy, FaceVerySad as IconFaceVerySad, Filter as IconFilter, Flag as IconFlag, Flask as IconFlask, Folder as IconFolder, FolderArrow as IconFolderArrow, FolderPencil as IconFolderPencil, FolderUser as IconFolderUser, Footprint as IconFootprint, Fullscreen as IconFullscreen, Gear as IconGear, Globe as IconGlobe, GovBuilding as IconGovBuilding, GraduationHat as IconGraduationHat, Grid as IconGrid, GridCarets as IconGridCarets, GridCircles as IconGridCircles, Group as IconGroup, Happy as IconHappy, HappyFilled as IconHappyFilled, Health as IconHealth, Heart as IconHeart, HeartFilled as IconHeartFilled, HeartHand as IconHeartHand, Help as IconHelp, HelpFilled as IconHelpFilled, House as IconHouse, Image as IconImage, ImageArrow as IconImageArrow, Inbox as IconInbox, Info as IconInfo, InfoFilled as IconInfoFilled, Insights as IconInsights, JumpToQuestion as IconJumpToQuestion, JumpToQuestionFilled as IconJumpToQuestionFilled, Key as IconKey, Keyboard as IconKeyboard, Launchpad as IconLaunchpad, Lightbulb as IconLightbulb, Lighting as IconLighting, Link as IconLink, List as IconList, ListBulleted as IconListBulleted, ListChecks as IconListChecks, Location as IconLocation, Lock as IconLock, LogoDrive as IconLogoDrive, LogoFacebook as IconLogoFacebook, LogoGmail as IconLogoGmail, LogoGoldie as IconLogoGoldie, LogoGoogle as IconLogoGoogle, LogoGooglePlus as IconLogoGooglePlus, LogoInstagram$1 as IconLogoInstagram, LogoLinkedIn as IconLogoLinkedIn, LogoMailChimp as IconLogoMailChimp, LogoMessenger as IconLogoMessenger, LogoOffice as IconLogoOffice, LogoOutlook as IconLogoOutlook, LogoPinterest as IconLogoPinterest, LogoWhatsApp as IconLogoWhatsApp, LogoX as IconLogoX, LogoYahooMail as IconLogoYahooMail, LogoYouTube as IconLogoYouTube, Megaphone as IconMegaphone, Menu as IconMenu, Minimize as IconMinimize, Minus as IconMinus, MinusCircle as IconMinusCircle, Mobile as IconMobile, More as IconMore, MoreAndroid as IconMoreAndroid, Move as IconMove, MoveToGroup as IconMoveToGroup, Nest as IconNest, News as IconNews, NoWifi as IconNoWifi, Number1 as IconNumber1, Number10 as IconNumber10, Number2 as IconNumber2, Number3 as IconNumber3, Number4 as IconNumber4, Number5 as IconNumber5, Number6 as IconNumber6, Number7 as IconNumber7, Number8 as IconNumber8, Number9 as IconNumber9, OpenMinimizeLeft as IconOpenMinimizeLeft, OpenMinimizeRight$1 as IconOpenMinimizeRight, Paragraph as IconParagraph, PencilBox as IconPencilBox, PencilPaper as IconPencilPaper, Phone as IconPhone, Pin as IconPin, Plus as IconPlus, PlusCircle as IconPlusCircle, Printer as IconPrinter, Pulse as IconPulse, QRCode as IconQRCode, QuestionBank as IconQuestionBank, ScatterPlot as IconScatterPlot, ScreenReader as IconScreenReader, Search as IconSearch, Shapes as IconShapes, Share as IconShare, ShareAndroid as IconShareAndroid, ShareIOS as IconShareIOS, Shuffle as IconShuffle, ShuffleBox as IconShuffleBox, ShuffleBoxFilled as IconShuffleBoxFilled, Slider as IconSlider, SlidersVert as IconSlidersVert, Star as IconStar, StarFilled as IconStarFilled, TableCol as IconTableCol, TableRow as IconTableRow, Tablet as IconTablet, TabletCheck as IconTabletCheck, Tag as IconTag, Template as IconTemplate, Textbox as IconTextbox, TextboxMultiple as IconTextboxMultiple, ThumbsUp as IconThumbsUp, ThumbsUpFilled as IconThumbsUpFilled, Timer as IconTimer, Toggle as IconToggle, Toolbox as IconToolbox, Translation as IconTranslation, Trash as IconTrash, Truck as IconTruck, Ungroup as IconUngroup, Unlock as IconUnlock, Unnest as IconUnnest, User as IconUser, UserArrow as IconUserArrow, UserDeleted as IconUserDeleted, UserFilled as IconUserFilled, UserPlus as IconUserPlus, Users as IconUsers, UsersFilled as IconUsersFilled, UsersGroup as IconUsersGroup, UsersGroupFilled as IconUsersGroupFilled, Variable as IconVariable, Video as IconVideo, VideoEnd as IconVideoEnd, VideoNext as IconVideoNext, VideoPrev as IconVideoPrev, VideoStart as IconVideoStart, VideoStop as IconVideoStop, Warning as IconWarning, WarningFilled as IconWarningFilled, Workgroup as IconWorkgroup, Wrench as IconWrench, X as IconX, XCircle as IconXCircle, withIcon };
